import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useEffect,
} from "react";
import { useStoredState } from "../../hooks/storedState/storedState";

// Define available languages
export type Language =
  | "en"
  | "fa"
  | "ar"
  | "ko"
  | "de"
  | "fr"
  | "ru"
  | "zh"
  | "ja"
  | "it"
  | "tr";

// Translations for both languages
export const translations = {
  en: {
    appTitle: "LinguaTalk",
    appTagline:
      "Find the perfect language partner. Practice any language at your pace.",
    appDescription:
      "LinguaTalk connects language enthusiasts with practice partners from around the world. Whether you want to learn Arabic, Chinese, English, French, German, Italian, Japanese, Korean, Persian, Russian, Turkish, or any other language, we have you covered. Chat with AI tutors for guided practice or connect with real people to improve your speaking, writing, and reading skills in a fun, social environment.",
    downloadIOS: "Download for iOS",
    downloadAndroid: "Download for Android",
    directDownload: "Download .apk file",
    learnMore: "Learn more about LinguaTalk",
    howItWorks: "How LinguaTalk Works",
    step1Title: "Create Your Profile",
    step1Description:
      "Set up your language preferences, skill level, and interests to help find your perfect match.",
    step2Title: "Find Partners",
    step2Description:
      "Explore AI characters or discover real users who share your language learning goals.",
    step3Title: "Practice & Improve",
    step3Description:
      "Chat, voice call, or message to practice your target language skills regularly.",
    whyChoose: "Why Choose LinguaTalk?",
    feature1Title: "AI Language Partners",
    feature1Description:
      "Practice anytime with intelligent AI characters designed to help you improve your language skills.",
    feature2Title: "Real Partners",
    feature2Description:
      "Connect with native speakers and other learners for authentic language exchange.",
    feature3Title: "Detailed Profiles",
    feature3Description:
      "Create and customize your profile to showcase your language skills and interests.",
    feature4Title: "Smart Matching",
    feature4Description:
      "Find the perfect language partner based on your skill level, interests, and learning goals.",
    aiScreenTitle: "Chat with AI Characters",
    aiScreenDescription:
      "Improve your language skills by chatting with AI characters designed to help you practice in a safe, judgment-free environment. Perfect for beginners or when human partners aren't available.",
    exploreScreenTitle: "Explore Real Partners",
    exploreScreenDescription:
      "Search and connect with real users from around the world. Filter by language, proficiency level, and interests to find your perfect language exchange partner.",
    footerText: "© 2025 LinguaTalk. All rights reserved.",
    language: "Language",
    // Top Menu
    menuHome: "Home",
    menuPlacementTest: "Placement Test",
    menuPrivacyPolicy: "Privacy Policy",
    menuSafetySecurity: "Safety & Security",
    menuDeleteAccount: "Delete Account",
    // Popup
    popupTitle: "Test Your Language Level!",
    popupDescription:
      "Discover your proficiency in just 8 minutes. Get instant results from A1 to C2 level.",
    popupButton: "Take Test Now",
    popupFooter: "or download the app to save your results",
    userPreviewTitle: "You're invited!",
    userPreviewMessage:
      "A friend wants to practice languages with you on Lingua Talk. Download the app to connect — free, fun, and no pressure.",
    userPreviewTagline: "Practice languages together",
    userPreviewOpenInApp: "Open in app",
    userPreviewHint: "Free to download · No account needed to try",
    // Placement Test
    testStarting: "Test Starting...",
    getReady: "Get ready!",
    testPreview: "Test Preview",
    testYourLanguageProficiency: "Test your language proficiency",
    guest: "Guest",
    totalQuestions: "Total Questions",
    totalTime: "Total Time",
    testParts: "Test Parts",
    question: "question",
    questions: "questions",
    startTest: "Start Test",
    loadingTest: "Loading Test...",
    preparingTest: "Please wait while we prepare your placement test.",
    unableToLoadTest: "Unable to load the test. Please try again from the app.",
    greatJob: "Great job!",
    yourEstimatedLevel: "Your estimated level is",
    score: "Score:",
    testEvaluated:
      "This placement test evaluated your {language} proficiency across multiple levels from A1 to C2.",
    greatWorkCompleted:
      "Great work! You've completed the {language} placement test. Sign in to save your progress and unlock personalized learning features.",
    tryAgain: "Try Again",
    backToHome: "Back to Home",
    test: "Test",
    level: "Level:",
    type: "Type:",
    questionOf: "Question {current} of {total} ({answered} answered)",
    playAudio: "Play Audio",
    previous: "Previous",
    next: "Next",
    submitTest: "Submit Test",
    testResultSaved: "Your test result has been saved successfully!",
    testResultFailed: "Failed to save test result. Please try again.",
    saveResultsTrackProgress:
      "Want to save your results and track your progress?",
    improveWithApp:
      "Also you can improve your language skills with our App AI and real partners.",
    grammar: "Grammar",
    vocabulary: "Vocabulary",
    listening: "Listening",
    downloadForIOS: "Download for iOS",
    downloadForAndroid: "Download for Android",
    placementTest: "Placement Test",
    min: "min",
    testForLanguage: "Test for language:",
    testNotImplemented: "Are you looking for another language tests?",
    availableLanguages: "For now, you can take the test in these languages:",
    viewAllLanguages: "Are you looking for another language test?",
    comingSoon: "Coming soon...",
    // Privacy Page
    privacyTitle: "Privacy Policy",
    privacyIntro:
      "At Lingua Talk, we value your privacy and are committed to protecting your personal information. This policy explains how we collect, use, and safeguard your data when you use our language learning and social platform.",
    informationWeCollect: "Information We Collect",
    accountInformationTitle: "Account Information:",
    infoCollectEmailUsername: "Email address and username",
    infoCollectProfileInfo: "Profile information (name, age, location)",
    infoCollectLanguagePrefs: "Language preferences and learning goals",
    infoCollectSettingsPrefs: "Account settings and preferences",
    usageDataTitle: "Usage Data:",
    usageDataConversationsAI: "Conversations with AI characters",
    usageDataMessagesPartners: "Messages with language partners",
    usageDataProgressStats: "Learning progress and statistics",
    usageDataAppPatterns: "App usage patterns and preferences",
    howWeUseTitle: "How We Use Your Information",
    provideServicesTitle: "Provide our services",
    provideServicesDesc:
      "Enable language learning features and partner matching",
    improveExperienceTitle: "Improve user experience",
    improveExperienceDesc: "Personalize content and AI character interactions",
    safetyModerationTitle: "Safety and moderation",
    safetyModerationDesc: "Monitor conversations for inappropriate content",
    communicationTitle: "Communication",
    communicationDesc: "Send important updates and notifications",
    analyticsTitle: "Analytics",
    analyticsDesc: "Understand how users engage with our platform",
    dataProtectionTitle: "Data Protection",
    encryptionTitle: "Encryption",
    encryptionDesc: "All data is encrypted in transit and at rest",
    accessControlsTitle: "Access controls",
    accessControlsDesc: "Limited employee access on a need-to-know basis",
    regularAuditsTitle: "Regular audits",
    regularAuditsDesc: "Security reviews and vulnerability assessments",
    secureInfrastructureTitle: "Secure infrastructure",
    secureInfrastructureDesc: "Industry-standard cloud security measures",
    dataMinimizationTitle: "Data minimization",
    dataMinimizationDesc: "We only collect necessary information",
    dataSharingTitle: "Data Sharing and Third Parties",
    noSellData: "We do NOT sell your personal data to third parties.",
    serviceProvidersTitle: "Service Providers",
    serviceProvidersDesc:
      "Cloud hosting, analytics, and security services that help us operate the platform",
    legalRequirementsTitle: "Legal Requirements",
    legalRequirementsDesc:
      "When required by law, court order, or to protect user safety",
    withConsentTitle: "With Your Consent",
    withConsentDesc:
      "Only when you explicitly agree to share information with specific partners",
    yourRightsTitle: "Your Privacy Rights",
    rightAccessTitle: "Access",
    rightAccessDesc: "View your personal data",
    rightCorrectionTitle: "Correction",
    rightCorrectionDesc: "Update incorrect information",
    rightDeletionTitle: "Deletion",
    rightDeletionDesc: "Delete your account and data",
    rightPortabilityTitle: "Portability",
    rightPortabilityDesc: "Export your data",
    contactUsTitle: "Contact Us",
    contactUsDesc: "For privacy-related questions or to exercise your rights:",
    policyUpdatesTitle: "Policy Updates",
    policyUpdatesDesc:
      "We may update this policy from time to time. We'll notify you of significant changes via email or in-app notification.",
    lastUpdated: "Last updated: January 2024",
    // Delete Account Page
    deleteTitle: "Delete Your Account",
    deleteIntro:
      "We're sorry to see you go! If you need to delete your Lingua Talk account, please follow the instructions below. This action is permanent and cannot be undone.",
    deleteImportantTitle:
      "⚠️ IMPORTANT: Account deletion can only be done through the mobile app",
    deleteImportantDesc:
      "For security reasons, account deletion is only available in the Lingua Talk mobile application. You cannot delete your account from this website.",
    howToDeleteTitle: "How to Delete Your Account",
    stepOpenAppTitle: "Open the Lingua Talk Mobile App",
    stepOpenAppDesc: "Launch the app on your phone or tablet",
    stepGoSettingsTitle: "Go to Settings",
    stepGoSettingsDesc: "Navigate to your profile settings or account settings",
    stepFindAccountTitle: "Find Account Management",
    stepFindAccountDesc:
      'Look for "Account Settings" or "Privacy & Security" section',
    stepSelectDeleteTitle: 'Select "Delete Account"',
    stepSelectDeleteDesc:
      "Choose the account deletion option and follow the confirmation steps",
    whatDeletedTitle: "What Will Be Permanently Deleted",
    personalDataTitle: "Personal Data",
    personalProfileInfo: "Your profile information and settings",
    personalEmailCredentials: "Email address and account credentials",
    personalLangPrefsProgress: "Language preferences and learning progress",
    personalStatsAchievements: "Personal statistics and achievements",
    conversationsContentTitle: "Conversations & Content",
    conversationsWithPartners: "All conversations with language partners",
    chatHistoryAI: "Chat history with AI characters",
    sharedMediaVoice: "Shared media and voice messages",
    learningSessionRecordings: "Learning session recordings",
    socialConnectionsTitle: "Social Connections",
    partnerConnections: "Your language partner connections",
    friendListsBlocked: "Friend lists and blocked users",
    groupMemberships: "Group memberships and communities",
    favoritesSavedContent: "Favorites and saved content",
    aiCharacterDataTitle: "AI Character Data",
    customAIConfigs: "Custom AI character configurations",
    aiConversationHistory: "Conversation history with AI tutors",
    personalizedRecommendations: "Personalized learning recommendations",
    aiGeneratedContent: "AI-generated content and responses",
    beforeDeleteTitle: "Before You Delete Your Account",
    exportYourDataTitle: "Export Your Data",
    exportYourDataDesc:
      "Download your conversation history and learning progress from the app settings before deletion",
    considerBreakTitle: "Consider Taking a Break",
    considerBreakDesc:
      "You can temporarily deactivate your account instead of permanent deletion",
    contactSupportTitle: "Contact Support",
    contactSupportDesc:
      "If you're having issues, our support team might be able to help resolve them",
    needHelpTitle: "Need Help?",
    needHelpIntro:
      "If you're having trouble deleting your account or have questions about the process:",
    supportResponseTime:
      "Our support team typically responds within 24 hours and can assist you with:",
    helpDeletionTroubleshooting: "Account deletion troubleshooting",
    helpDataExport: "Data export requests",
    helpAlternativeSolutions: "Alternative solutions to account issues",
    helpPrivacySecurity: "Privacy and security questions",
    securityNoteTitle: "Security Note",
    securityNoteDesc:
      "Account deletion requests are processed securely and typically completed within 30 days to ensure complete data removal from all systems and backups.",
    // Safety Page
    safetyTitle: "Lingua Talk – Child Safety and Protection Policy",
    safetyIntro:
      "At Lingua Talk, the safety and well-being of our users, especially minors, is a top priority. As a language learning and social app that connects users with AI characters and real language partners, we are committed to maintaining a secure and respectful environment for all.",
    zeroToleranceTitle: "Zero Tolerance for CSAE",
    zeroToleranceIntro: "We do not permit any content or behavior related to:",
    zeroToleranceItem1: "Child sexual abuse or exploitation",
    zeroToleranceItem2: "Inappropriate or harmful behavior toward minors",
    zeroToleranceItem3:
      "Any form of grooming, nudity involving minors, or illegal communication",
    zeroToleranceAction:
      "All such content will be immediately removed and reported to relevant authorities.",
    moderationTitle: "Moderation and Monitoring",
    moderationItem1:
      "Users can report conversations and profiles directly in the app.",
    moderationItem2: "Flagged content is reviewed by our moderation team.",
    moderationItem3:
      "We take swift action against violations (warnings, suspensions, bans, or legal reports).",
    userAgePolicyTitle: "User Age Policy",
    userAgePolicyDesc:
      "Lingua Talk is intended for users aged 13 and above. Accounts found in violation of this policy may be suspended or removed.",
    reportAbuseTitle: "How to Report Abuse",
    reportAbuseIntro: "Users can report:",
    reportAbuseItem1: "Messages or inappropriate content in chats",
    reportAbuseItem2: "User profiles or suspicious behavior",
    reportAbuseUseButton:
      'Use the "Report" button in chats or user profiles. Reports are reviewed by our moderation team.',
    complianceTitle: "Compliance with Child Safety Laws",
    compliesWith: "Lingua Talk complies with:",
    complianceItem1: "Google Play's child safety standards",
    complianceItem2: "Applicable laws regarding CSAE in all operating regions",
    complianceItem3:
      "Industry best practices for platform safety and reporting",

    contactTitle: "Contact",
    safetyContactIntro:
      "For child safety-related questions or to report a serious concern, contact our safety team at:",
    // Home V2
    v2MetaTitle: "LinguaTalk — Learn languages by talking to AI",
    v2MetaDescription: "LinguaTalk is a language learning app. Chat with AI tutors in Spanish, French, Japanese and 9 more languages. Free on iOS and Android.",
    v2HeroLine1: "AI designs your",
    v2HeroLine2: "personal roadmap to fluency",
    v2Pill: "📱 Language Learning App — Free on iOS & Android",
    v2Subtext: "LinguaTalk builds a learning path tailored to your level — then lets you practice every day with AI tutors and real native speakers until you can speak with confidence.",
    v2StoreSubApple: "Download on the",
    v2StoreNameApple: "App Store",
    v2StoreSubGoogle: "Get it on",
    v2StoreNameGoogle: "Google Play",
    v2BazaarSub: "Download from",
    v2BazaarName: "Cafe Bazaar",
    v2MarqueeEnglish: "English",
    v2MarqueeSpanish: "Español",
    v2MarqueeFrench: "Français",
    v2MarqueeGerman: "Deutsch",
    v2MarqueeItalian: "Italiano",
    v2MarqueeTurkish: "Türkçe",
    v2WhatTag: "What is LinguaTalk?",
    v2WhatTitle1: "Not just lessons —",
    v2WhatTitle2: "a roadmap built around you",
    v2WhatDesc: "Most apps throw the same content at everyone. LinguaTalk starts by understanding your level, your goals, and the language you want to learn — then builds a personalized roadmap. From there, you practice through real conversations with AI tutors and native speakers, both matched to exactly where you are right now.",
    v2WhyLabel: "Why it works",
    v2Feature1Title: "AI Tutors",
    v2Feature1Desc: "Talk to intelligent AI characters that respond, correct, and guide you like a real teacher.",
    v2Feature2Title: "Real Conversations",
    v2Feature2Desc: "No flashcards. No multiple choice. Just you and your tutor — chatting in the language you want to learn.",
    v2Feature3Title: "Placement Test",
    v2Feature3Desc: "Start at exactly the right level. A short test puts you where you belong before your first lesson.",
    v2Feature4Title: "12+ Languages",
    v2Feature4Desc: "English, Spanish, French, German, Italian, Turkish and more — all in one app.",
    v2HowLabel: "How it works",
    v2HowTitle1: "Three steps to start",
    v2HowTitle2: "speaking with confidence",
    v2Step1Title: "Take the placement test",
    v2Step1Desc: "A 5-minute adaptive quiz finds your exact level.",
    v2Step2Title: "Choose your AI tutor",
    v2Step2Desc: "Pick a character that fits your style and target language.",
    v2Step3Title: "Start chatting every day",
    v2Step3Desc: "Short daily chats build real speaking confidence over time.",
    v2RingSub: "Your level",
    v2StatLessons: "Lessons done",
    v2StatStreak: "Day streak 🔥",
    v2StatWords: "Words learned",
    v2CtaTitle: "Get your free roadmap today",
    v2CtaSub: "Takes 5 minutes to set up · Free forever · iOS & Android",
    v2PopupTitle: "Find Your Level",
    v2PopupDesc: "Take a free 5-minute placement test and get a personalized learning roadmap.",
    v2PopupBtn: "Take the Test — Free",
    v2PopupFootnote: "No sign-up required",
    v2ChipSpanish: "🇪🇸 Spanish lesson",
    v2ChipAiTyping: "🤖 AI is typing…",
    v2ChipStreak: "🔥 7-day streak",
    v2ChatToday: "Today",
    v2ChatInputPlaceholder: "Type your reply…",
    v2TutorSub: "AI Tutor · Spanish",
  },
  fa: {
    appTitle: "LinguaTalk",
    appTagline:
      "شریک زبانی مناسب خود را پیدا کنید. هر زبانی را با سرعت خود تمرین کنید.",
    appDescription:
      "LinguaTalk علاقه‌مندان به زبان را با شرکای تمرینی از سراسر جهان متصل می‌کند. چه می‌خواهید عربی، چینی، انگلیسی، فرانسوی، آلمانی، ایتالیایی، ژاپنی، کره‌ای، فارسی، روسی، ترکی یا هر زبان دیگری را یاد بگیرید، ما شما را پوشش می‌دهیم. با مربیان هوش مصنوعی برای تمرین هدایت‌شده گفتگو کنید یا با افراد واقعی ارتباط برقرار کنید تا مهارت‌های گفتاری، نوشتاری و خواندن خود را در یک محیط اجتماعی و سرگرم‌کننده بهبود بخشید.",
    downloadIOS: "دانلود برای iOS",
    downloadAndroid: "دانلود برای اندروید",
    directDownload: "دانلود فایل .apk",
    learnMore: "اطلاعات بیشتر درباره LinguaTalk",
    howItWorks: "LinguaTalk چگونه کار می‌کند",
    step1Title: "پروفایل خود را ایجاد کنید",
    step1Description:
      "ترجیحات زبانی، سطح مهارت و علایق خود را تنظیم کنید تا به پیدا کردن مناسب‌ترین زوج کمک کند.",
    step2Title: "شرکا را پیدا کنید",
    step2Description:
      "شخصیت‌های هوش مصنوعی را کاوش کنید یا کاربران واقعی را که اهداف یادگیری زبان شما را به اشتراک می‌گذارند، کشف کنید.",
    step3Title: "تمرین و پیشرفت",
    step3Description:
      "برای تمرین منظم مهارت‌های زبانی هدف خود، چت کنید، تماس صوتی برقرار کنید یا پیام ارسال کنید.",
    whyChoose: "چرا LinguaTalk را انتخاب کنیم؟",
    feature1Title: "شرکای زبانی هوش مصنوعی",
    feature1Description:
      "با شخصیت‌های هوشمند هوش مصنوعی که برای کمک به بهبود مهارت‌های زبانی شما طراحی شده‌اند، در هر زمان تمرین کنید.",
    feature2Title: "شرکای واقعی",
    feature2Description:
      "با سخنرانان بومی و سایر یادگیرندگان برای تبادل زبان اصیل ارتباط برقرار کنید.",
    feature3Title: "پروفایل‌های دقیق",
    feature3Description:
      "پروفایل خود را ایجاد و سفارشی کنید تا مهارت‌ها و علایق زبانی خود را نشان دهید.",
    feature4Title: "تطبیق هوشمند",
    feature4Description:
      "بر اساس سطح مهارت، علایق و اهداف یادگیری خود، شریک زبانی مناسب را پیدا کنید.",
    aiScreenTitle: "گفتگو با شخصیت‌های هوش مصنوعی",
    aiScreenDescription:
      "مهارت‌های زبانی خود را با گفتگو با شخصیت‌های هوش مصنوعی که برای کمک به تمرین شما در یک محیط امن و بدون قضاوت طراحی شده‌اند، بهبود بخشید. مناسب برای مبتدیان یا زمانی که شرکای انسانی در دسترس نیستند.",
    exploreScreenTitle: "کاوش شرکای واقعی",
    exploreScreenDescription:
      "کاربران واقعی را از سراسر جهان جستجو و با آنها ارتباط برقرار کنید. بر اساس زبان، سطح تسلط و علایق، شریک تبادل زبان کامل خود را پیدا کنید.",
    footerText: "© ۲۰۲۵ LinguaTalk. تمامی حقوق محفوظ است.",
    language: "زبان",
    // Top Menu
    menuHome: "خانه",
    menuPlacementTest: "آزمون تعیین سطح",
    menuPrivacyPolicy: "سیاست حفظ حریم خصوصی",
    menuSafetySecurity: "امنیت",
    menuDeleteAccount: "حذف حساب",
    // Popup
    popupTitle: "سطح زبانی خود را آزمایش کنید!",
    popupDescription:
      "مهارت خود را در ۸ دقیقه کشف کنید. نتایج فوری از سطح A1 تا C2 دریافت کنید.",
    popupButton: "شروع آزمون",
    popupFooter: "یا برنامه را دانلود کنید تا نتایج خود را ذخیره کنید",
    userPreviewTitle: "دعوت شدید!",
    userPreviewMessage:
      "یکی می‌خواد با شما توی لینگوا تاک زبان تمرین کنه. اپ رو نصب کنید و وصل شوید — رایگان و سرگرم‌کننده است!",
    userPreviewTagline: "با هم زبان تمرین کنید",
    userPreviewOpenInApp: "باز کردن در اپ",
    userPreviewHint: "دانلود رایگان · برای امتحان ثبت‌نام لازم نیست",
    // Placement Test
    testStarting: "آزمون در حال شروع...",
    getReady: "آماده شوید!",
    testPreview: "پیش‌نمایش آزمون",
    testYourLanguageProficiency: "مهارت زبانی خود را آزمایش کنید",
    guest: "مهمان",
    totalQuestions: "تعداد کل سوالات",
    totalTime: "زمان کل",
    testParts: "بخش‌های آزمون",
    question: "سوال",
    questions: "سوال",
    startTest: "شروع آزمون",
    loadingTest: "در حال بارگذاری آزمون...",
    preparingTest: "لطفاً صبر کنید تا آزمون تعیین سطح شما آماده شود.",
    unableToLoadTest:
      "امکان بارگذاری آزمون وجود ندارد. لطفاً دوباره از برنامه تلاش کنید.",
    greatJob: "عالی بود!",
    yourEstimatedLevel: "سطح تخمینی شما",
    score: "امتیاز:",
    testEvaluated:
      "این آزمون تعیین سطح، مهارت {language} شما را در سطوح مختلف از A1 تا C2 ارزیابی کرده است.",
    greatWorkCompleted:
      "کار عالی! شما آزمون تعیین سطح {language} را تکمیل کرده‌اید. برای ذخیره پیشرفت خود و باز کردن ویژگی‌های یادگیری شخصی‌سازی شده، وارد شوید.",
    tryAgain: "دوباره تلاش کنید",
    backToHome: "بازگشت به خانه",
    test: "آزمون",
    level: "سطح:",
    type: "نوع:",
    questionOf: "سوال {current} از {total} ({answered} پاسخ داده شده)",
    playAudio: "پخش صدا",
    previous: "قبلی",
    next: "بعدی",
    submitTest: "ارسال آزمون",
    testResultSaved: "نتیجه آزمون شما با موفقیت ذخیره شد!",
    testResultFailed: "ذخیره نتیجه آزمون ناموفق بود. لطفاً دوباره تلاش کنید.",
    saveResultsTrackProgress:
      "می‌خواهید نتایج خود را ذخیره کنید و پیشرفت خود را پیگیری کنید؟",
    improveWithApp:
      "همچنین می‌توانید مهارت‌های زبانی خود را با برنامه هوش مصنوعی و شرکای واقعی ما بهبود بخشید.",
    grammar: "گرامر",
    vocabulary: "واژگان",
    listening: "شنیداری",
    downloadForIOS: "دانلود برای iOS",
    downloadForAndroid: "دانلود برای اندروید",
    placementTest: "آزمون تعیین سطح",
    min: "دقیقه",
    testForLanguage: "آزمون تعیین سطح برای:",
    testNotImplemented: "آیا به دنبال آزمون زبان دیگری هستید؟",
    availableLanguages:
      "در حال حاضر می‌توانید آزمون را به این زبان‌ها انجام دهید:",
    viewAllLanguages: "آیا به دنبال آزمون زبان دیگری هستید؟",
    comingSoon: "به زودی...",

    // Privacy Page
    privacyTitle: "سیاست حریم خصوصی",
    privacyIntro:
      "در Lingua Talk، ما به حریم خصوصی شما احترام می‌گذاریم و متعهد به حفاظت از اطلاعات شخصی شما هستیم. این سیاست توضیح می‌دهد هنگام استفاده از پلتفرم اجتماعی و یادگیری زبان ما چگونه داده‌های شما را جمع‌آوری، استفاده و محافظت می‌کنیم.",
    informationWeCollect: "اطلاعاتی که جمع‌آوری می‌کنیم",
    accountInformationTitle: "اطلاعات حساب:",
    infoCollectEmailUsername: "آدرس ایمیل و نام کاربری",
    infoCollectProfileInfo: "اطلاعات پروفایل (نام، سن، موقعیت مکانی)",
    infoCollectLanguagePrefs: "ترجیحات زبانی و اهداف یادگیری",
    infoCollectSettingsPrefs: "تنظیمات حساب و ترجیحات",
    usageDataTitle: "داده‌های استفاده:",
    usageDataConversationsAI: "گفتگوها با شخصیت‌های هوش مصنوعی",
    usageDataMessagesPartners: "پیام‌ها با شرکای زبانی",
    usageDataProgressStats: "پیشرفت یادگیری و آمارها",
    usageDataAppPatterns: "الگوهای استفاده از اپلیکیشن و ترجیحات",
    howWeUseTitle: "نحوه استفاده از اطلاعات شما",
    provideServicesTitle: "ارائه خدمات",
    provideServicesDesc: "فعال‌سازی قابلیت‌های یادگیری زبان و تطبیق شریک",
    improveExperienceTitle: "بهبود تجربه کاربری",
    improveExperienceDesc: "شخصی‌سازی محتوا و تعاملات با شخصیت‌های هوش مصنوعی",
    safetyModerationTitle: "ایمنی و نظارت",
    safetyModerationDesc: "پایش گفتگوها برای محتوای نامناسب",
    communicationTitle: "ارتباطات",
    communicationDesc: "ارسال به‌روزرسانی‌ها و اعلان‌های مهم",
    analyticsTitle: "تحلیل‌ها",
    analyticsDesc: "درک نحوه تعامل کاربران با پلتفرم",
    dataProtectionTitle: "حفاظت از داده‌ها",
    encryptionTitle: "رمزنگاری",
    encryptionDesc:
      "تمام داده‌ها در حال انتقال و در حالت ذخیره رمزنگاری می‌شوند",
    accessControlsTitle: "کنترل‌های دسترسی",
    accessControlsDesc: "دسترسی محدود کارکنان بر اساس نیاز",
    regularAuditsTitle: "ممیزی‌های منظم",
    regularAuditsDesc: "بازبینی‌های امنیتی و ارزیابی آسیب‌پذیری",
    secureInfrastructureTitle: "زیرساخت امن",
    secureInfrastructureDesc: "استانداردهای امنیتی ابری در سطح صنعت",
    dataMinimizationTitle: "کمینه‌سازی داده",
    dataMinimizationDesc: "فقط اطلاعات ضروری را جمع‌آوری می‌کنیم",
    dataSharingTitle: "اشتراک‌گذاری داده و اشخاص ثالث",
    noSellData: "ما داده‌های شخصی شما را به اشخاص ثالث نمی‌فروشیم.",
    serviceProvidersTitle: "ارائه‌دهندگان خدمات",
    serviceProvidersDesc:
      "میزبانی ابری، تحلیل‌ها و خدمات امنیتی که به بهره‌برداری از پلتفرم کمک می‌کنند",
    legalRequirementsTitle: "الزامات قانونی",
    legalRequirementsDesc:
      "وقتی قانون، حکم دادگاه یا حفاظت از ایمنی کاربران لازم بداند",
    withConsentTitle: "با رضایت شما",
    withConsentDesc:
      "فقط زمانی که به‌طور صریح با اشتراک‌گذاری با شرکای مشخص موافقت کنید",
    yourRightsTitle: "حقوق حریم خصوصی شما",
    rightAccessTitle: "دسترسی",
    rightAccessDesc: "مشاهده داده‌های شخصی",
    rightCorrectionTitle: "اصلاح",
    rightCorrectionDesc: "به‌روزرسانی اطلاعات نادرست",
    rightDeletionTitle: "حذف",
    rightDeletionDesc: "حذف حساب و داده‌های شما",
    rightPortabilityTitle: "قابلیت انتقال",
    rightPortabilityDesc: "برون‌برد داده‌های شما",
    contactUsTitle: "تماس با ما",
    contactUsDesc: "برای پرسش‌های مرتبط با حریم خصوصی یا اعمال حقوق خود:",
    policyUpdatesTitle: "به‌روزرسانی‌های سیاست",
    policyUpdatesDesc:
      "ممکن است گاهی این سیاست را به‌روزرسانی کنیم. تغییرات مهم از طریق ایمیل یا اعلان درون‌برنامه‌ای اطلاع داده می‌شود.",
    lastUpdated: "آخرین به‌روزرسانی: ژانویه ۲۰۲۴",
    // Delete Account Page (placeholders)
    deleteTitle: "حذف حساب کاربری شما",
    deleteIntro:
      "متأسفیم که می‌خواهید بروید! اگر نیاز دارید حساب Lingua Talk خود را حذف کنید، لطفاً دستورالعمل‌های زیر را دنبال کنید. این اقدام دائمی است و قابل بازگشت نیست.",
    deleteImportantTitle:
      "⚠️ مهم: حذف حساب فقط از طریق اپلیکیشن موبایل امکان‌پذیر است",
    deleteImportantDesc:
      "به دلایل امنیتی، حذف حساب فقط در برنامه موبایل Lingua Talk امکان‌پذیر است. شما نمی‌توانید حساب خود را از این وبسایت حذف کنید.",
    howToDeleteTitle: "چگونه حساب خود را حذف کنیم",
    stepOpenAppTitle: "اپلیکیشن موبایل Lingua Talk را باز کنید",
    stepOpenAppDesc: "برنامه را در تلفن یا تبلت خود اجرا کنید",
    stepGoSettingsTitle: "به تنظیمات بروید",
    stepGoSettingsDesc: "به تنظیمات پروفایل یا حساب خود بروید",
    stepFindAccountTitle: "مدیریت حساب را پیدا کنید",
    stepFindAccountDesc:
      "بخش «تنظیمات حساب» یا «حریم خصوصی و امنیت» را پیدا کنید",
    stepSelectDeleteTitle: "گزینه «حذف حساب» را انتخاب کنید",
    stepSelectDeleteDesc:
      "گزینه حذف حساب را انتخاب کرده و مراحل تأیید را دنبال کنید",
    whatDeletedTitle: "چه مواردی به‌طور دائمی حذف می‌شوند",
    personalDataTitle: "داده‌های شخصی",
    personalProfileInfo: "اطلاعات و تنظیمات پروفایل شما",
    personalEmailCredentials: "آدرس ایمیل و اطلاعات ورود به حساب",
    personalLangPrefsProgress: "ترجیحات زبان و پیشرفت یادگیری",
    personalStatsAchievements: "آمار و دستاوردهای شخصی",
    conversationsContentTitle: "مکالمات و محتوا",
    conversationsWithPartners: "تمام مکالمات با شرکای زبانی",
    chatHistoryAI: "تاریخچه چت با شخصیت‌های هوش مصنوعی",
    sharedMediaVoice: "رسانه‌های به اشتراک گذاشته شده و پیام‌های صوتی",
    learningSessionRecordings: "ضبط جلسات یادگیری",
    socialConnectionsTitle: "ارتباطات اجتماعی",
    partnerConnections: "ارتباطات با شرکای زبانی شما",
    friendListsBlocked: "فهرست دوستان و کاربران مسدود شده",
    groupMemberships: "عضویت در گروه‌ها و انجمن‌ها",
    favoritesSavedContent: "علاقه‌مندی‌ها و محتواهای ذخیره‌شده",
    aiCharacterDataTitle: "داده‌های شخصیت هوش مصنوعی",
    customAIConfigs: "پیکربندی‌های شخصی شخصیت هوش مصنوعی",
    aiConversationHistory: "تاریخچه مکالمات با معلمان هوش مصنوعی",
    personalizedRecommendations: "توصیه‌های یادگیری شخصی‌سازی‌شده",
    aiGeneratedContent: "محتوا و پاسخ‌های تولید شده توسط هوش مصنوعی",
    beforeDeleteTitle: "قبل از حذف حساب خود",
    exportYourDataTitle: "داده‌های خود را صادر کنید",
    exportYourDataDesc:
      "تاریخچه مکالمات و پیشرفت یادگیری خود را از تنظیمات برنامه قبل از حذف دانلود کنید",
    considerBreakTitle: "در نظر گرفتن وقفه",
    considerBreakDesc:
      "می‌توانید به‌جای حذف دائمی، حساب خود را به‌طور موقت غیرفعال کنید",
    contactSupportTitle: "تماس با پشتیبانی",
    contactSupportDesc:
      "اگر با مشکلی مواجه هستید، تیم پشتیبانی ما ممکن است بتواند به حل آن کمک کند",
    needHelpTitle: "نیاز به کمک دارید؟",
    needHelpIntro:
      "اگر در حذف حساب خود مشکل دارید یا سوالی درباره فرآیند دارید:",
    supportResponseTime:
      "تیم پشتیبانی ما معمولاً ظرف ۲۴ ساعت پاسخ می‌دهد و می‌تواند در زمینه‌های زیر به شما کمک کند:",
    helpDeletionTroubleshooting: "رفع مشکلات مربوط به حذف حساب",
    helpDataExport: "درخواست‌های صادر کردن داده‌ها",
    helpAlternativeSolutions: "راه‌حل‌های جایگزین برای مشکلات حساب",
    helpPrivacySecurity: "سوالات مربوط به حریم خصوصی و امنیت",
    securityNoteTitle: "یادداشت امنیتی",
    securityNoteDesc:
      "درخواست‌های حذف حساب به‌صورت ایمن پردازش می‌شوند و معمولاً ظرف ۳۰ روز تکمیل می‌شوند تا اطمینان حاصل شود که داده‌ها به‌طور کامل از تمام سیستم‌ها و نسخه‌های پشتیبان حذف شده‌اند.",
    safetyTitle: "Lingua Talk – سیاست ایمنی و حفاظت کودکان",
    safetyIntro:
      "در Lingua Talk، ایمنی و رفاه کاربران، به‌ویژه خردسالان، اولویت بالایی دارد. به‌عنوان یک برنامه یادگیری زبان و اجتماعی که کاربران را با شخصیت‌های هوش مصنوعی و شرکای واقعی زبانی متصل می‌کند، متعهد به حفظ محیطی امن و محترمانه برای همه هستیم.",
    zeroToleranceTitle: "صفر تحمل برای CSAE",
    zeroToleranceIntro:
      "ما هیچ محتوایی یا رفتاری مرتبط با موارد زیر را اجازه نمی‌دهیم:",
    zeroToleranceItem1: "سوء استفاده یا بهره‌برداری جنسی از کودکان",
    zeroToleranceItem2: "رفتار نامناسب یا آسیب‌رسان نسبت به خردسالان",
    zeroToleranceItem3:
      "هرگونه آموزش، برهنگی مربوط به کودکان یا ارتباط غیرقانونی",
    zeroToleranceAction:
      "تمام چنین محتواهایی فوراً حذف و به مراجع ذی‌ربط گزارش می‌شوند.",
    moderationTitle: "نظارت و پایش",
    moderationItem1:
      "کاربران می‌توانند مکالمات و پروفایل‌ها را مستقیماً در برنامه گزارش دهند.",
    moderationItem2: "محتوای گزارش‌شده توسط تیم نظارت ما بررسی می‌شود.",
    moderationItem3:
      "ما علیه نقض قوانین اقدام سریع انجام می‌دهیم (هشدارها، تعلیق‌ها، ممنوعیت‌ها یا گزارش‌های قانونی).",
    userAgePolicyTitle: "سیاست سن کاربران",
    userAgePolicyDesc:
      "Lingua Talk برای کاربران بالای ۱۳ سال طراحی شده است. حساب‌هایی که این سیاست را نقض کنند ممکن است تعلیق یا حذف شوند.",
    reportAbuseTitle: "چگونه سوء استفاده را گزارش دهیم",
    reportAbuseIntro: "کاربران می‌توانند گزارش دهند:",
    reportAbuseItem1: "پیام‌ها یا محتوای نامناسب در چت‌ها",
    reportAbuseItem2: "پروفایل کاربران یا رفتار مشکوک",
    reportAbuseUseButton:
      "از دکمه «گزارش» در چت‌ها یا پروفایل‌ها استفاده کنید. گزارش‌ها توسط تیم نظارت بررسی می‌شوند.",
    complianceTitle: "رعایت قوانین ایمنی کودکان",
    compliesWith: "Lingua Talk مطابق با موارد زیر است:",
    complianceItem1: "استانداردهای ایمنی کودکان Google Play",
    complianceItem2: "قوانین مربوط به CSAE در تمام مناطق عملیاتی",
    complianceItem3: "بهترین شیوه‌های صنعتی برای ایمنی و گزارش‌دهی پلتفرم",

    contactTitle: "تماس",
    safetyContactIntro:
      "برای سوالات مربوط به ایمنی کودکان یا گزارش یک مورد جدی، با تیم ایمنی ما تماس بگیرید:",

    // Home V2
    v2MetaTitle: "LinguaTalk — یادگیری زبان با گفتگو با هوش مصنوعی",
    v2MetaDescription: "LinguaTalk یک اپ یادگیری زبان است. با مربیان هوش مصنوعی به اسپانیایی، فرانسوی، ژاپنی و ۹ زبان دیگر گفتگو کنید. رایگان در iOS و Android.",
    v2HeroLine1: "هوش مصنوعی مسیر",
    v2HeroLine2: "شخصی شما تا تسلط را طراحی می‌کند",
    v2Pill: "📱 اپ یادگیری زبان — رایگان در iOS و Android",
    v2Subtext: "LinguaTalk مسیر یادگیری متناسب با سطح شما می‌سازد — سپس هر روز با مربیان هوش مصنوعی و سخنرانان بومی تمرین می‌کنید تا با اعتماد به خود صحبت کنید.",
    v2StoreSubApple: "دانلود از",
    v2StoreNameApple: "App Store",
    v2StoreSubGoogle: "دریافت از",
    v2StoreNameGoogle: "Google Play",
    v2BazaarSub: "دانلود از",
    v2BazaarName: "کافه بازار",
    v2MarqueeEnglish: "انگلیسی",
    v2MarqueeSpanish: "اسپانیایی",
    v2MarqueeFrench: "فرانسوی",
    v2MarqueeGerman: "آلمانی",
    v2MarqueeItalian: "ایتالیایی",
    v2MarqueeTurkish: "ترکی",
    v2WhatTag: "LinguaTalk چیست؟",
    v2WhatTitle1: "نه فقط درس —",
    v2WhatTitle2: "مسیر ساخته‌شده دور شما",
    v2WhatDesc: "بیشتر اپ‌ها همان محتوا را به همه می‌دهند. LinguaTalk با درک سطح، اهداف و زبان مورد نظر شما شروع می‌کند — سپس مسیر شخصی می‌سازد. از آنجا با گفتگوهای واقعی با مربیان هوش مصنوعی و سخنرانان بومی تمرین می‌کنید، متناسب با جایی که الان هستید.",
    v2WhyLabel: "چرا کار می‌کند",
    v2Feature1Title: "مربیان هوش مصنوعی",
    v2Feature1Desc: "با شخصیت‌های هوشمند هوش مصنوعی صحبت کنید که مانند یک معلم واقعی پاسخ می‌دهند، اصلاح می‌کنند و راهنمایی می‌کنند.",
    v2Feature2Title: "گفتگوهای واقعی",
    v2Feature2Desc: "بدون فلش‌کارت. بدون چندگزینه‌ای. فقط شما و مربی‌تان — گفتگو به زبان مورد نظر.",
    v2Feature3Title: "آزمون تعیین سطح",
    v2Feature3Desc: "از سطح درست شروع کنید. یک آزمون کوتاه شما را قبل از اولین درس در جای مناسب قرار می‌دهد.",
    v2Feature4Title: "۱۲+ زبان",
    v2Feature4Desc: "انگلیسی، اسپانیایی، فرانسوی، آلمانی، ایتالیایی، ترکی و بیشتر — همه در یک اپ.",
    v2HowLabel: "چگونه کار می‌کند",
    v2HowTitle1: "سه قدم برای شروع",
    v2HowTitle2: "صحبت با اعتماد به خود",
    v2Step1Title: "آزمون تعیین سطح بگیرید",
    v2Step1Desc: "یک آزمون ۵ دقیقه‌ای سطح دقیق شما را مشخص می‌کند.",
    v2Step2Title: "مربی هوش مصنوعی انتخاب کنید",
    v2Step2Desc: "شخصیتی متناسب با سبک و زبان هدف خود انتخاب کنید.",
    v2Step3Title: "هر روز گفتگو کنید",
    v2Step3Desc: "گفتگوهای کوتاه روزانه اعتماد به خود در صحبت را می‌سازد.",
    v2RingSub: "سطح شما",
    v2StatLessons: "درس انجام‌شده",
    v2StatStreak: "روز متوالی 🔥",
    v2StatWords: "کلمه یادگرفته",
    v2CtaTitle: "مسیر رایگان خود را امروز بگیرید",
    v2CtaSub: "۵ دقیقه راه‌اندازی · همیشه رایگان · iOS و Android",
    v2PopupTitle: "سطح خود را پیدا کنید",
    v2PopupDesc: "یک آزمون تعیین سطح ۵ دقیقه‌ای رایگان بگیرید و مسیر یادگیری شخصی دریافت کنید.",
    v2PopupBtn: "شروع آزمون — رایگان",
    v2PopupFootnote: "بدون ثبت‌نام",
    v2ChipSpanish: "🇪🇸 درس اسپانیایی",
    v2ChipAiTyping: "🤖 هوش مصنوعی در حال تایپ…",
    v2ChipStreak: "🔥 ۷ روز متوالی",
    v2ChatToday: "امروز",
    v2ChatInputPlaceholder: "پاسخ خود را بنویسید…",
    v2TutorSub: "مربی هوش مصنوعی · اسپانیایی",
  },
  ar: {
    appTitle: "LinguaTalk",
    appTagline: "ابحث عن شريك اللغة المثالي. تدرب على أي لغة على وتيرتك.",
    appDescription:
      "يربط LinguaTalk متحمسي اللغة بشركاء التدرب من جميع أنحاء العالم. سواء كنت ترغب في تعلم العربية أو الصينية أو الإنجليزية أو الفرنسية أو الألمانية أو الإيطالية أو اليابانية أو الكورية أو الفارسية أو الروسية أو التركية أو أي لغة أخرى، فنحن نغطي احتياجاتك. دردش مع معلمي الذكاء الاصطناعي للتدرب الموجه أو تواصل مع أشخاص حقيقيين لتحسين مهارات التحدث والكتابة والقراءة في بيئة اجتماعية وممتعة.",
    downloadIOS: "تنزيل لـ iOS",
    downloadAndroid: "تنزيل لـ Android",
    directDownload: "تنزيل ملف .apk",
    learnMore: "تعرف على المزيد عن LinguaTalk",
    howItWorks: "كيف يعمل LinguaTalk",
    step1Title: "أنشئ ملفك الشخصي",
    step1Description:
      "قم بإعداد تفضيلات اللغة ومستوى المهارة والاهتمامات للمساعدة في العثور على التطابق المثالي.",
    step2Title: "ابحث عن شركاء",
    step2Description:
      "استكشف شخصيات الذكاء الاصطناعي أو اكتشف مستخدمين حقيقيين يشاركونك أهداف تعلم اللغة.",
    step3Title: "تدرب وتحسّن",
    step3Description:
      "دردش أو أجرِ مكالمات صوتية أو أرسل رسائل لممارسة مهارات اللغة المستهدفة بانتظام.",
    whyChoose: "لماذا تختار LinguaTalk؟",
    feature1Title: "شركاء اللغة من الذكاء الاصطناعي",
    feature1Description:
      "تدرب في أي وقت مع شخصيات ذكاء اصطناعي ذكية مصممة لمساعدتك على تحسين مهاراتك اللغوية.",
    feature2Title: "شركاء حقيقيون",
    feature2Description:
      "تواصل مع متحدثين أصليين ومتعلمين آخرين لتبادل اللغة بشكل أصيل.",
    feature3Title: "ملفات شخصية مفصلة",
    feature3Description:
      "أنشئ ملفك الشخصي وخصصه لعرض مهاراتك واهتماماتك اللغوية.",
    feature4Title: "مطابقة ذكية",
    feature4Description:
      "ابحث عن شريك اللغة المثالي بناءً على مستوى مهارتك واهتماماتك وأهداف التعلم.",
    aiScreenTitle: "دردش مع شخصيات الذكاء الاصطناعي",
    aiScreenDescription:
      "حسّن مهاراتك اللغوية من خلال الدردشة مع شخصيات الذكاء الاصطناعي المصممة لمساعدتك على التدرب في بيئة آمنة وخالية من الأحكام. مثالي للمبتدئين أو عندما لا يتوفر شركاء بشريون.",
    exploreScreenTitle: "استكشف الشركاء الحقيقيين",
    exploreScreenDescription:
      "ابحث عن مستخدمين حقيقيين من جميع أنحاء العالم وتواصل معهم. قم بالتصفية حسب اللغة ومستوى الإتقان والاهتمامات للعثور على شريك تبادل اللغة المثالي.",
    footerText: "© ٢٠٢٥ LinguaTalk. جميع الحقوق محفوظة.",
    language: "اللغة",
    // Top Menu
    menuHome: "الرئيسية",
    menuPlacementTest: "اختبار تحديد المستوى",
    menuPrivacyPolicy: "سياسة الخصوصية",
    menuSafetySecurity: "السلامة والأمان",
    menuDeleteAccount: "حذف الحساب",
    // Popup
    popupTitle: "اختبر مستوى لغتك!",
    popupDescription:
      "اكتشف مستوى إتقانك في 8 دقائق فقط. احصل على نتائج فورية من المستوى A1 إلى C2.",
    popupButton: "ابدأ الاختبار الآن",
    popupFooter: "أو قم بتنزيل التطبيق لحفظ نتائجك",
    userPreviewTitle: "أنت مدعو!",
    userPreviewMessage:
      "هناك من يريد ممارسة اللغات معك على Lingua Talk. حمّل التطبيق للتواصل — مجاني وممتع!",
    userPreviewTagline: "تعلّم اللغات معاً",
    userPreviewOpenInApp: "فتح في التطبيق",
    userPreviewHint: "تحميل مجاني · لا حاجة لحساب للتجربة",
    // Placement Test
    testStarting: "بدء الاختبار...",
    getReady: "استعد!",
    testPreview: "معاينة الاختبار",
    testYourLanguageProficiency: "اختبر كفاءتك اللغوية",
    guest: "ضيف",
    totalQuestions: "إجمالي الأسئلة",
    totalTime: "الوقت الإجمالي",
    testParts: "أجزاء الاختبار",
    question: "سؤال",
    questions: "أسئلة",
    startTest: "بدء الاختبار",
    loadingTest: "جاري تحميل الاختبار...",
    preparingTest:
      "يرجى الانتظار بينما نقوم بإعداد اختبار تحديد المستوى الخاص بك.",
    unableToLoadTest: "تعذر تحميل الاختبار. يرجى المحاولة مرة أخرى من التطبيق.",
    greatJob: "عمل رائع!",
    yourEstimatedLevel: "مستواك المقدر هو",
    score: "النتيجة:",
    testEvaluated:
      "قيم هذا الاختبار كفاءتك في {language} عبر مستويات متعددة من A1 إلى C2.",
    greatWorkCompleted:
      "عمل رائع! لقد أكملت اختبار تحديد المستوى في {language}. سجل الدخول لحفظ تقدمك وفتح ميزات التعلم المخصصة.",
    tryAgain: "حاول مرة أخرى",
    backToHome: "العودة إلى الصفحة الرئيسية",
    test: "اختبار",
    level: "المستوى:",
    type: "النوع:",
    questionOf: "السؤال {current} من {total} ({answered} تم الإجابة)",
    playAudio: "تشغيل الصوت",
    previous: "السابق",
    next: "التالي",
    submitTest: "إرسال الاختبار",
    testResultSaved: "تم حفظ نتيجة الاختبار بنجاح!",
    testResultFailed: "فشل حفظ نتيجة الاختبار. يرجى المحاولة مرة أخرى.",
    saveResultsTrackProgress: "هل تريد حفظ نتائجك وتتبع تقدمك؟",
    improveWithApp:
      "يمكنك أيضًا تحسين مهاراتك اللغوية مع تطبيق الذكاء الاصطناعي وشركائنا الحقيقيين.",
    grammar: "قواعد",
    vocabulary: "المفردات",
    listening: "الاستماع",
    downloadForIOS: "تحميل لـ iOS",
    downloadForAndroid: "تحميل لـ Android",
    placementTest: "اختبار تحديد المستوى",
    min: "دقيقة",
    testForLanguage: "اختبار تحديد المستوى للغة:",
    testNotImplemented: "هل تبحث عن اختبار لغة آخر؟",
    availableLanguages: "في الوقت الحالي، يمكنك إجراء الاختبار بهذه اللغات:",
    viewAllLanguages: "هل تبحث عن اختبار لغة آخر؟",
    comingSoon: "قريباً...",

    // Privacy Page (placeholders)
    privacyTitle: "سياسة الخصوصية",
    privacyIntro:
      "في Lingua Talk، نحن نقدر خصوصيتك ونلتزم بحماية معلوماتك الشخصية. توضح هذه السياسة كيف نجمع بياناتك ونستخدمها ونحميها عند استخدامك منصتنا لتعلم اللغات والتواصل الاجتماعي.",
    informationWeCollect: "المعلومات التي نجمعها",
    accountInformationTitle: "معلومات الحساب:",
    infoCollectEmailUsername: "عنوان البريد الإلكتروني واسم المستخدم",
    infoCollectProfileInfo: "معلومات الملف الشخصي (الاسم، العمر، الموقع)",
    infoCollectLanguagePrefs: "تفضيلات اللغة وأهداف التعلم",
    infoCollectSettingsPrefs: "إعدادات الحساب والتفضيلات",
    usageDataTitle: "بيانات الاستخدام:",
    usageDataConversationsAI: "المحادثات مع الشخصيات الذكية (الذكاء الاصطناعي)",
    usageDataMessagesPartners: "الرسائل مع شركاء اللغة",
    usageDataProgressStats: "التقدم في التعلم والإحصائيات",
    usageDataAppPatterns: "أنماط وتفضيلات استخدام التطبيق",
    howWeUseTitle: "كيف نستخدم معلوماتك",
    provideServicesTitle: "تقديم خدماتنا",
    provideServicesDesc: "تمكين ميزات تعلم اللغة وربط الشركاء",
    improveExperienceTitle: "تحسين تجربة المستخدم",
    improveExperienceDesc: "تخصيص المحتوى وتفاعلات الشخصيات الذكية",
    safetyModerationTitle: "السلامة والإشراف",
    safetyModerationDesc: "مراقبة المحادثات لرصد المحتوى غير اللائق",
    communicationTitle: "التواصل",
    communicationDesc: "إرسال التحديثات والإشعارات الهامة",
    analyticsTitle: "التحليلات",
    analyticsDesc: "فهم كيفية تفاعل المستخدمين مع منصتنا",
    dataProtectionTitle: "حماية البيانات",
    encryptionTitle: "التشفير",
    encryptionDesc: "جميع البيانات مشفرة أثناء النقل والتخزين",
    accessControlsTitle: "ضوابط الوصول",
    accessControlsDesc: "وصول محدود للموظفين عند الحاجة فقط",
    regularAuditsTitle: "المراجعات الدورية",
    regularAuditsDesc: "مراجعات أمنية وتقييمات للثغرات",
    secureInfrastructureTitle: "بنية تحتية آمنة",
    secureInfrastructureDesc: "معايير أمان سحابية معتمدة على مستوى الصناعة",
    dataMinimizationTitle: "تقليل البيانات",
    dataMinimizationDesc: "نجمع فقط المعلومات الضرورية",
    dataSharingTitle: "مشاركة البيانات والأطراف الثالثة",
    noSellData: "نحن لا نبيع بياناتك الشخصية لأطراف ثالثة.",
    serviceProvidersTitle: "مزودو الخدمات",
    serviceProvidersDesc:
      "الاستضافة السحابية، التحليلات، وخدمات الأمان التي تساعدنا في تشغيل المنصة",
    legalRequirementsTitle: "المتطلبات القانونية",
    legalRequirementsDesc:
      "عندما يتطلب القانون أو أمر المحكمة أو لحماية سلامة المستخدمين",
    withConsentTitle: "بموافقتك",
    withConsentDesc:
      "فقط عندما توافق صراحة على مشاركة المعلومات مع شركاء محددين",
    yourRightsTitle: "حقوقك في الخصوصية",
    rightAccessTitle: "الوصول",
    rightAccessDesc: "عرض بياناتك الشخصية",
    rightCorrectionTitle: "التصحيح",
    rightCorrectionDesc: "تحديث المعلومات غير الصحيحة",
    rightDeletionTitle: "الحذف",
    rightDeletionDesc: "حذف حسابك وبياناتك",
    rightPortabilityTitle: "قابلية النقل",
    rightPortabilityDesc: "تصدير بياناتك",
    contactUsTitle: "اتصل بنا",
    contactUsDesc: "للاستفسارات المتعلقة بالخصوصية أو لممارسة حقوقك:",
    policyUpdatesTitle: "تحديثات السياسة",
    policyUpdatesDesc:
      "قد نقوم بتحديث هذه السياسة من وقت لآخر. سنخطرك بأي تغييرات هامة عبر البريد الإلكتروني أو إشعار داخل التطبيق.",
    lastUpdated: "آخر تحديث: يناير 2024",

    // Delete Account Page (placeholders)
    deleteTitle: "حذف حسابك",
    deleteIntro:
      "نأسف لرؤيتك تغادر! إذا كنت بحاجة لحذف حسابك في Lingua Talk، يرجى اتباع التعليمات أدناه. هذا الإجراء دائم ولا يمكن التراجع عنه.",
    deleteImportantTitle:
      "⚠️ هام: يمكن حذف الحساب فقط من خلال التطبيق على الهاتف المحمول",
    deleteImportantDesc:
      "لأسباب أمنية، لا يمكن حذف الحساب إلا عبر تطبيق Lingua Talk على الهاتف المحمول. لا يمكنك حذف حسابك من هذا الموقع.",
    howToDeleteTitle: "كيفية حذف حسابك",
    stepOpenAppTitle: "افتح تطبيق Lingua Talk على الهاتف",
    stepOpenAppDesc: "قم بتشغيل التطبيق على هاتفك أو جهازك اللوحي",
    stepGoSettingsTitle: "اذهب إلى الإعدادات",
    stepGoSettingsDesc: "انتقل إلى إعدادات ملفك الشخصي أو إعدادات الحساب",
    stepFindAccountTitle: "ابحث عن إدارة الحساب",
    stepFindAccountDesc: 'ابحث عن قسم "إعدادات الحساب" أو "الخصوصية والأمان"',
    stepSelectDeleteTitle: 'اختر "حذف الحساب"',
    stepSelectDeleteDesc: "اختر خيار حذف الحساب واتبع خطوات التأكيد",
    whatDeletedTitle: "ما الذي سيتم حذفه نهائيًا",
    personalDataTitle: "البيانات الشخصية",
    personalProfileInfo: "معلومات وإعدادات ملفك الشخصي",
    personalEmailCredentials: "البريد الإلكتروني وبيانات تسجيل الدخول",
    personalLangPrefsProgress: "تفضيلات اللغة والتقدم في التعلم",
    personalStatsAchievements: "إحصائياتك وإنجازاتك الشخصية",
    conversationsContentTitle: "المحادثات والمحتوى",
    conversationsWithPartners: "جميع محادثاتك مع شركاء اللغة",
    chatHistoryAI: "سجل المحادثات مع الشخصيات الذكية",
    sharedMediaVoice: "الوسائط والرسائل الصوتية المشتركة",
    learningSessionRecordings: "تسجيلات جلسات التعلم",
    socialConnectionsTitle: "الاتصالات الاجتماعية",
    partnerConnections: "اتصالاتك مع شركاء اللغة",
    friendListsBlocked: "قوائم الأصدقاء والمستخدمين المحظورين",
    groupMemberships: "الانضمام إلى المجموعات والمجتمعات",
    favoritesSavedContent: "المفضلة والمحتوى المحفوظ",
    aiCharacterDataTitle: "بيانات الشخصيات الذكية",
    customAIConfigs: "إعدادات مخصصة للشخصيات الذكية",
    aiConversationHistory: "سجل المحادثات مع المعلمين الذكيين",
    personalizedRecommendations: "توصيات تعلم شخصية",
    aiGeneratedContent: "المحتوى والردود التي أنشأها الذكاء الاصطناعي",
    beforeDeleteTitle: "قبل حذف حسابك",
    exportYourDataTitle: "تصدير بياناتك",
    exportYourDataDesc:
      "قم بتنزيل سجل محادثاتك وتقدمك في التعلم من إعدادات التطبيق قبل الحذف",
    considerBreakTitle: "فكر في أخذ استراحة",
    considerBreakDesc: "يمكنك تعطيل حسابك مؤقتًا بدلًا من حذفه نهائيًا",
    contactSupportTitle: "اتصل بالدعم",
    contactSupportDesc:
      "إذا كنت تواجه مشكلات، قد يتمكن فريق الدعم لدينا من مساعدتك في حلها",
    needHelpTitle: "هل تحتاج إلى مساعدة؟",
    needHelpIntro:
      "إذا كنت تواجه صعوبة في حذف حسابك أو لديك أسئلة حول العملية:",
    supportResponseTime:
      "عادةً ما يستجيب فريق الدعم خلال 24 ساعة ويمكنه مساعدتك في:",
    helpDeletionTroubleshooting: "استكشاف مشكلات حذف الحساب",
    helpDataExport: "طلبات تصدير البيانات",
    helpAlternativeSolutions: "حلول بديلة لمشكلات الحساب",
    helpPrivacySecurity: "أسئلة الخصوصية والأمان",
    securityNoteTitle: "ملاحظة أمان",
    securityNoteDesc:
      "يتم معالجة طلبات حذف الحساب بأمان، وتكتمل عادةً خلال 30 يومًا لضمان إزالة جميع البيانات من الأنظمة والنسخ الاحتياطية.",

    // Safety Page (placeholders)
    safetyTitle: "Lingua Talk – سياسة سلامة وحماية الأطفال",
    safetyIntro:
      "في Lingua Talk، تعتبر سلامة ورفاهية مستخدمينا، وخاصة القُصَّر، أولوية قصوى. نحن ملتزمون بتوفير بيئة آمنة ومحترمة للجميع في تطبيقنا لتعلم اللغات والتواصل.",
    zeroToleranceTitle: "عدم التسامح مطلقًا مع استغلال الأطفال",
    zeroToleranceIntro: "نحن لا نسمح بأي محتوى أو سلوك يتعلق بـ:",
    zeroToleranceItem1: "الاستغلال أو الإساءة الجنسية للأطفال",
    zeroToleranceItem2: "السلوك غير اللائق أو الضار تجاه القُصَّر",
    zeroToleranceItem3:
      "أي شكل من أشكال الاستدراج أو العُري المتعلق بالقُصَّر أو الاتصالات غير القانونية",
    zeroToleranceAction: "سيتم إزالة هذا المحتوى فورًا وإبلاغ السلطات المختصة.",
    moderationTitle: "الإشراف والمراقبة",
    moderationItem1:
      "يمكن للمستخدمين الإبلاغ عن المحادثات والملفات الشخصية مباشرة داخل التطبيق.",
    moderationItem2: "يتم مراجعة المحتوى المبلغ عنه من قبل فريق الإشراف.",
    moderationItem3:
      "نتخذ إجراءات سريعة ضد الانتهاكات (تحذيرات، إيقافات، حظر، أو إبلاغ قانوني).",
    userAgePolicyTitle: "سياسة عمر المستخدم",
    userAgePolicyDesc:
      "يُخصص Lingua Talk للمستخدمين بعمر 13 عامًا فما فوق. قد يتم تعليق أو إزالة الحسابات التي تنتهك هذه السياسة.",
    reportAbuseTitle: "كيفية الإبلاغ عن إساءة",
    reportAbuseIntro: "يمكن للمستخدمين الإبلاغ عن:",
    reportAbuseItem1: "الرسائل أو المحتوى غير المناسب في الدردشات",
    reportAbuseItem2: "الملفات الشخصية أو السلوك المشبوه",
    reportAbuseUseButton:
      'استخدم زر "الإبلاغ" في الدردشات أو الملفات الشخصية. يتم مراجعة البلاغات من قبل فريق الإشراف.',
    complianceTitle: "الامتثال لقوانين حماية الأطفال",
    compliesWith: "يلتزم Lingua Talk بـ:",
    complianceItem1: "معايير Google Play لسلامة الأطفال",
    complianceItem2:
      "القوانين المعمول بها بشأن استغلال الأطفال في جميع المناطق",
    complianceItem3: "أفضل الممارسات الصناعية لسلامة المنصات والإبلاغ",

    contactTitle: "الاتصال",
    safetyContactIntro:
      "للاستفسارات المتعلقة بسلامة الأطفال أو للإبلاغ عن مشكلة خطيرة، يرجى التواصل مع فريق السلامة لدينا على:",

    // Home V2
    v2MetaTitle: "LinguaTalk — تعلم اللغات بالتحدث مع الذكاء الاصطناعي",
    v2MetaDescription: "LinguaTalk تطبيق لتعلم اللغات. تحدث مع مدرسين ذكاء اصطناعي بالإسبانية والفرنسية واليابانية و9 لغات أخرى. مجاني على iOS وAndroid.",
    v2HeroLine1: "الذكاء الاصطناعي يصمم",
    v2HeroLine2: "خريطة طريقك الشخصية للإتقان",
    v2Pill: "📱 تطبيق تعلم اللغات — مجاني على iOS وAndroid",
    v2Subtext: "LinguaTalk يبني مسار تعلم مناسب لمستواك — ثم تتمرن يومياً مع مدرسين ذكاء اصطناعي ومتحدثين أصليين حتى تتحدث بثقة.",
    v2StoreSubApple: "التنزيل من",
    v2StoreNameApple: "App Store",
    v2StoreSubGoogle: "احصل عليه من",
    v2StoreNameGoogle: "Google Play",
    v2BazaarSub: "التنزيل من",
    v2BazaarName: "كافه بازار",
    v2MarqueeEnglish: "الإنجليزية",
    v2MarqueeSpanish: "الإسبانية",
    v2MarqueeFrench: "الفرنسية",
    v2MarqueeGerman: "الألمانية",
    v2MarqueeItalian: "الإيطالية",
    v2MarqueeTurkish: "التركية",
    v2WhatTag: "ما هو LinguaTalk؟",
    v2WhatTitle1: "ليس دروساً فقط —",
    v2WhatTitle2: "خريطة طريق مبنية حولك",
    v2WhatDesc: "معظم التطبيقات تقدم نفس المحتوى للجميع. LinguaTalk يبدأ بفهم مستواك وأهدافك واللغة التي تريد تعلمها — ثم يبني خريطة طريق شخصية. من هناك تتمرن عبر محادثات حقيقية مع مدرسين ذكاء اصطناعي ومتحدثين أصليين، مناسبة لمستواك الحالي.",
    v2WhyLabel: "لماذا ينجح",
    v2Feature1Title: "مدرسون بالذكاء الاصطناعي",
    v2Feature1Desc: "تحدث مع شخصيات ذكاء اصطناعي ترد، تصحح، وترشدك مثل معلم حقيقي.",
    v2Feature2Title: "محادثات حقيقية",
    v2Feature2Desc: "بدون بطاقات. بدون اختيار من متعدد. فقط أنت ومدرسك — تتحدث باللغة التي تريد تعلمها.",
    v2Feature3Title: "اختبار تحديد المستوى",
    v2Feature3Desc: "ابدأ بالمستوى المناسب. اختبار قصير يضعك في المكان الصحيح قبل أول درس.",
    v2Feature4Title: "١٢+ لغة",
    v2Feature4Desc: "الإنجليزية، الإسبانية، الفرنسية، الألمانية، الإيطالية، التركية والمزيد — في تطبيق واحد.",
    v2HowLabel: "كيف يعمل",
    v2HowTitle1: "ثلاث خطوات للبدء",
    v2HowTitle2: "التحدث بثقة",
    v2Step1Title: "خذ اختبار تحديد المستوى",
    v2Step1Desc: "اختبار تكيفي لمدة 5 دقائق يحدد مستواك بدقة.",
    v2Step2Title: "اختر مدرسك الذكي",
    v2Step2Desc: "اختر شخصية تناسب أسلوبك ولغتك المستهدفة.",
    v2Step3Title: "ابدأ المحادثة يومياً",
    v2Step3Desc: "محادثات يومية قصيرة تبني ثقة حقيقية في التحدث.",
    v2RingSub: "مستواك",
    v2StatLessons: "دروس مكتملة",
    v2StatStreak: "أيام متتالية 🔥",
    v2StatWords: "كلمات تعلمتها",
    v2CtaTitle: "احصل على خريطتك المجانية اليوم",
    v2CtaSub: "٥ دقائق للإعداد · مجاني للأبد · iOS وAndroid",
    v2PopupTitle: "اعرف مستواك",
    v2PopupDesc: "خذ اختبار تحديد مستوى مجاني لمدة 5 دقائق واحصل على خريطة تعلم شخصية.",
    v2PopupBtn: "ابدأ الاختبار — مجاني",
    v2PopupFootnote: "بدون تسجيل",
    v2ChipSpanish: "🇪🇸 درس إسباني",
    v2ChipAiTyping: "🤖 الذكاء الاصطناعي يكتب…",
    v2ChipStreak: "🔥 ٧ أيام متتالية",
    v2ChatToday: "اليوم",
    v2ChatInputPlaceholder: "اكتب ردك…",
    v2TutorSub: "مدرس ذكاء اصطناعي · إسباني",
  },
  ko: {
    appTitle: "LinguaTalk",
    appTagline:
      "완벽한 언어 파트너를 찾아보세요. 어떤 언어든 자신의 속도로 연습하세요.",
    appDescription:
      "LinguaTalk은 언어 애호가들을 전 세계의 연습 파트너와 연결합니다. 아랍어, 중국어, 영어, 프랑스어, 독일어, 이탈리아어, 일본어, 한국어, 페르시아어, 러시아어, 터키어 또는 다른 어떤 언어를 배우고 싶으시든, 저희가 도와드립니다. AI 튜터와 채팅하여 안내된 연습을 하거나 실제 사람들과 연결하여 재미있고 사회적인 환경에서 말하기, 쓰기 및 읽기 기술을 향상시키세요.",
    downloadIOS: "iOS용 다운로드",
    downloadAndroid: "Android용 다운로드",
    directDownload: ".apk 파일 다운로드",
    learnMore: "LinguaTalk에 대해 더 알아보기",
    howItWorks: "LinguaTalk 이용 방법",
    step1Title: "프로필 만들기",
    step1Description:
      "언어 선호도, 숙련도 및 관심사를 설정하여 완벽한 매치를 찾는 데 도움이 됩니다.",
    step2Title: "파트너 찾기",
    step2Description:
      "AI 캐릭터를 탐색하거나 언어 학습 목표를 공유하는 실제 사용자를 발견하세요.",
    step3Title: "연습 및 향상",
    step3Description:
      "정기적으로 목표 언어 기술을 연습하기 위해 채팅, 음성 통화 또는 메시지를 보내세요.",
    whyChoose: "LinguaTalk을 선택하는 이유",
    feature1Title: "AI 언어 파트너",
    feature1Description:
      "언어 기술 향상을 돕기 위해 설계된 지능적인 AI 캐릭터와 언제든지 연습하세요.",
    feature2Title: "실제 파트너",
    feature2Description:
      "원어민 및 다른 학습자와 연결하여 진정한 언어 교환을 경험하세요.",
    feature3Title: "상세 프로필",
    feature3Description:
      "프로필을 생성하고 맞춤 설정하여 언어 기술과 관심사를 보여주세요.",
    feature4Title: "스마트 매칭",
    feature4Description:
      "숙련도, 관심사 및 학습 목표에 따라 완벽한 언어 파트너를 찾으세요.",
    aiScreenTitle: "AI 캐릭터와 채팅하기",
    aiScreenDescription:
      "안전하고 판단 없는 환경에서 연습할 수 있도록 설계된 AI 캐릭터와 채팅하여 언어 기술을 향상시키세요. 초보자나 인간 파트너를 이용할 수 없을 때 완벽합니다.",
    exploreScreenTitle: "실제 파트너 탐색하기",
    exploreScreenDescription:
      "전 세계의 실제 사용자를 검색하고 연결하세요. 언어, 숙련도 및 관심사로 필터링하여 완벽한 언어 교환 파트너를 찾으세요.",
    footerText: "© 2025 LinguaTalk. 모든 권리 보유.",
    language: "언어",
    // Top Menu
    menuHome: "홈",
    menuPlacementTest: "배치 테스트",
    menuPrivacyPolicy: "개인정보 처리방침",
    menuSafetySecurity: "안전 및 보안",
    menuDeleteAccount: "계정 삭제",
    // Popup
    popupTitle: "언어 수준을 테스트하세요!",
    popupDescription:
      "단 8분 만에 실력을 확인하세요. A1부터 C2까지 즉시 결과를 받아보세요.",
    popupButton: "지금 테스트 시작",
    popupFooter: "또는 앱을 다운로드하여 결과를 저장하세요",
    userPreviewTitle: "Join the conversation!",
    userPreviewMessage:
      "A friend is waiting to practice languages with you on Lingua Talk. Download the app and connect — free and fun!",
    userPreviewTagline: "Practice together",
    userPreviewOpenInApp: "Open in app",
    userPreviewHint: "Free to download · No account needed to try",
    // Placement Test
    testStarting: "테스트 시작 중...",
    getReady: "준비하세요!",
    testPreview: "테스트 미리보기",
    testYourLanguageProficiency: "언어 실력을 테스트하세요",
    guest: "게스트",
    totalQuestions: "전체 문제 수",
    totalTime: "총 시간",
    testParts: "테스트 섹션",
    question: "문제",
    questions: "문제",
    startTest: "테스트 시작",
    loadingTest: "테스트 로딩 중...",
    preparingTest: "배치 테스트를 준비하는 동안 잠시 기다려 주세요.",
    unableToLoadTest: "테스트를 불러올 수 없습니다. 앱에서 다시 시도해 주세요.",
    greatJob: "훌륭합니다!",
    yourEstimatedLevel: "예상 수준은",
    score: "점수:",
    testEvaluated:
      "이 배치 테스트는 A1부터 C2까지 여러 수준에서 {language} 실력을 평가했습니다.",
    greatWorkCompleted:
      "훌륭합니다! {language} 배치 테스트를 완료하셨습니다. 진행 상황을 저장하고 개인화된 학습 기능을 잠금 해제하려면 로그인하세요.",
    tryAgain: "다시 시도",
    backToHome: "홈으로 돌아가기",
    test: "테스트",
    level: "수준:",
    type: "유형:",
    questionOf: "문제 {current}/{total} ({answered}개 답변함)",
    playAudio: "오디오 재생",
    previous: "이전",
    next: "다음",
    submitTest: "테스트 제출",
    testResultSaved: "테스트 결과가 성공적으로 저장되었습니다!",
    testResultFailed: "테스트 결과 저장에 실패했습니다. 다시 시도해 주세요.",
    saveResultsTrackProgress:
      "결과를 저장하고 진행 상황을 추적하고 싶으신가요?",
    improveWithApp:
      "또한 앱 AI와 실제 파트너와 함께 언어 실력을 향상시킬 수 있습니다.",
    grammar: "문법",
    vocabulary: "어휘",
    listening: "듣기",
    downloadForIOS: "iOS용 다운로드",
    downloadForAndroid: "Android용 다운로드",
    placementTest: "배치 테스트",
    min: "분",
    testForLanguage: "언어 테스트:",
    testNotImplemented: "다른 언어 테스트를 찾고 계신가요?",
    availableLanguages: "현재 다음 언어로 테스트를 진행할 수 있습니다:",
    viewAllLanguages: "다른 언어 테스트를 찾고 계신가요?",
    comingSoon: "곧 출시 예정...",

    // Privacy Page (placeholders)
    privacyTitle: "개인정보 처리방침",
    privacyIntro:
      "Lingua Talk은 회원님의 개인정보를 소중히 여기며 이를 보호하기 위해 최선을 다하고 있습니다. 본 방침은 당사가 언어 학습 및 소셜 플랫폼을 제공할 때 회원님의 데이터를 어떻게 수집, 사용 및 보호하는지를 설명합니다.",
    informationWeCollect: "수집하는 정보",
    accountInformationTitle: "계정 정보:",
    infoCollectEmailUsername: "이메일 주소와 사용자 이름",
    infoCollectProfileInfo: "프로필 정보 (이름, 나이, 위치)",
    infoCollectLanguagePrefs: "언어 선호도 및 학습 목표",
    infoCollectSettingsPrefs: "계정 설정 및 환경설정",
    usageDataTitle: "사용 데이터:",
    usageDataConversationsAI: "AI 캐릭터와의 대화",
    usageDataMessagesPartners: "언어 파트너와의 메시지",
    usageDataProgressStats: "학습 진행 상황 및 통계",
    usageDataAppPatterns: "앱 사용 패턴 및 선호도",
    howWeUseTitle: "정보 활용 방법",
    provideServicesTitle: "서비스 제공",
    provideServicesDesc: "언어 학습 기능과 파트너 매칭 지원",
    improveExperienceTitle: "사용자 경험 개선",
    improveExperienceDesc: "콘텐츠와 AI 캐릭터 상호작용 개인화",
    safetyModerationTitle: "안전 및 관리",
    safetyModerationDesc: "부적절한 콘텐츠 감지를 위한 대화 모니터링",
    communicationTitle: "소통",
    communicationDesc: "중요한 업데이트 및 알림 전송",
    analyticsTitle: "분석",
    analyticsDesc: "사용자가 플랫폼을 어떻게 사용하는지 이해",
    dataProtectionTitle: "데이터 보호",
    encryptionTitle: "암호화",
    encryptionDesc: "모든 데이터는 전송 중과 저장 중 암호화됩니다",
    accessControlsTitle: "접근 제어",
    accessControlsDesc: "직원 접근은 필요 최소한으로 제한",
    regularAuditsTitle: "정기 감사",
    regularAuditsDesc: "보안 검토 및 취약점 평가",
    secureInfrastructureTitle: "안전한 인프라",
    secureInfrastructureDesc: "업계 표준 클라우드 보안 조치 적용",
    dataMinimizationTitle: "데이터 최소화",
    dataMinimizationDesc: "필요한 정보만 수집합니다",
    dataSharingTitle: "데이터 공유 및 제3자",
    noSellData: "개인정보를 제3자에게 판매하지 않습니다.",
    serviceProvidersTitle: "서비스 제공업체",
    serviceProvidersDesc:
      "플랫폼 운영을 지원하는 클라우드 호스팅, 분석 및 보안 서비스",
    legalRequirementsTitle: "법적 요구",
    legalRequirementsDesc:
      "법, 법원 명령 또는 사용자 안전 보호를 위해 필요할 때",
    withConsentTitle: "사용자 동의 시",
    withConsentDesc:
      "사용자가 특정 파트너와 정보 공유에 명시적으로 동의할 때만",
    yourRightsTitle: "개인정보 권리",
    rightAccessTitle: "열람",
    rightAccessDesc: "개인정보 확인",
    rightCorrectionTitle: "정정",
    rightCorrectionDesc: "잘못된 정보 수정",
    rightDeletionTitle: "삭제",
    rightDeletionDesc: "계정 및 데이터 삭제",
    rightPortabilityTitle: "이동",
    rightPortabilityDesc: "데이터 내보내기",
    contactUsTitle: "문의하기",
    contactUsDesc: "개인정보 관련 문의 또는 권리 행사:",
    policyUpdatesTitle: "정책 업데이트",
    policyUpdatesDesc:
      "본 방침은 수시로 업데이트될 수 있습니다. 중요한 변경 사항은 이메일 또는 앱 내 알림을 통해 알려드립니다.",
    lastUpdated: "마지막 업데이트: 2024년 1월",

    // Delete Account Page (placeholders)
    deleteTitle: "계정 삭제",
    deleteIntro:
      "떠나시게 되어 아쉽습니다! Lingua Talk 계정을 삭제하려면 아래 지침을 따라 주세요. 이 작업은 영구적이며 취소할 수 없습니다.",
    deleteImportantTitle: "⚠️ 중요: 계정 삭제는 모바일 앱에서만 가능합니다",
    deleteImportantDesc:
      "보안상의 이유로 계정 삭제는 Lingua Talk 모바일 앱에서만 가능합니다. 웹사이트에서는 계정을 삭제할 수 없습니다.",
    howToDeleteTitle: "계정 삭제 방법",
    stepOpenAppTitle: "Lingua Talk 모바일 앱 열기",
    stepOpenAppDesc: "휴대폰 또는 태블릿에서 앱 실행",
    stepGoSettingsTitle: "설정으로 이동",
    stepGoSettingsDesc: "프로필 설정 또는 계정 설정으로 이동",
    stepFindAccountTitle: "계정 관리 찾기",
    stepFindAccountDesc: '"계정 설정" 또는 "개인정보 및 보안" 섹션 찾기',
    stepSelectDeleteTitle: '"계정 삭제" 선택',
    stepSelectDeleteDesc: "계정 삭제 옵션을 선택하고 확인 단계를 따르세요",
    whatDeletedTitle: "영구적으로 삭제되는 항목",
    personalDataTitle: "개인 데이터",
    personalProfileInfo: "프로필 정보 및 설정",
    personalEmailCredentials: "이메일 주소 및 로그인 정보",
    personalLangPrefsProgress: "언어 선호도 및 학습 진행 상황",
    personalStatsAchievements: "개인 통계 및 성취",
    conversationsContentTitle: "대화 및 콘텐츠",
    conversationsWithPartners: "언어 파트너와의 모든 대화",
    chatHistoryAI: "AI 캐릭터와의 대화 기록",
    sharedMediaVoice: "공유된 미디어 및 음성 메시지",
    learningSessionRecordings: "학습 세션 녹음",
    socialConnectionsTitle: "소셜 연결",
    partnerConnections: "언어 파트너 연결",
    friendListsBlocked: "친구 목록 및 차단된 사용자",
    groupMemberships: "그룹 가입 및 커뮤니티",
    favoritesSavedContent: "즐겨찾기 및 저장된 콘텐츠",
    aiCharacterDataTitle: "AI 캐릭터 데이터",
    customAIConfigs: "사용자 맞춤 AI 캐릭터 설정",
    aiConversationHistory: "AI 튜터와의 대화 기록",
    personalizedRecommendations: "개인화된 학습 추천",
    aiGeneratedContent: "AI가 생성한 콘텐츠 및 응답",
    beforeDeleteTitle: "계정 삭제 전",
    exportYourDataTitle: "데이터 내보내기",
    exportYourDataDesc:
      "계정을 삭제하기 전 앱 설정에서 대화 기록 및 학습 진행 상황을 다운로드하세요",
    considerBreakTitle: "잠시 쉬기",
    considerBreakDesc:
      "계정을 영구 삭제하지 않고 일시적으로 비활성화할 수 있습니다",
    contactSupportTitle: "지원팀 문의",
    contactSupportDesc: "문제가 있는 경우 지원팀이 해결을 도와드릴 수 있습니다",
    needHelpTitle: "도움이 필요하신가요?",
    needHelpIntro: "계정 삭제에 어려움이 있거나 절차에 대한 질문이 있는 경우:",
    supportResponseTime:
      "지원팀은 일반적으로 24시간 이내에 응답하며 다음을 도와드립니다:",
    helpDeletionTroubleshooting: "계정 삭제 문제 해결",
    helpDataExport: "데이터 내보내기 요청",
    helpAlternativeSolutions: "계정 문제 대안 솔루션",
    helpPrivacySecurity: "개인정보 및 보안 관련 질문",
    securityNoteTitle: "보안 안내",
    securityNoteDesc:
      "계정 삭제 요청은 안전하게 처리되며, 모든 시스템과 백업에서 완전히 제거되도록 일반적으로 30일 이내에 완료됩니다.",

    // Safety Page (placeholders)
    safetyTitle: "Lingua Talk – 아동 안전 및 보호 정책",
    safetyIntro:
      "Lingua Talk은 사용자, 특히 미성년자의 안전과 복지를 최우선으로 합니다. 언어 학습 및 소셜 앱으로서 AI 캐릭터와 실제 언어 파트너를 연결하는 플랫폼에서 안전하고 존중받는 환경을 유지하기 위해 최선을 다하고 있습니다.",
    zeroToleranceTitle: "아동 성학대에 대한 무관용",
    zeroToleranceIntro: "다음과 관련된 콘텐츠나 행위는 허용되지 않습니다:",
    zeroToleranceItem1: "아동 성학대 또는 착취",
    zeroToleranceItem2: "미성년자에 대한 부적절하거나 해로운 행동",
    zeroToleranceItem3: "미성년자 관련 유인, 누드 또는 불법적 의사소통",
    zeroToleranceAction:
      "모든 해당 콘텐츠는 즉시 삭제되며 관련 당국에 신고됩니다.",
    moderationTitle: "관리 및 모니터링",
    moderationItem1:
      "사용자는 앱 내에서 대화와 프로필을 직접 신고할 수 있습니다.",
    moderationItem2: "신고된 콘텐츠는 관리팀이 검토합니다.",
    moderationItem3:
      "위반 시 즉각 조치 (경고, 정지, 차단 또는 법적 신고)가 취해집니다.",
    userAgePolicyTitle: "사용자 연령 정책",
    userAgePolicyDesc:
      "Lingua Talk은 만 16세 이상 사용자만 이용할 수 있습니다. 이 정책을 위반하는 계정은 정지되거나 삭제될 수 있습니다.",
    reportAbuseTitle: "학대 신고 방법",
    reportAbuseIntro: "사용자는 다음을 신고할 수 있습니다:",
    reportAbuseItem1: "채팅 내 메시지 또는 부적절한 콘텐츠",
    reportAbuseItem2: "사용자 프로필 또는 의심스러운 행동",
    reportAbuseUseButton:
      '채팅 또는 사용자 프로필에서 "신고" 버튼을 사용하세요. 신고는 관리팀이 검토합니다.',
    complianceTitle: "아동 안전 법규 준수",
    compliesWith: "Lingua Talk은 다음을 준수합니다:",
    complianceItem1: "Google Play 아동 안전 기준",
    complianceItem2: "운영 지역 내 CSAE 관련 법규",
    complianceItem3: "플랫폼 안전 및 신고에 관한 업계 모범 사례",
    complianceCooperate: "필요 시 법 집행 기관 및 관련 당국과 적극 협력합니다.",
    contactTitle: "문의",
    safetyContactIntro:
      "아동 안전 관련 문의 또는 심각한 문제 신고는 다음을 통해 안전팀에 연락하세요:",

    // Home V2
    v2MetaTitle: "LinguaTalk — Learn languages by talking to AI",
    v2MetaDescription: "LinguaTalk is a language learning app. Chat with AI tutors in Spanish, French, Japanese and 9 more languages. Free on iOS and Android.",
    v2StoreSubApple: "Download on the",
    v2StoreNameApple: "App Store",
    v2StoreSubGoogle: "Get it on",
    v2StoreNameGoogle: "Google Play",
    v2BazaarSub: "Download from",
    v2BazaarName: "Cafe Bazaar",
    v2MarqueeEnglish: "English",
    v2MarqueeSpanish: "Español",
    v2MarqueeFrench: "Français",
    v2MarqueeGerman: "Deutsch",
    v2MarqueeItalian: "Italiano",
    v2MarqueeTurkish: "Türkçe",
    v2WhatDesc: "Most apps throw the same content at everyone. LinguaTalk starts by understanding your level, your goals, and the language you want to learn — then builds a personalized roadmap. From there, you practice through real conversations with AI tutors and native speakers, both matched to exactly where you are right now.",
    v2Feature1Title: "AI Tutors",
    v2Feature1Desc: "Talk to intelligent AI characters that respond, correct, and guide you like a real teacher.",
    v2Feature2Title: "Real Conversations",
    v2Feature2Desc: "No flashcards. No multiple choice. Just you and your tutor — chatting in the language you want to learn.",
    v2Feature3Title: "Placement Test",
    v2Feature3Desc: "Start at exactly the right level. A short test puts you where you belong before your first lesson.",
    v2Feature4Title: "12+ Languages",
    v2Feature4Desc: "English, Spanish, French, German, Italian, Turkish and more — all in one app.",
    v2Step1Title: "Take the placement test",
    v2Step1Desc: "A 5-minute adaptive quiz finds your exact level.",
    v2Step2Title: "Choose your AI tutor",
    v2Step2Desc: "Pick a character that fits your style and target language.",
    v2Step3Title: "Start chatting every day",
    v2Step3Desc: "Short daily chats build real speaking confidence over time.",
    v2RingSub: "Your level",
    v2StatLessons: "Lessons done",
    v2StatStreak: "Day streak 🔥",
    v2StatWords: "Words learned",
    v2PopupDesc: "Take a free 5-minute placement test and get a personalized learning roadmap.",
    v2ChipSpanish: "🇪🇸 Spanish lesson",
    v2ChipAiTyping: "🤖 AI is typing…",
    v2ChipStreak: "🔥 7-day streak",
    v2ChatToday: "Today",
    v2ChatInputPlaceholder: "Type your reply…",
    v2TutorSub: "AI Tutor · Spanish",
    v2HeroLine1: "AI가 설계하는",
    v2HeroLine2: "개인 맞춤 유창성 로드맵",
    v2Pill: "📱 언어 학습 앱 — iOS & Android 무료",
    v2Subtext: "LinguaTalk은 당신의 수준에 맞는 학습 경로를 만들고, AI 튜터와 원어민과 매일 연습하여 자신 있게 말할 수 있게 합니다.",
    v2WhatTag: "LinguaTalk이란?",
    v2WhatTitle1: "단순한 강의가 아닌 —",
    v2WhatTitle2: "당신을 위한 로드맵",
    v2WhyLabel: "왜 효과적인가",
    v2HowLabel: "이용 방법",
    v2HowTitle1: "시작하는 3단계",
    v2HowTitle2: "자신 있게 말하기",
    v2CtaTitle: "무료 로드맵 받기",
    v2CtaSub: "5분 설정 · 영구 무료 · iOS & Android",
    v2PopupTitle: "수준 확인하기",
    v2PopupBtn: "무료 테스트 시작",
    v2PopupFootnote: "가입 불필요",
  },
  de: {
    appTitle: "LinguaTalk",
    appTagline:
      "Finden Sie den perfekten Sprachpartner. Üben Sie jede Sprache in Ihrem eigenen Tempo.",
    appDescription:
      "LinguaTalk verbindet Sprachbegeisterte mit Übungspartnern aus der ganzen Welt. Ob Sie Arabisch, Chinesisch, Englisch, Französisch, Deutsch, Italienisch, Japanisch, Koreanisch, Persisch, Russisch, Türkisch oder eine andere Sprache lernen möchten - wir haben für Sie gesorgt. Chatten Sie mit KI-Tutoren für geführte Übungen oder verbinden Sie sich mit echten Menschen, um Ihre Sprech-, Schreib- und Lesefähigkeiten in einer unterhaltsamen, sozialen Umgebung zu verbessern.",
    downloadIOS: "Für iOS herunterladen",
    downloadAndroid: "Für Android herunterladen",
    directDownload: ".apk-Datei herunterladen",
    learnMore: "Mehr über LinguaTalk erfahren",
    howItWorks: "So funktioniert LinguaTalk",
    step1Title: "Erstellen Sie Ihr Profil",
    step1Description:
      "Legen Sie Ihre Sprachpräferenzen, Ihr Kenntnislevel und Ihre Interessen fest, um den perfekten Partner zu finden.",
    step2Title: "Partner finden",
    step2Description:
      "Erkunden Sie KI-Charaktere oder entdecken Sie echte Benutzer, die Ihre Sprachlernziele teilen.",
    step3Title: "Üben & Verbessern",
    step3Description:
      "Chatten, telefonieren oder senden Sie Nachrichten, um Ihre Zielsprachfähigkeiten regelmäßig zu üben.",
    whyChoose: "Warum LinguaTalk wählen?",
    feature1Title: "KI-Sprachpartner",
    feature1Description:
      "Üben Sie jederzeit mit intelligenten KI-Charakteren, die entwickelt wurden, um Ihre Sprachkenntnisse zu verbessern.",
    feature2Title: "Echte Partner",
    feature2Description:
      "Verbinden Sie sich mit Muttersprachlern und anderen Lernenden für authentischen Sprachaustausch.",
    feature3Title: "Detaillierte Profile",
    feature3Description:
      "Erstellen und personalisieren Sie Ihr Profil, um Ihre Sprachkenntnisse und Interessen zu präsentieren.",
    feature4Title: "Intelligentes Matching",
    feature4Description:
      "Finden Sie den perfekten Sprachpartner basierend auf Ihrem Kenntnisstand, Interessen und Lernzielen.",
    aiScreenTitle: "Mit KI-Charakteren chatten",
    aiScreenDescription:
      "Verbessern Sie Ihre Sprachkenntnisse durch Chats mit KI-Charakteren, die entwickelt wurden, um Ihnen in einer sicheren, urteilsfreien Umgebung zu helfen. Perfekt für Anfänger oder wenn keine menschlichen Partner verfügbar sind.",
    exploreScreenTitle: "Echte Partner entdecken",
    exploreScreenDescription:
      "Suchen und verbinden Sie sich mit echten Benutzern aus der ganzen Welt. Filtern Sie nach Sprache, Kenntnisstand und Interessen, um Ihren perfekten Sprachaustauschpartner zu finden.",
    footerText: "© 2025 LinguaTalk. Alle Rechte vorbehalten.",
    language: "Sprache",
    // Top Menu
    menuHome: "Startseite",
    menuPlacementTest: "Einstufungstest",
    menuPrivacyPolicy: "Datenschutzrichtlinie",
    menuSafetySecurity: "Sicherheit",
    menuDeleteAccount: "Konto löschen",
    // Popup
    popupTitle: "Testen Sie Ihr Sprachniveau!",
    popupDescription:
      "Entdecken Sie Ihre Kenntnisse in nur 8 Minuten. Erhalten Sie sofortige Ergebnisse von A1 bis C2.",
    popupButton: "Test jetzt starten",
    popupFooter:
      "oder laden Sie die App herunter, um Ihre Ergebnisse zu speichern",
    userPreviewTitle: "Du bist eingeladen!",
    userPreviewMessage:
      "Jemand möchte mit dir auf Lingua Talk Sprachen üben. Lade die App herunter und verbinde dich — kostenlos und macht Spaß!",
    userPreviewTagline: "Sprachen gemeinsam üben",
    userPreviewOpenInApp: "In App öffnen",
    userPreviewHint: "Kostenlos · Kein Konto zum Ausprobieren nötig",
    // Placement Test
    testStarting: "Test startet...",
    getReady: "Bereit machen!",
    testPreview: "Testvorschau",
    testYourLanguageProficiency: "Testen Sie Ihre Sprachkenntnisse",
    guest: "Gast",
    totalQuestions: "Gesamtfragen",
    totalTime: "Gesamtzeit",
    testParts: "Testteile",
    question: "Frage",
    questions: "Fragen",
    startTest: "Test starten",
    loadingTest: "Test wird geladen...",
    preparingTest:
      "Bitte warten Sie, während wir Ihren Einstufungstest vorbereiten.",
    unableToLoadTest:
      "Test konnte nicht geladen werden. Bitte versuchen Sie es erneut über die App.",
    greatJob: "Großartig!",
    yourEstimatedLevel: "Ihr geschätztes Niveau ist",
    score: "Punkte:",
    testEvaluated:
      "Dieser Einstufungstest hat Ihre {language}-Kenntnisse auf mehreren Niveaus von A1 bis C2 bewertet.",
    greatWorkCompleted:
      "Großartige Arbeit! Sie haben den {language}-Einstufungstest abgeschlossen. Melden Sie sich an, um Ihren Fortschritt zu speichern und personalisierte Lernfunktionen freizuschalten.",
    tryAgain: "Erneut versuchen",
    backToHome: "Zurück zur Startseite",
    test: "Test",
    level: "Niveau:",
    type: "Typ:",
    questionOf: "Frage {current} von {total} ({answered} beantwortet)",
    playAudio: "Audio abspielen",
    previous: "Zurück",
    next: "Weiter",
    submitTest: "Test absenden",
    testResultSaved: "Ihr Testergebnis wurde erfolgreich gespeichert!",
    testResultFailed:
      "Testergebnis konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.",
    saveResultsTrackProgress:
      "Möchten Sie Ihre Ergebnisse speichern und Ihren Fortschritt verfolgen?",
    improveWithApp:
      "Sie können Ihre Sprachkenntnisse auch mit unserer App-KI und echten Partnern verbessern.",
    grammar: "Grammatik",
    vocabulary: "Wortschatz",
    listening: "Hören",
    downloadForIOS: "Für iOS herunterladen",
    downloadForAndroid: "Für Android herunterladen",
    placementTest: "Einstufungstest",
    min: "Min.",
    testForLanguage: "Test für Sprache:",
    testNotImplemented: "Suchen Sie nach einem anderen Sprachtests?",
    availableLanguages:
      "Derzeit können Sie den Test in diesen Sprachen absolvieren:",
    viewAllLanguages: "Suchen Sie nach einem anderen Sprachtest?",
    comingSoon: "Demnächst verfügbar...",

    // Privacy Page (placeholders)
    privacyTitle: "Datenschutzrichtlinie",
    privacyIntro:
      "Bei Lingua Talk schätzen wir Ihre Privatsphäre und verpflichten uns, Ihre persönlichen Daten zu schützen. Diese Richtlinie erklärt, wie wir Ihre Daten sammeln, verwenden und schützen, wenn Sie unsere Sprachlern- und Social-Plattform nutzen.",
    informationWeCollect: "Welche Informationen wir sammeln",
    accountInformationTitle: "Kontoinformationen:",
    infoCollectEmailUsername: "E-Mail-Adresse und Benutzername",
    infoCollectProfileInfo: "Profilinformationen (Name, Alter, Standort)",
    infoCollectLanguagePrefs: "Sprachpräferenzen und Lernziele",
    infoCollectSettingsPrefs: "Kontoeinstellungen und Präferenzen",
    usageDataTitle: "Nutzungsdaten:",
    usageDataConversationsAI: "Unterhaltungen mit KI-Charakteren",
    usageDataMessagesPartners: "Nachrichten mit Sprachpartnern",
    usageDataProgressStats: "Lernfortschritt und Statistiken",
    usageDataAppPatterns: "App-Nutzungsmuster und Präferenzen",
    howWeUseTitle: "Wie wir Ihre Informationen nutzen",
    provideServicesTitle: "Unsere Dienste bereitstellen",
    provideServicesDesc:
      "Aktivierung von Sprachlern-Funktionen und Partner-Matching",
    improveExperienceTitle: "Benutzererfahrung verbessern",
    improveExperienceDesc: "Personalisierung von Inhalten und KI-Interaktionen",
    safetyModerationTitle: "Sicherheit und Moderation",
    safetyModerationDesc:
      "Überwachung von Unterhaltungen auf unangemessene Inhalte",
    communicationTitle: "Kommunikation",
    communicationDesc: "Versand wichtiger Updates und Benachrichtigungen",
    analyticsTitle: "Analytik",
    analyticsDesc: "Verstehen, wie Nutzer mit unserer Plattform interagieren",
    dataProtectionTitle: "Datenschutz",
    encryptionTitle: "Verschlüsselung",
    encryptionDesc:
      "Alle Daten werden während der Übertragung und Speicherung verschlüsselt",
    accessControlsTitle: "Zugriffskontrollen",
    accessControlsDesc:
      "Eingeschränkter Mitarbeiterzugang nach dem Need-to-know-Prinzip",
    regularAuditsTitle: "Regelmäßige Prüfungen",
    regularAuditsDesc: "Sicherheitsüberprüfungen und Schwachstellenanalysen",
    secureInfrastructureTitle: "Sichere Infrastruktur",
    secureInfrastructureDesc: "Branchenübliche Cloud-Sicherheitsmaßnahmen",
    dataMinimizationTitle: "Datenminimierung",
    dataMinimizationDesc: "Wir sammeln nur notwendige Informationen",
    dataSharingTitle: "Datenteilung und Dritte",
    noSellData: "Wir verkaufen Ihre personenbezogenen Daten NICHT an Dritte.",
    serviceProvidersTitle: "Dienstleister",
    serviceProvidersDesc:
      "Cloud-Hosting, Analytik und Sicherheitsdienste, die uns beim Betrieb der Plattform unterstützen",
    legalRequirementsTitle: "Gesetzliche Anforderungen",
    legalRequirementsDesc:
      "Wenn dies gesetzlich vorgeschrieben ist, durch Gerichtsbeschluss oder zum Schutz der Nutzersicherheit",
    withConsentTitle: "Mit Ihrer Zustimmung",
    withConsentDesc:
      "Nur wenn Sie ausdrücklich zustimmen, Informationen mit bestimmten Partnern zu teilen",
    yourRightsTitle: "Ihre Datenschutzrechte",
    rightAccessTitle: "Zugriff",
    rightAccessDesc: "Einsicht in Ihre persönlichen Daten",
    rightCorrectionTitle: "Berichtigung",
    rightCorrectionDesc: "Aktualisierung falscher Informationen",
    rightDeletionTitle: "Löschung",
    rightDeletionDesc: "Löschen Ihres Kontos und Ihrer Daten",
    rightPortabilityTitle: "Datenübertragbarkeit",
    rightPortabilityDesc: "Export Ihrer Daten",
    contactUsTitle: "Kontaktieren Sie uns",
    contactUsDesc:
      "Für datenschutzbezogene Fragen oder zur Ausübung Ihrer Rechte:",
    policyUpdatesTitle: "Richtlinien-Updates",
    policyUpdatesDesc:
      "Wir können diese Richtlinie von Zeit zu Zeit aktualisieren. Über wesentliche Änderungen informieren wir Sie per E-Mail oder In-App-Benachrichtigung.",
    lastUpdated: "Zuletzt aktualisiert: Januar 2024",

    // Delete Account Page (placeholders)
    deleteTitle: "Ihr Konto löschen",
    deleteIntro:
      "Es tut uns leid, dass Sie gehen! Wenn Sie Ihr Lingua Talk-Konto löschen möchten, folgen Sie bitte den untenstehenden Anweisungen. Diese Aktion ist dauerhaft und kann nicht rückgängig gemacht werden.",
    deleteImportantTitle:
      "⚠️ WICHTIG: Das Löschen eines Kontos ist nur über die mobile App möglich",
    deleteImportantDesc:
      "Aus Sicherheitsgründen ist das Löschen von Konten nur in der Lingua Talk Mobile-App möglich. Auf dieser Website können Sie Ihr Konto nicht löschen.",
    howToDeleteTitle: "So löschen Sie Ihr Konto",
    stepOpenAppTitle: "Öffnen Sie die Lingua Talk Mobile-App",
    stepOpenAppDesc: "Starten Sie die App auf Ihrem Smartphone oder Tablet",
    stepGoSettingsTitle: "Gehen Sie zu Einstellungen",
    stepGoSettingsDesc:
      "Navigieren Sie zu Ihren Profil- oder Kontoeinstellungen",
    stepFindAccountTitle: "Kontoverwaltung finden",
    stepFindAccountDesc:
      'Suchen Sie nach "Kontoeinstellungen" oder "Datenschutz & Sicherheit"',
    stepSelectDeleteTitle: '"Konto löschen" auswählen',
    stepSelectDeleteDesc:
      "Wählen Sie die Option zum Löschen des Kontos und folgen Sie den Bestätigungsschritten",
    whatDeletedTitle: "Was dauerhaft gelöscht wird",
    personalDataTitle: "Persönliche Daten",
    personalProfileInfo: "Ihre Profilinformationen und Einstellungen",
    personalEmailCredentials: "E-Mail-Adresse und Zugangsdaten",
    personalLangPrefsProgress: "Sprachpräferenzen und Lernfortschritt",
    personalStatsAchievements: "Persönliche Statistiken und Erfolge",
    conversationsContentTitle: "Unterhaltungen & Inhalte",
    conversationsWithPartners: "Alle Unterhaltungen mit Sprachpartnern",
    chatHistoryAI: "Chatverlauf mit KI-Charakteren",
    sharedMediaVoice: "Geteilte Medien und Sprachnachrichten",
    learningSessionRecordings: "Aufzeichnungen von Lernsitzungen",
    socialConnectionsTitle: "Soziale Verbindungen",
    partnerConnections: "Ihre Sprachpartner-Verbindungen",
    friendListsBlocked: "Freundeslisten und blockierte Nutzer",
    groupMemberships: "Gruppenmitgliedschaften und Communities",
    favoritesSavedContent: "Favoriten und gespeicherte Inhalte",
    aiCharacterDataTitle: "KI-Charakterdaten",
    customAIConfigs: "Benutzerdefinierte KI-Charaktereinstellungen",
    aiConversationHistory: "Unterhaltungsverlauf mit KI-Tutoren",
    personalizedRecommendations: "Personalisierte Lernempfehlungen",
    aiGeneratedContent: "Von KI erstellte Inhalte und Antworten",
    beforeDeleteTitle: "Bevor Sie Ihr Konto löschen",
    exportYourDataTitle: "Ihre Daten exportieren",
    exportYourDataDesc:
      "Laden Sie Ihren Chatverlauf und Lernfortschritt vor der Löschung in den App-Einstellungen herunter",
    considerBreakTitle: "Überlegen Sie, eine Pause einzulegen",
    considerBreakDesc:
      "Sie können Ihr Konto vorübergehend deaktivieren, anstatt es dauerhaft zu löschen",
    contactSupportTitle: "Support kontaktieren",
    contactSupportDesc:
      "Wenn Sie Probleme haben, kann unser Support-Team möglicherweise helfen, diese zu lösen",
    needHelpTitle: "Brauchen Sie Hilfe?",
    needHelpIntro:
      "Wenn Sie Schwierigkeiten beim Löschen Ihres Kontos haben oder Fragen zum Prozess:",
    supportResponseTime:
      "Unser Support-Team antwortet in der Regel innerhalb von 24 Stunden und kann Ihnen helfen mit:",
    helpDeletionTroubleshooting: "Fehlerbehebung bei Kontolöschung",
    helpDataExport: "Datenexport-Anfragen",
    helpAlternativeSolutions: "Alternative Lösungen für Kontoprobleme",
    helpPrivacySecurity: "Fragen zum Datenschutz und zur Sicherheit",
    securityNoteTitle: "Sicherheitshinweis",
    securityNoteDesc:
      "Anfragen zur Kontolöschung werden sicher verarbeitet und in der Regel innerhalb von 30 Tagen abgeschlossen, um eine vollständige Entfernung aus allen Systemen und Backups sicherzustellen.",

    // Safety Page (placeholders)
    safetyTitle: "Lingua Talk – Kinderschutz- und Sicherheitsrichtlinie",
    safetyIntro:
      "Bei Lingua Talk haben die Sicherheit und das Wohlbefinden unserer Nutzer, insbesondere von Minderjährigen, oberste Priorität. Als Sprachlern- und Social-App, die Nutzer mit KI-Charakteren und realen Sprachpartnern verbindet, verpflichten wir uns, eine sichere und respektvolle Umgebung für alle aufrechtzuerhalten.",
    zeroToleranceTitle: "Nulltoleranz für CSAE",
    zeroToleranceIntro:
      "Wir erlauben keine Inhalte oder Verhaltensweisen im Zusammenhang mit:",
    zeroToleranceItem1: "Sexueller Missbrauch oder Ausbeutung von Kindern",
    zeroToleranceItem2:
      "Unangemessenes oder schädliches Verhalten gegenüber Minderjährigen",
    zeroToleranceItem3:
      "Jegliche Form von Anbahnung, Nacktheit mit Minderjährigen oder illegale Kommunikation",
    zeroToleranceAction:
      "Alle derartigen Inhalte werden sofort entfernt und den zuständigen Behörden gemeldet.",
    moderationTitle: "Moderation und Überwachung",
    moderationItem1:
      "Nutzer können Unterhaltungen und Profile direkt in der App melden.",
    moderationItem2:
      "Markierte Inhalte werden von unserem Moderationsteam überprüft.",
    moderationItem3:
      "Wir ergreifen schnelle Maßnahmen bei Verstößen (Verwarnungen, Sperrungen, Bans oder rechtliche Meldungen).",
    userAgePolicyTitle: "Nutzer-Altersrichtlinie",
    userAgePolicyDesc:
      "Lingua Talk ist für Nutzer ab 13 Jahren vorgesehen. Konten, die gegen diese Richtlinie verstoßen, können gesperrt oder entfernt werden.",
    reportAbuseTitle: "Missbrauch melden",
    reportAbuseIntro: "Nutzer können melden:",
    reportAbuseItem1: "Nachrichten oder unangemessene Inhalte in Chats",
    reportAbuseItem2: "Benutzerprofile oder verdächtiges Verhalten",
    reportAbuseUseButton:
      'Verwenden Sie die Schaltfläche "Melden" in Chats oder Profilen. Meldungen werden von unserem Moderationsteam geprüft.',
    complianceTitle: "Einhaltung der Kinderschutzgesetze",
    compliesWith: "Lingua Talk erfüllt:",
    complianceItem1: "Die Kinderschutzstandards von Google Play",
    complianceItem2: "Anwendbare Gesetze zu CSAE in allen Regionen",
    complianceItem3:
      "Bewährte Branchenpraktiken für Plattform-Sicherheit und Meldungen",

    contactTitle: "Kontakt",
    safetyContactIntro:
      "Für Fragen zum Kinderschutz oder um ein ernsthaftes Problem zu melden, kontaktieren Sie bitte unser Sicherheitsteam unter:",

    // Home V2
    v2MetaTitle: "LinguaTalk — Learn languages by talking to AI",
    v2MetaDescription: "LinguaTalk is a language learning app. Chat with AI tutors in Spanish, French, Japanese and 9 more languages. Free on iOS and Android.",
    v2StoreSubApple: "Download on the",
    v2StoreNameApple: "App Store",
    v2StoreSubGoogle: "Get it on",
    v2StoreNameGoogle: "Google Play",
    v2BazaarSub: "Download from",
    v2BazaarName: "Cafe Bazaar",
    v2MarqueeEnglish: "English",
    v2MarqueeSpanish: "Español",
    v2MarqueeFrench: "Français",
    v2MarqueeGerman: "Deutsch",
    v2MarqueeItalian: "Italiano",
    v2MarqueeTurkish: "Türkçe",
    v2WhatDesc: "Most apps throw the same content at everyone. LinguaTalk starts by understanding your level, your goals, and the language you want to learn — then builds a personalized roadmap. From there, you practice through real conversations with AI tutors and native speakers, both matched to exactly where you are right now.",
    v2Feature1Title: "AI Tutors",
    v2Feature1Desc: "Talk to intelligent AI characters that respond, correct, and guide you like a real teacher.",
    v2Feature2Title: "Real Conversations",
    v2Feature2Desc: "No flashcards. No multiple choice. Just you and your tutor — chatting in the language you want to learn.",
    v2Feature3Title: "Placement Test",
    v2Feature3Desc: "Start at exactly the right level. A short test puts you where you belong before your first lesson.",
    v2Feature4Title: "12+ Languages",
    v2Feature4Desc: "English, Spanish, French, German, Italian, Turkish and more — all in one app.",
    v2Step1Title: "Take the placement test",
    v2Step1Desc: "A 5-minute adaptive quiz finds your exact level.",
    v2Step2Title: "Choose your AI tutor",
    v2Step2Desc: "Pick a character that fits your style and target language.",
    v2Step3Title: "Start chatting every day",
    v2Step3Desc: "Short daily chats build real speaking confidence over time.",
    v2RingSub: "Your level",
    v2StatLessons: "Lessons done",
    v2StatStreak: "Day streak 🔥",
    v2StatWords: "Words learned",
    v2PopupDesc: "Take a free 5-minute placement test and get a personalized learning roadmap.",
    v2ChipSpanish: "🇪🇸 Spanish lesson",
    v2ChipAiTyping: "🤖 AI is typing…",
    v2ChipStreak: "🔥 7-day streak",
    v2ChatToday: "Today",
    v2ChatInputPlaceholder: "Type your reply…",
    v2TutorSub: "AI Tutor · Spanish",
    v2HeroLine1: "KI entwirft deine",
    v2HeroLine2: "persönliche Route zur Sprachgewandtheit",
    v2Pill: "📱 Sprachlern-App — Kostenlos für iOS & Android",
    v2Subtext: "LinguaTalk erstellt einen Lernpfad für dein Niveau — dann übst du täglich mit KI-Tutoren und Muttersprachlern.",
    v2WhatTag: "Was ist LinguaTalk?",
    v2WhatTitle1: "Nicht nur Lektionen —",
    v2WhatTitle2: "ein Weg, der um dich gebaut ist",
    v2WhyLabel: "Warum es funktioniert",
    v2HowLabel: "So funktioniert's",
    v2HowTitle1: "Drei Schritte zum Start",
    v2HowTitle2: "selbstbewusst sprechen",
    v2CtaTitle: "Hol dir heute deine kostenlose Route",
    v2CtaSub: "5 Min. Einrichtung · Für immer kostenlos · iOS & Android",
    v2PopupTitle: "Finde dein Niveau",
    v2PopupBtn: "Test starten — Kostenlos",
    v2PopupFootnote: "Keine Registrierung nötig",
  },
  fr: {
    appTitle: "LinguaTalk",
    appTagline:
      "Trouvez le partenaire linguistique idéal. Pratiquez n'importe quelle langue à votre rythme.",
    appDescription:
      "LinguaTalk connecte les passionnés de langues avec des partenaires de pratique du monde entier. Que vous souhaitiez apprendre l'arabe, le chinois, l'anglais, le français, l'allemand, l'italien, le japonais, le coréen, le persan, le russe, le turc ou toute autre langue, nous avons ce qu'il vous faut. Discutez avec des tuteurs IA pour une pratique guidée ou connectez-vous avec de vraies personnes pour améliorer vos compétences en expression orale, en écriture et en lecture dans un environnement social et amusant.",
    downloadIOS: "Télécharger pour iOS",
    downloadAndroid: "Télécharger pour Android",
    directDownload: "Télécharger le fichier .apk",
    learnMore: "En savoir plus sur LinguaTalk",
    howItWorks: "Comment fonctionne LinguaTalk",
    step1Title: "Créez votre profil",
    step1Description:
      "Définissez vos préférences linguistiques, votre niveau de compétence et vos centres d'intérêt pour trouver votre correspondance parfaite.",
    step2Title: "Trouvez des partenaires",
    step2Description:
      "Explorez les personnages IA ou découvrez de vrais utilisateurs qui partagent vos objectifs d'apprentissage linguistique.",
    step3Title: "Pratiquez et progressez",
    step3Description:
      "Chattez, appelez ou envoyez des messages pour pratiquer régulièrement vos compétences linguistiques cibles.",
    whyChoose: "Pourquoi choisir LinguaTalk ?",
    feature1Title: "Partenaires linguistiques IA",
    feature1Description:
      "Pratiquez à tout moment avec des personnages IA intelligents conçus pour vous aider à améliorer vos compétences linguistiques.",
    feature2Title: "Partenaires réels",
    feature2Description:
      "Connectez-vous avec des locuteurs natifs et d'autres apprenants pour un échange linguistique authentique.",
    feature3Title: "Profils détaillés",
    feature3Description:
      "Créez et personnalisez votre profil pour mettre en valeur vos compétences linguistiques et vos centres d'intérêt.",
    feature4Title: "Correspondance intelligente",
    feature4Description:
      "Trouvez le partenaire linguistique idéal en fonction de votre niveau de compétence, de vos centres d'intérêt et de vos objectifs d'apprentissage.",
    aiScreenTitle: "Discutez avec des personnages IA",
    aiScreenDescription:
      "Améliorez vos compétences linguistiques en discutant avec des personnages IA conçus pour vous aider à pratiquer dans un environnement sûr et sans jugement. Parfait pour les débutants ou lorsque des partenaires humains ne sont pas disponibles.",
    exploreScreenTitle: "Explorez des partenaires réels",
    exploreScreenDescription:
      "Recherchez et connectez-vous avec de vrais utilisateurs du monde entier. Filtrez par langue, niveau de compétence et centres d'intérêt pour trouver votre partenaire d'échange linguistique idéal.",
    footerText: "© 2025 LinguaTalk. Tous droits réservés.",
    language: "Langue",
    // Top Menu
    menuHome: "Accueil",
    menuPlacementTest: "Test de placement",
    menuPrivacyPolicy: "Politique de confidentialité",
    menuSafetySecurity: "Sécurité et protection",
    menuDeleteAccount: "Supprimer le compte",
    // Popup
    popupTitle: "Testez votre niveau de langue!",
    popupDescription:
      "Découvrez votre maîtrise en seulement 8 minutes. Obtenez des résultats instantanés du niveau A1 à C2.",
    popupButton: "Commencer le test",
    popupFooter: "ou téléchargez l'application pour sauvegarder vos résultats",
    userPreviewTitle: "Vous êtes invité !",
    userPreviewMessage:
      "Quelqu'un veut pratiquer les langues avec vous sur Lingua Talk. Téléchargez l'app pour vous connecter — c'est gratuit et amusant !",
    userPreviewTagline: "Pratiquez les langues ensemble",
    userPreviewOpenInApp: "Ouvrir dans l'app",
    userPreviewHint: "Gratuit · Pas de compte requis pour essayer",
    // Placement Test
    testStarting: "Démarrage du test...",
    getReady: "Préparez-vous!",
    testPreview: "Aperçu du test",
    testYourLanguageProficiency: "Testez votre compétence linguistique",
    guest: "Invité",
    totalQuestions: "Total des questions",
    totalTime: "Temps total",
    testParts: "Sections du test",
    question: "question",
    questions: "questions",
    startTest: "Commencer le test",
    loadingTest: "Chargement du test...",
    preparingTest:
      "Veuillez patienter pendant que nous préparons votre test de placement.",
    unableToLoadTest:
      "Impossible de charger le test. Veuillez réessayer depuis l'application.",
    greatJob: "Excellent travail!",
    yourEstimatedLevel: "Votre niveau estimé est",
    score: "Score:",
    testEvaluated:
      "Ce test de placement a évalué votre compétence en {language} à travers plusieurs niveaux de A1 à C2.",
    greatWorkCompleted:
      "Excellent travail! Vous avez terminé le test de placement en {language}. Connectez-vous pour enregistrer vos progrès et débloquer des fonctionnalités d'apprentissage personnalisées.",
    tryAgain: "Réessayer",
    backToHome: "Retour à l'accueil",
    test: "Test",
    level: "Niveau:",
    type: "Type:",
    questionOf: "Question {current} sur {total} ({answered} répondu)",
    playAudio: "Lire l'audio",
    previous: "Précédent",
    next: "Suivant",
    submitTest: "Soumettre le test",
    testResultSaved: "Votre résultat de test a été enregistré avec succès!",
    testResultFailed:
      "Échec de l'enregistrement du résultat du test. Veuillez réessayer.",
    saveResultsTrackProgress:
      "Voulez-vous enregistrer vos résultats et suivre vos progrès?",
    improveWithApp:
      "Vous pouvez également améliorer vos compétences linguistiques avec notre application IA et de vrais partenaires.",
    grammar: "Grammaire",
    vocabulary: "Vocabulaire",
    listening: "Écoute",
    downloadForIOS: "Télécharger pour iOS",
    downloadForAndroid: "Télécharger pour Android",
    placementTest: "Test de placement",
    min: "min",
    testForLanguage: "Test pour la langue:",
    testNotImplemented: "Cherchez-vous d'autres tests de langue?",
    availableLanguages:
      "Pour l'instant, vous pouvez passer le test dans ces langues:",
    viewAllLanguages: "Cherchez-vous un autre test de langue?",
    comingSoon: "Bientôt disponible...",
    // Privacy Page
    privacyTitle: "Politique de confidentialité",
    privacyIntro:
      "Chez Lingua Talk, nous valorisons votre vie privée et nous nous engageons à protéger vos informations personnelles. Cette politique explique comment nous collectons, utilisons et protégeons vos données lorsque vous utilisez notre plateforme d'apprentissage des langues et sociale.",
    informationWeCollect: "Informations que nous collectons",
    accountInformationTitle: "Informations de compte :",
    infoCollectEmailUsername: "Adresse e-mail et nom d'utilisateur",
    infoCollectProfileInfo: "Informations de profil (nom, âge, localisation)",
    infoCollectLanguagePrefs:
      "Préférences linguistiques et objectifs d'apprentissage",
    infoCollectSettingsPrefs: "Paramètres de compte et préférences",
    usageDataTitle: "Données d'utilisation :",
    usageDataConversationsAI: "Conversations avec les personnages IA",
    usageDataMessagesPartners: "Messages avec les partenaires linguistiques",
    usageDataProgressStats: "Progrès d'apprentissage et statistiques",
    usageDataAppPatterns:
      "Modèles d'utilisation de l'application et préférences",
    howWeUseTitle: "Comment nous utilisons vos informations",
    provideServicesTitle: "Fournir nos services",
    provideServicesDesc:
      "Activer les fonctionnalités d'apprentissage des langues et de jumelage de partenaires",
    improveExperienceTitle: "Améliorer l'expérience utilisateur",
    improveExperienceDesc:
      "Personnaliser le contenu et les interactions avec les personnages IA",
    safetyModerationTitle: "Sécurité et modération",
    safetyModerationDesc:
      "Surveiller les conversations pour détecter le contenu inapproprié",
    communicationTitle: "Communication",
    communicationDesc:
      "Envoyer des mises à jour importantes et des notifications",
    analyticsTitle: "Analyses",
    analyticsDesc:
      "Comprendre comment les utilisateurs interagissent avec notre plateforme",
    dataProtectionTitle: "Protection des données",
    encryptionTitle: "Chiffrement",
    encryptionDesc: "Toutes les données sont chiffrées en transit et au repos",
    accessControlsTitle: "Contrôles d'accès",
    accessControlsDesc:
      "Accès limité des employés sur la base du besoin de savoir",
    regularAuditsTitle: "Audits réguliers",
    regularAuditsDesc: "Examens de sécurité et évaluations de vulnérabilité",
    secureInfrastructureTitle: "Infrastructure sécurisée",
    secureInfrastructureDesc:
      "Mesures de sécurité cloud standard de l'industrie",
    dataMinimizationTitle: "Minimisation des données",
    dataMinimizationDesc: "Nous ne collectons que les informations nécessaires",
    dataSharingTitle: "Partage de données et tiers",
    noSellData: "Nous ne vendons PAS vos données personnelles à des tiers.",
    serviceProvidersTitle: "Fournisseurs de services",
    serviceProvidersDesc:
      "Hébergement cloud, analyses et services de sécurité qui nous aident à exploiter la plateforme",
    legalRequirementsTitle: "Exigences légales",
    legalRequirementsDesc:
      "Lorsque requis par la loi, ordonnance du tribunal ou pour protéger la sécurité des utilisateurs",
    withConsentTitle: "Avec votre consentement",
    withConsentDesc:
      "Seulement lorsque vous acceptez explicitement de partager des informations avec des partenaires spécifiques",
    yourRightsTitle: "Vos droits de confidentialité",
    rightAccessTitle: "Accès",
    rightAccessDesc: "Voir vos données personnelles",
    rightCorrectionTitle: "Correction",
    rightCorrectionDesc: "Mettre à jour les informations incorrectes",
    rightDeletionTitle: "Suppression",
    rightDeletionDesc: "Supprimer votre compte et vos données",
    rightPortabilityTitle: "Portabilité",
    rightPortabilityDesc: "Exporter vos données",
    contactUsTitle: "Nous contacter",
    contactUsDesc:
      "Pour les questions liées à la confidentialité ou pour exercer vos droits :",
    policyUpdatesTitle: "Mises à jour de la politique",
    policyUpdatesDesc:
      "Nous pouvons mettre à jour cette politique de temps en temps. Nous vous notifierons des changements importants par e-mail ou notification dans l'application.",
    lastUpdated: "Dernière mise à jour : janvier 2024",
    // Delete Account Page
    deleteTitle: "Supprimer votre compte",
    deleteIntro:
      "Nous sommes désolés de vous voir partir ! Si vous devez supprimer votre compte Lingua Talk, veuillez suivre les instructions ci-dessous. Cette action est permanente et ne peut pas être annulée.",
    deleteImportantTitle:
      "⚠️ IMPORTANT : La suppression de compte ne peut se faire que via l'application mobile",
    deleteImportantDesc:
      "Pour des raisons de sécurité, la suppression de compte n'est disponible que dans l'application mobile Lingua Talk. Vous ne pouvez pas supprimer votre compte depuis ce site web.",
    howToDeleteTitle: "Comment supprimer votre compte",
    stepOpenAppTitle: "Ouvrir l'application mobile Lingua Talk",
    stepOpenAppDesc: "Lancez l'application sur votre téléphone ou tablette",
    stepGoSettingsTitle: "Aller aux paramètres",
    stepGoSettingsDesc:
      "Naviguez vers vos paramètres de profil ou paramètres de compte",
    stepFindAccountTitle: "Trouver la gestion de compte",
    stepFindAccountDesc:
      'Recherchez la section "Paramètres de compte" ou "Confidentialité et sécurité"',
    stepSelectDeleteTitle: 'Sélectionner "Supprimer le compte"',
    stepSelectDeleteDesc:
      "Choisissez l'option de suppression de compte et suivez les étapes de confirmation",
    whatDeletedTitle: "Ce qui sera supprimé définitivement",
    personalDataTitle: "Données personnelles",
    personalProfileInfo: "Vos informations de profil et paramètres",
    personalEmailCredentials: "Adresse e-mail et identifiants de compte",
    personalLangPrefsProgress:
      "Préférences linguistiques et progrès d'apprentissage",
    personalStatsAchievements: "Statistiques personnelles et réalisations",
    conversationsContentTitle: "Conversations et contenu",
    conversationsWithPartners:
      "Toutes les conversations avec les partenaires linguistiques",
    chatHistoryAI: "Historique des discussions avec les personnages IA",
    sharedMediaVoice: "Médias partagés et messages vocaux",
    learningSessionRecordings: "Enregistrements des sessions d'apprentissage",
    socialConnectionsTitle: "Connexions sociales",
    partnerConnections: "Vos connexions de partenaires linguistiques",
    friendListsBlocked: "Listes d'amis et utilisateurs bloqués",
    groupMemberships: "Adhésions aux groupes et communautés",
    favoritesSavedContent: "Favoris et contenu sauvegardé",
    aiCharacterDataTitle: "Données des personnages IA",
    customAIConfigs: "Configurations personnalisées des personnages IA",
    aiConversationHistory: "Historique des conversations avec les tuteurs IA",
    personalizedRecommendations:
      "Recommandations d'apprentissage personnalisées",
    aiGeneratedContent: "Contenu et réponses générés par l'IA",
    beforeDeleteTitle: "Avant de supprimer votre compte",
    exportYourDataTitle: "Exporter vos données",
    exportYourDataDesc:
      "Téléchargez votre historique de conversations et vos progrès d'apprentissage depuis les paramètres de l'application avant la suppression",
    considerBreakTitle: "Envisager une pause",
    considerBreakDesc:
      "Vous pouvez temporairement désactiver votre compte au lieu d'une suppression permanente",
    contactSupportTitle: "Contacter le support",
    contactSupportDesc:
      "Si vous rencontrez des problèmes, notre équipe de support pourrait être en mesure de vous aider à les résoudre",
    needHelpTitle: "Besoin d'aide ?",
    needHelpIntro:
      "Si vous avez des difficultés à supprimer votre compte ou des questions sur le processus :",
    supportResponseTime:
      "Notre équipe de support répond généralement dans les 24 heures et peut vous aider avec :",
    helpDeletionTroubleshooting: "Dépannage de suppression de compte",
    helpDataExport: "Demandes d'exportation de données",
    helpAlternativeSolutions: "Solutions alternatives aux problèmes de compte",
    helpPrivacySecurity: "Questions de confidentialité et sécurité",
    securityNoteTitle: "Note de sécurité",
    securityNoteDesc:
      "Les demandes de suppression de compte sont traitées de manière sécurisée et généralement complétées dans les 30 jours pour assurer la suppression complète des données de tous les systèmes et sauvegardes.",
    // Safety Page
    safetyTitle:
      "Lingua Talk – Politique de sécurité et protection des enfants",
    safetyIntro:
      "Chez Lingua Talk, la sécurité et le bien-être de nos utilisateurs, en particulier les mineurs, est une priorité absolue. En tant qu'application d'apprentissage des langues et sociale qui connecte les utilisateurs avec des personnages IA et de vrais partenaires linguistiques, nous nous engageons à maintenir un environnement sécurisé et respectueux pour tous.",
    zeroToleranceTitle: "Tolérance zéro pour l'AEESE",
    zeroToleranceIntro:
      "Nous ne permettons aucun contenu ou comportement lié à :",
    zeroToleranceItem1: "Abus sexuel ou exploitation d'enfants",
    zeroToleranceItem2:
      "Comportement inapproprié ou nuisible envers les mineurs",
    zeroToleranceItem3:
      "Toute forme de manipulation, nudité impliquant des mineurs, ou communication illégale",
    zeroToleranceAction:
      "Tout ce contenu sera immédiatement supprimé et signalé aux autorités compétentes.",
    moderationTitle: "Modération et surveillance",
    moderationItem1:
      "Les utilisateurs peuvent signaler les conversations et profils directement dans l'application.",
    moderationItem2:
      "Le contenu signalé est examiné par notre équipe de modération.",
    moderationItem3:
      "Nous prenons des mesures rapides contre les violations (avertissements, suspensions, bannissements, ou rapports légaux).",
    userAgePolicyTitle: "Politique d'âge des utilisateurs",
    userAgePolicyDesc:
      "Lingua Talk est destiné aux utilisateurs âgés de 13 ans et plus. Les comptes trouvés en violation de cette politique peuvent être suspendus ou supprimés.",
    reportAbuseTitle: "Comment signaler un abus",
    reportAbuseIntro: "Les utilisateurs peuvent signaler :",
    reportAbuseItem1: "Messages ou contenu inapproprié dans les discussions",
    reportAbuseItem2: "Profils d'utilisateurs ou comportements suspects",
    reportAbuseUseButton:
      'Utilisez le bouton "Signaler" dans les discussions ou profils d\'utilisateurs. Les signalements sont examinés par notre équipe de modération.',
    complianceTitle: "Conformité aux lois de sécurité des enfants",
    compliesWith: "Lingua Talk se conforme à :",
    complianceItem1: "Les normes de sécurité des enfants de Google Play",
    complianceItem2:
      "Les lois applicables concernant l'AEESE dans toutes les régions d'exploitation",
    complianceItem3:
      "Les meilleures pratiques de l'industrie pour la sécurité de la plateforme et les signalements",

    contactTitle: "Contact",
    safetyContactIntro:
      "Pour les questions liées à la sécurité des enfants ou pour signaler une préoccupation sérieuse, contactez notre équipe de sécurité à :",

    // Home V2
    v2MetaTitle: "LinguaTalk — Learn languages by talking to AI",
    v2MetaDescription: "LinguaTalk is a language learning app. Chat with AI tutors in Spanish, French, Japanese and 9 more languages. Free on iOS and Android.",
    v2StoreSubApple: "Download on the",
    v2StoreNameApple: "App Store",
    v2StoreSubGoogle: "Get it on",
    v2StoreNameGoogle: "Google Play",
    v2BazaarSub: "Download from",
    v2BazaarName: "Cafe Bazaar",
    v2MarqueeEnglish: "English",
    v2MarqueeSpanish: "Español",
    v2MarqueeFrench: "Français",
    v2MarqueeGerman: "Deutsch",
    v2MarqueeItalian: "Italiano",
    v2MarqueeTurkish: "Türkçe",
    v2WhatDesc: "Most apps throw the same content at everyone. LinguaTalk starts by understanding your level, your goals, and the language you want to learn — then builds a personalized roadmap. From there, you practice through real conversations with AI tutors and native speakers, both matched to exactly where you are right now.",
    v2Feature1Title: "AI Tutors",
    v2Feature1Desc: "Talk to intelligent AI characters that respond, correct, and guide you like a real teacher.",
    v2Feature2Title: "Real Conversations",
    v2Feature2Desc: "No flashcards. No multiple choice. Just you and your tutor — chatting in the language you want to learn.",
    v2Feature3Title: "Placement Test",
    v2Feature3Desc: "Start at exactly the right level. A short test puts you where you belong before your first lesson.",
    v2Feature4Title: "12+ Languages",
    v2Feature4Desc: "English, Spanish, French, German, Italian, Turkish and more — all in one app.",
    v2Step1Title: "Take the placement test",
    v2Step1Desc: "A 5-minute adaptive quiz finds your exact level.",
    v2Step2Title: "Choose your AI tutor",
    v2Step2Desc: "Pick a character that fits your style and target language.",
    v2Step3Title: "Start chatting every day",
    v2Step3Desc: "Short daily chats build real speaking confidence over time.",
    v2RingSub: "Your level",
    v2StatLessons: "Lessons done",
    v2StatStreak: "Day streak 🔥",
    v2StatWords: "Words learned",
    v2PopupDesc: "Take a free 5-minute placement test and get a personalized learning roadmap.",
    v2ChipSpanish: "🇪🇸 Spanish lesson",
    v2ChipAiTyping: "🤖 AI is typing…",
    v2ChipStreak: "🔥 7-day streak",
    v2ChatToday: "Today",
    v2ChatInputPlaceholder: "Type your reply…",
    v2TutorSub: "AI Tutor · Spanish",
    v2HeroLine1: "L'IA conçoit votre",
    v2HeroLine2: "parcours personnel vers la maîtrise",
    v2Pill: "📱 App d'apprentissage — Gratuite sur iOS & Android",
    v2Subtext: "LinguaTalk crée un parcours adapté à votre niveau — puis vous pratiquez chaque jour avec des tuteurs IA et des locuteurs natifs.",
    v2WhatTag: "Qu'est-ce que LinguaTalk ?",
    v2WhatTitle1: "Pas seulement des leçons —",
    v2WhatTitle2: "un parcours construit pour vous",
    v2WhyLabel: "Pourquoi ça marche",
    v2HowLabel: "Comment ça marche",
    v2HowTitle1: "Trois étapes pour commencer",
    v2HowTitle2: "parler avec confiance",
    v2CtaTitle: "Obtenez votre parcours gratuit",
    v2CtaSub: "5 min de configuration · Gratuit · iOS & Android",
    v2PopupTitle: "Trouvez votre niveau",
    v2PopupBtn: "Commencer le test — Gratuit",
    v2PopupFootnote: "Sans inscription",
  },
  ru: {
    appTitle: "LinguaTalk",
    appTagline:
      "Найдите идеального языкового партнера. Практикуйте любой язык в своем темпе.",
    appDescription:
      "LinguaTalk соединяет любителей языков с партнерами для практики со всего мира. Хотите ли вы выучить арабский, китайский, английский, французский, немецкий, итальянский, японский, корейский, персидский, русский, турецкий или любой другой язык - мы предоставим вам такую возможность. Общайтесь с ИИ-репетиторами для целенаправленной практики или свяжитесь с реальными людьми, чтобы улучшить свои навыки говорения, письма и чтения в увлекательной социальной среде.",
    downloadIOS: "Скачать для iOS",
    downloadAndroid: "Скачать для Android",
    directDownload: "Скачать .apk файл",
    learnMore: "Узнать больше о LinguaTalk",
    howItWorks: "Как работает LinguaTalk",
    step1Title: "Создайте свой профиль",
    step1Description:
      "Укажите языковые предпочтения, уровень владения и интересы, чтобы найти идеальное соответствие.",
    step2Title: "Найдите партнеров",
    step2Description:
      "Изучите ИИ-персонажей или откройте для себя реальных пользователей, которые разделяют ваши цели изучения языка.",
    step3Title: "Практикуйтесь и совершенствуйтесь",
    step3Description:
      "Общайтесь в чате, звоните или отправляйте сообщения, чтобы регулярно практиковать целевые языковые навыки.",
    whyChoose: "Почему стоит выбрать LinguaTalk?",
    feature1Title: "ИИ-языковые партнеры",
    feature1Description:
      "Практикуйтесь в любое время с интеллектуальными ИИ-персонажами, созданными для улучшения ваших языковых навыков.",
    feature2Title: "Реальные партнеры",
    feature2Description:
      "Свяжитесь с носителями языка и другими учащимися для подлинного языкового обмена.",
    feature3Title: "Подробные профили",
    feature3Description:
      "Создайте и настройте свой профиль, чтобы продемонстрировать свои языковые навыки и интересы.",
    feature4Title: "Умный подбор",
    feature4Description:
      "Найдите идеального языкового партнера на основе своего уровня владения, интересов и целей обучения.",
    aiScreenTitle: "Общайтесь с ИИ-персонажами",
    aiScreenDescription:
      "Улучшайте свои языковые навыки, общаясь с ИИ-персонажами, созданными для практики в безопасной среде без осуждения. Идеально для начинающих или когда человеческие партнеры недоступны.",
    exploreScreenTitle: "Исследуйте реальных партнеров",
    exploreScreenDescription:
      "Ищите и подключайтесь к реальным пользователям со всего мира. Фильтруйте по языку, уровню владения и интересам, чтобы найти идеального партнера для языкового обмена.",
    footerText: "© 2025 LinguaTalk. Все права защищены.",
    language: "Язык",
    // Top Menu
    menuHome: "Главная",
    menuPlacementTest: "Тест на определение уровня",
    menuPrivacyPolicy: "Политика конфиденциальности",
    menuSafetySecurity: "Безопасность",
    menuDeleteAccount: "Удалить аккаунт",
    // Popup
    popupTitle: "Проверьте свой уровень языка!",
    popupDescription:
      "Узнайте свой уровень всего за 8 минут. Получите мгновенные результаты от A1 до C2.",
    popupButton: "Начать тест",
    popupFooter: "или скачайте приложение, чтобы сохранить результаты",
    userPreviewTitle: "Вас пригласили!",
    userPreviewMessage:
      "Кто-то хочет практиковать языки с вами в Lingua Talk. Скачайте приложение и общайтесь — бесплатно и интересно!",
    userPreviewTagline: "Практикуйте языки вместе",
    userPreviewOpenInApp: "Открыть в приложении",
    userPreviewHint: "Бесплатно · Регистрация не обязательна для пробы",
    // Placement Test
    testStarting: "Тест запускается...",
    getReady: "Приготовьтесь!",
    testPreview: "Предпросмотр теста",
    testYourLanguageProficiency: "Проверьте свой уровень языка",
    guest: "Гость",
    totalQuestions: "Всего вопросов",
    totalTime: "Общее время",
    testParts: "Разделы теста",
    question: "вопрос",
    questions: "вопросов",
    startTest: "Начать тест",
    loadingTest: "Загрузка теста...",
    preparingTest:
      "Пожалуйста, подождите, пока мы готовим ваш тест на определение уровня.",
    unableToLoadTest:
      "Не удалось загрузить тест. Пожалуйста, попробуйте снова из приложения.",
    greatJob: "Отличная работа!",
    yourEstimatedLevel: "Ваш предполагаемый уровень",
    score: "Балл:",
    testEvaluated:
      "Этот тест на определение уровня оценил ваше знание {language} на нескольких уровнях от A1 до C2.",
    greatWorkCompleted:
      "Отличная работа! Вы завершили тест на определение уровня по {language}. Войдите, чтобы сохранить свой прогресс и разблокировать персонализированные функции обучения.",
    tryAgain: "Попробовать снова",
    backToHome: "Вернуться на главную",
    test: "Тест",
    level: "Уровень:",
    type: "Тип:",
    questionOf: "Вопрос {current} из {total} ({answered} отвечено)",
    playAudio: "Воспроизвести аудио",
    previous: "Назад",
    next: "Далее",
    submitTest: "Отправить тест",
    testResultSaved: "Ваш результат теста успешно сохранен!",
    testResultFailed:
      "Не удалось сохранить результат теста. Пожалуйста, попробуйте снова.",
    saveResultsTrackProgress:
      "Хотите сохранить свои результаты и отслеживать прогресс?",
    improveWithApp:
      "Вы также можете улучшить свои языковые навыки с помощью нашего приложения ИИ и реальных партнеров.",
    grammar: "Грамматика",
    vocabulary: "Словарный запас",
    listening: "Аудирование",
    downloadForIOS: "Скачать для iOS",
    downloadForAndroid: "Скачать для Android",
    placementTest: "Тест на определение уровня",
    min: "мин",
    testForLanguage: "Тест для языка:",
    testNotImplemented: "Ищете тесты на других языках?",
    availableLanguages:
      "На данный момент вы можете пройти тест на этих языках:",
    viewAllLanguages: "Ищете тест на другом языке?",
    comingSoon: "Скоро...",

    // Privacy Page (placeholders)
    privacyTitle: "Политика конфиденциальности",
    privacyIntro:
      "В Lingua Talk мы ценим вашу конфиденциальность и обязуемся защищать вашу личную информацию. В этой политике объясняется, как мы собираем, используем и защищаем ваши данные при использовании нашей платформы для изучения языков и общения.",
    informationWeCollect: "Информация, которую мы собираем",
    accountInformationTitle: "Информация аккаунта:",
    infoCollectEmailUsername: "Адрес электронной почты и имя пользователя",
    infoCollectProfileInfo: "Информация профиля (имя, возраст, местоположение)",
    infoCollectLanguagePrefs: "Языковые предпочтения и цели обучения",
    infoCollectSettingsPrefs: "Настройки и предпочтения аккаунта",
    usageDataTitle: "Данные об использовании:",
    usageDataConversationsAI: "Разговоры с персонажами ИИ",
    usageDataMessagesPartners: "Сообщения с языковыми партнёрами",
    usageDataProgressStats: "Прогресс в обучении и статистика",
    usageDataAppPatterns: "Шаблоны и предпочтения использования приложения",
    howWeUseTitle: "Как мы используем вашу информацию",
    provideServicesTitle: "Предоставление наших услуг",
    provideServicesDesc: "Включение функций изучения языков и подбор партнёров",
    improveExperienceTitle: "Улучшение пользовательского опыта",
    improveExperienceDesc:
      "Персонализация контента и взаимодействия с персонажами ИИ",
    safetyModerationTitle: "Безопасность и модерация",
    safetyModerationDesc:
      "Мониторинг разговоров на наличие неприемлемого контента",
    communicationTitle: "Коммуникация",
    communicationDesc: "Отправка важных обновлений и уведомлений",
    analyticsTitle: "Аналитика",
    analyticsDesc: "Анализ вовлечённости пользователей на платформе",
    dataProtectionTitle: "Защита данных",
    encryptionTitle: "Шифрование",
    encryptionDesc: "Все данные шифруются при передаче и хранении",
    accessControlsTitle: "Контроль доступа",
    accessControlsDesc:
      "Ограниченный доступ сотрудников по принципу необходимости",
    regularAuditsTitle: "Регулярные аудиты",
    regularAuditsDesc: "Проверки безопасности и анализ уязвимостей",
    secureInfrastructureTitle: "Защищённая инфраструктура",
    secureInfrastructureDesc:
      "Меры облачной безопасности по отраслевым стандартам",
    dataMinimizationTitle: "Минимизация данных",
    dataMinimizationDesc: "Мы собираем только необходимую информацию",
    dataSharingTitle: "Передача данных и третьи стороны",
    noSellData: "Мы НЕ продаём ваши персональные данные третьим лицам.",
    serviceProvidersTitle: "Поставщики услуг",
    serviceProvidersDesc:
      "Облачный хостинг, аналитика и службы безопасности, которые помогают нам работать",
    legalRequirementsTitle: "Юридические требования",
    legalRequirementsDesc:
      "Когда это требуется по закону, решению суда или для защиты безопасности пользователей",
    withConsentTitle: "С вашего согласия",
    withConsentDesc:
      "Только если вы явно согласились делиться информацией с определёнными партнёрами",
    yourRightsTitle: "Ваши права на конфиденциальность",
    rightAccessTitle: "Доступ",
    rightAccessDesc: "Просмотр ваших персональных данных",
    rightCorrectionTitle: "Исправление",
    rightCorrectionDesc: "Обновление неверной информации",
    rightDeletionTitle: "Удаление",
    rightDeletionDesc: "Удаление вашего аккаунта и данных",
    rightPortabilityTitle: "Портативность",
    rightPortabilityDesc: "Экспорт ваших данных",
    contactUsTitle: "Свяжитесь с нами",
    contactUsDesc:
      "По вопросам конфиденциальности или для реализации ваших прав:",
    policyUpdatesTitle: "Обновления политики",
    policyUpdatesDesc:
      "Мы можем время от времени обновлять эту политику. О значительных изменениях мы уведомим вас по электронной почте или внутри приложения.",
    lastUpdated: "Последнее обновление: январь 2024",

    // Delete Account Page (placeholders)
    deleteTitle: "Удалить аккаунт",
    deleteIntro:
      "Нам жаль, что вы решили уйти! Если вы хотите удалить свой аккаунт Lingua Talk, пожалуйста, следуйте инструкциям ниже. Это действие необратимо.",
    deleteImportantTitle:
      "⚠️ ВАЖНО: Удалить аккаунт можно только через мобильное приложение",
    deleteImportantDesc:
      "В целях безопасности удаление аккаунта доступно только в мобильном приложении Lingua Talk. С этого сайта удалить аккаунт невозможно.",
    howToDeleteTitle: "Как удалить аккаунт",
    stepOpenAppTitle: "Откройте мобильное приложение Lingua Talk",
    stepOpenAppDesc: "Запустите приложение на своём телефоне или планшете",
    stepGoSettingsTitle: "Перейдите в настройки",
    stepGoSettingsDesc: "Откройте настройки профиля или аккаунта",
    stepFindAccountTitle: "Найдите управление аккаунтом",
    stepFindAccountDesc:
      'Ищите раздел "Настройки аккаунта" или "Конфиденциальность и безопасность"',
    stepSelectDeleteTitle: 'Выберите "Удалить аккаунт"',
    stepSelectDeleteDesc:
      "Выберите опцию удаления аккаунта и следуйте инструкциям подтверждения",
    whatDeletedTitle: "Что будет удалено навсегда",
    personalDataTitle: "Персональные данные",
    personalProfileInfo: "Информация и настройки вашего профиля",
    personalEmailCredentials: "Адрес электронной почты и данные для входа",
    personalLangPrefsProgress: "Языковые предпочтения и прогресс в обучении",
    personalStatsAchievements: "Персональная статистика и достижения",
    conversationsContentTitle: "Разговоры и контент",
    conversationsWithPartners: "Все ваши разговоры с языковыми партнёрами",
    chatHistoryAI: "История чатов с персонажами ИИ",
    sharedMediaVoice: "Общие медиафайлы и голосовые сообщения",
    learningSessionRecordings: "Записи учебных занятий",
    socialConnectionsTitle: "Социальные связи",
    partnerConnections: "Ваши связи с языковыми партнёрами",
    friendListsBlocked: "Списки друзей и заблокированные пользователи",
    groupMemberships: "Членство в группах и сообществах",
    favoritesSavedContent: "Избранное и сохранённый контент",
    aiCharacterDataTitle: "Данные персонажей ИИ",
    customAIConfigs: "Пользовательские настройки персонажей ИИ",
    aiConversationHistory: "История разговоров с ИИ-наставниками",
    personalizedRecommendations: "Персонализированные рекомендации по обучению",
    aiGeneratedContent: "Контент и ответы, сгенерированные ИИ",
    beforeDeleteTitle: "Перед удалением аккаунта",
    exportYourDataTitle: "Экспорт данных",
    exportYourDataDesc:
      "Загрузите историю чатов и прогресс обучения из настроек приложения перед удалением",
    considerBreakTitle: "Рассмотрите возможность паузы",
    considerBreakDesc:
      "Вы можете временно деактивировать аккаунт вместо окончательного удаления",
    contactSupportTitle: "Связаться с поддержкой",
    contactSupportDesc:
      "Если у вас есть проблемы, наша служба поддержки может помочь их решить",
    needHelpTitle: "Нужна помощь?",
    needHelpIntro:
      "Если у вас возникли трудности с удалением аккаунта или вопросы по процессу:",
    supportResponseTime:
      "Наша служба поддержки обычно отвечает в течение 24 часов и может помочь с:",
    helpDeletionTroubleshooting: "Устранение проблем с удалением аккаунта",
    helpDataExport: "Запросы на экспорт данных",
    helpAlternativeSolutions: "Альтернативные решения проблем с аккаунтом",
    helpPrivacySecurity: "Вопросы конфиденциальности и безопасности",
    securityNoteTitle: "Примечание по безопасности",
    securityNoteDesc:
      "Запросы на удаление аккаунта обрабатываются безопасно и обычно завершаются в течение 30 дней для полного удаления данных из всех систем и резервных копий.",

    // Safety Page (placeholders)
    safetyTitle: "Lingua Talk – Политика защиты и безопасности детей",
    safetyIntro:
      "В Lingua Talk безопасность и благополучие наших пользователей, особенно несовершеннолетних, являются нашим главным приоритетом. Как приложение для изучения языков и общения, соединяющее пользователей с персонажами ИИ и реальными языковыми партнёрами, мы обязуемся поддерживать безопасную и уважительную среду для всех.",
    zeroToleranceTitle: "Нулевая толерантность к эксплуатации детей",
    zeroToleranceIntro:
      "Мы не допускаем никакого контента или поведения, связанного с:",
    zeroToleranceItem1: "Сексуальное насилие или эксплуатация детей",
    zeroToleranceItem2:
      "Неприемлемое или вредное поведение по отношению к несовершеннолетним",
    zeroToleranceItem3:
      "Любые формы груминга, обнажённости несовершеннолетних или незаконного общения",
    zeroToleranceAction:
      "Весь подобный контент будет немедленно удалён и передан в соответствующие органы.",
    moderationTitle: "Модерация и мониторинг",
    moderationItem1:
      "Пользователи могут сообщать о разговорах и профилях прямо в приложении.",
    moderationItem2:
      "Отмеченный контент проверяется нашей командой модераторов.",
    moderationItem3:
      "Мы быстро реагируем на нарушения (предупреждения, приостановки, блокировки или сообщения в правоохранительные органы).",
    userAgePolicyTitle: "Политика возраста пользователей",
    userAgePolicyDesc:
      "Lingua Talk предназначен для пользователей от 13 лет и старше. Аккаунты, нарушающие эту политику, могут быть заблокированы или удалены.",
    reportAbuseTitle: "Как сообщить о нарушении",
    reportAbuseIntro: "Пользователи могут сообщать о:",
    reportAbuseItem1: "Сообщениях или неприемлемом контенте в чатах",
    reportAbuseItem2: "Профилях пользователей или подозрительном поведении",
    reportAbuseUseButton:
      'Используйте кнопку "Пожаловаться" в чатах или профилях. Жалобы проверяются нашей командой модераторов.',
    complianceTitle: "Соответствие законам о защите детей",
    compliesWith: "Lingua Talk соответствует:",
    complianceItem1: "Стандартам Google Play по защите детей",
    complianceItem2:
      "Применимым законам об эксплуатации детей во всех регионах",
    complianceItem3:
      "Лучшим практикам индустрии по безопасности платформ и отчётности",

    contactTitle: "Контакт",
    safetyContactIntro:
      "По вопросам, связанным с защитой детей, или для сообщения о серьёзной проблеме свяжитесь с нашей службой безопасности по адресу:",

    // Home V2
    v2MetaTitle: "LinguaTalk — Learn languages by talking to AI",
    v2MetaDescription: "LinguaTalk is a language learning app. Chat with AI tutors in Spanish, French, Japanese and 9 more languages. Free on iOS and Android.",
    v2StoreSubApple: "Download on the",
    v2StoreNameApple: "App Store",
    v2StoreSubGoogle: "Get it on",
    v2StoreNameGoogle: "Google Play",
    v2BazaarSub: "Download from",
    v2BazaarName: "Cafe Bazaar",
    v2MarqueeEnglish: "English",
    v2MarqueeSpanish: "Español",
    v2MarqueeFrench: "Français",
    v2MarqueeGerman: "Deutsch",
    v2MarqueeItalian: "Italiano",
    v2MarqueeTurkish: "Türkçe",
    v2WhatDesc: "Most apps throw the same content at everyone. LinguaTalk starts by understanding your level, your goals, and the language you want to learn — then builds a personalized roadmap. From there, you practice through real conversations with AI tutors and native speakers, both matched to exactly where you are right now.",
    v2Feature1Title: "AI Tutors",
    v2Feature1Desc: "Talk to intelligent AI characters that respond, correct, and guide you like a real teacher.",
    v2Feature2Title: "Real Conversations",
    v2Feature2Desc: "No flashcards. No multiple choice. Just you and your tutor — chatting in the language you want to learn.",
    v2Feature3Title: "Placement Test",
    v2Feature3Desc: "Start at exactly the right level. A short test puts you where you belong before your first lesson.",
    v2Feature4Title: "12+ Languages",
    v2Feature4Desc: "English, Spanish, French, German, Italian, Turkish and more — all in one app.",
    v2Step1Title: "Take the placement test",
    v2Step1Desc: "A 5-minute adaptive quiz finds your exact level.",
    v2Step2Title: "Choose your AI tutor",
    v2Step2Desc: "Pick a character that fits your style and target language.",
    v2Step3Title: "Start chatting every day",
    v2Step3Desc: "Short daily chats build real speaking confidence over time.",
    v2RingSub: "Your level",
    v2StatLessons: "Lessons done",
    v2StatStreak: "Day streak 🔥",
    v2StatWords: "Words learned",
    v2PopupDesc: "Take a free 5-minute placement test and get a personalized learning roadmap.",
    v2ChipSpanish: "🇪🇸 Spanish lesson",
    v2ChipAiTyping: "🤖 AI is typing…",
    v2ChipStreak: "🔥 7-day streak",
    v2ChatToday: "Today",
    v2ChatInputPlaceholder: "Type your reply…",
    v2TutorSub: "AI Tutor · Spanish",
    v2HeroLine1: "ИИ создаёт ваш",
    v2HeroLine2: "личный путь к беглости",
    v2Pill: "📱 Приложение для языков — Бесплатно на iOS и Android",
    v2Subtext: "LinguaTalk строит путь обучения по вашему уровню — затем вы практикуете каждый день с ИИ-тьюторами и носителями языка.",
    v2WhatTag: "Что такое LinguaTalk?",
    v2WhatTitle1: "Не только уроки —",
    v2WhatTitle2: "путь, созданный для вас",
    v2WhyLabel: "Почему это работает",
    v2HowLabel: "Как это работает",
    v2HowTitle1: "Три шага для начала",
    v2HowTitle2: "говорить с уверенностью",
    v2CtaTitle: "Получите бесплатный путь сегодня",
    v2CtaSub: "5 минут настройки · Бесплатно · iOS и Android",
    v2PopupTitle: "Определите уровень",
    v2PopupBtn: "Начать тест — Бесплатно",
    v2PopupFootnote: "Без регистрации",
  },
  zh: {
    appTitle: "LinguaTalk",
    appTagline: "寻找完美的语言伙伴。按照自己的节奏练习任何语言。",
    appDescription:
      "LinguaTalk将语言爱好者与世界各地的练习伙伴连接起来。无论您想学习阿拉伯语、中文、英语、法语、德语、意大利语、日语、韩语、波斯语、俄语、土耳其语或任何其他语言，我们都能满足您的需求。与AI导师聊天进行指导练习，或与真人连接，在有趣、社交的环境中提高您的口语、写作和阅读技能。",
    downloadIOS: "下载iOS版本",
    downloadAndroid: "下载Android版本",
    directDownload: "下载.apk文件",
    learnMore: "了解更多关于LinguaTalk",
    howItWorks: "LinguaTalk如何运作",
    step1Title: "创建您的个人资料",
    step1Description: "设置您的语言偏好、技能水平和兴趣，帮助找到最佳匹配。",
    step2Title: "寻找伙伴",
    step2Description: "探索AI角色或发现与您共享语言学习目标的真实用户。",
    step3Title: "练习和提高",
    step3Description: "通过聊天、语音通话或消息定期练习您的目标语言技能。",
    whyChoose: "为什么选择LinguaTalk？",
    feature1Title: "AI语言伙伴",
    feature1Description: "随时与智能AI角色练习，它们旨在帮助您提高语言技能。",
    feature2Title: "真实伙伴",
    feature2Description: "与母语人士和其他学习者连接，进行真实的语言交流。",
    feature3Title: "详细的个人资料",
    feature3Description: "创建和自定义您的个人资料，展示您的语言技能和兴趣。",
    feature4Title: "智能匹配",
    feature4Description: "根据您的技能水平、兴趣和学习目标找到完美的语言伙伴。",
    aiScreenTitle: "与AI角色聊天",
    aiScreenDescription:
      "通过与专为帮助您在安全、无评判环境中练习而设计的AI角色聊天来提高您的语言技能。非常适合初学者或当人类伙伴不可用时。",
    exploreScreenTitle: "探索真实伙伴",
    exploreScreenDescription:
      "搜索并连接来自世界各地的真实用户。按语言、熟练程度和兴趣过滤，找到您完美的语言交流伙伴。",
    footerText: "© 2025 LinguaTalk。保留所有权利。",
    language: "语言",
    // Top Menu
    menuHome: "首页",
    menuPlacementTest: "水平测试",
    menuPrivacyPolicy: "隐私政策",
    menuSafetySecurity: "安全与保障",
    menuDeleteAccount: "删除账户",
    // Popup
    popupTitle: "测试您的语言水平！",
    popupDescription: "只需8分钟即可了解您的水平。立即获得从A1到C2的测试结果。",
    popupButton: "立即开始测试",
    popupFooter: "或下载应用程序以保存您的结果",
    userPreviewTitle: "您被邀请了！",
    userPreviewMessage:
      "有人想和您在 Lingua Talk 上一起练习语言。下载应用即可连接 — 免费又有趣！",
    userPreviewTagline: "一起练语言",
    userPreviewOpenInApp: "在应用中打开",
    userPreviewHint: "免费下载 · 无需注册即可试用",
    // Placement Test
    testStarting: "测试开始中...",
    getReady: "准备好了！",
    testPreview: "测试预览",
    testYourLanguageProficiency: "测试您的语言水平",
    guest: "访客",
    totalQuestions: "总题数",
    totalTime: "总时间",
    testParts: "测试部分",
    question: "题",
    questions: "题",
    startTest: "开始测试",
    loadingTest: "加载测试中...",
    preparingTest: "请稍候，我们正在为您准备分班测试。",
    unableToLoadTest: "无法加载测试。请从应用程序重试。",
    greatJob: "太棒了！",
    yourEstimatedLevel: "您的估计水平是",
    score: "分数：",
    testEvaluated: "此分班测试评估了您在多个级别（从A1到C2）的{language}水平。",
    greatWorkCompleted:
      "干得好！您已完成{language}分班测试。登录以保存您的进度并解锁个性化学习功能。",
    tryAgain: "重试",
    backToHome: "返回首页",
    test: "测试",
    level: "级别：",
    type: "类型：",
    questionOf: "第 {current} 题，共 {total} 题（已回答 {answered} 题）",
    playAudio: "播放音频",
    previous: "上一题",
    next: "下一题",
    submitTest: "提交测试",
    testResultSaved: "您的测试结果已成功保存！",
    testResultFailed: "保存测试结果失败。请重试。",
    saveResultsTrackProgress: "想要保存您的结果并跟踪您的进度吗？",
    improveWithApp:
      "您还可以通过我们的应用程序AI和真实合作伙伴来提高您的语言技能。",
    grammar: "语法",
    vocabulary: "词汇",
    listening: "听力",
    downloadForIOS: "下载 iOS 版",
    downloadForAndroid: "下载 Android 版",
    placementTest: "水平测试",
    min: "分钟",
    testForLanguage: "语言测试：",
    testNotImplemented: "您是否在寻找其他语言测试？",
    availableLanguages: "目前，您可以使用以下语言进行测试：",
    viewAllLanguages: "您是否在寻找其他语言测试？",
    comingSoon: "即将推出...",

    privacyTitle: "隐私政策",
    privacyIntro:
      "在 Lingua Talk，我们重视您的隐私，并致力于保护您的个人信息。本政策解释了当您使用我们的语言学习和社交平台时，我们如何收集、使用和保护您的数据。",
    informationWeCollect: "我们收集的信息",
    accountInformationTitle: "账户信息：",
    infoCollectEmailUsername: "电子邮箱地址和用户名",
    infoCollectProfileInfo: "个人资料信息（姓名、年龄、所在地）",
    infoCollectLanguagePrefs: "语言偏好和学习目标",
    infoCollectSettingsPrefs: "账户设置和偏好",
    usageDataTitle: "使用数据：",
    usageDataConversationsAI: "与 AI 角色的对话",
    usageDataMessagesPartners: "与语言伙伴的消息",
    usageDataProgressStats: "学习进度和统计",
    usageDataAppPatterns: "应用使用模式和偏好",

    howWeUseTitle: "我们如何使用您的信息",
    provideServicesTitle: "提供我们的服务",
    provideServicesDesc: "启用语言学习功能和伙伴匹配",
    improveExperienceTitle: "提升用户体验",
    improveExperienceDesc: "个性化内容和 AI 角色互动",
    safetyModerationTitle: "安全与审核",
    safetyModerationDesc: "监控对话以防止不当内容",
    communicationTitle: "沟通",
    communicationDesc: "发送重要更新和通知",
    analyticsTitle: "分析",
    analyticsDesc: "了解用户如何使用我们的平台",

    dataProtectionTitle: "数据保护",
    encryptionTitle: "加密",
    encryptionDesc: "所有数据在传输和存储时均已加密",
    accessControlsTitle: "访问控制",
    accessControlsDesc: "仅限需要知情的员工访问",
    regularAuditsTitle: "定期审计",
    regularAuditsDesc: "安全审查和漏洞评估",
    secureInfrastructureTitle: "安全基础设施",
    secureInfrastructureDesc: "行业标准的云安全措施",
    dataMinimizationTitle: "数据最小化",
    dataMinimizationDesc: "我们只收集必要的信息",

    dataSharingTitle: "数据共享与第三方",
    noSellData: "我们不会将您的个人数据出售给第三方。",
    serviceProvidersTitle: "服务提供商",
    serviceProvidersDesc: "帮助我们运营平台的云托管、分析和安全服务",
    legalRequirementsTitle: "法律要求",
    legalRequirementsDesc: "当法律、法院命令要求，或为了保护用户安全时",
    withConsentTitle: "经您同意",
    withConsentDesc: "仅在您明确同意与特定合作伙伴共享信息时",

    yourRightsTitle: "您的隐私权利",
    rightAccessTitle: "访问",
    rightAccessDesc: "查看您的个人数据",
    rightCorrectionTitle: "更正",
    rightCorrectionDesc: "更新不正确的信息",
    rightDeletionTitle: "删除",
    rightDeletionDesc: "删除您的账户和数据",
    rightPortabilityTitle: "可携带性",
    rightPortabilityDesc: "导出您的数据",

    contactUsTitle: "联系我们",
    contactUsDesc: "如有隐私相关问题或行使您的权利：",
    policyUpdatesTitle: "政策更新",
    policyUpdatesDesc:
      "我们可能会不时更新此政策。重大更改将通过电子邮件或应用内通知告知您。",
    lastUpdated: "最后更新：2024年1月",

    deleteTitle: "删除您的账户",
    deleteIntro:
      "很遗憾看到您离开！如果您需要删除您的 Lingua Talk 账户，请按照以下说明操作。此操作是永久性的，无法撤销。",
    deleteImportantTitle: "⚠️ 重要提示：只能通过移动应用删除账户",
    deleteImportantDesc:
      "出于安全原因，账户删除功能仅在 Lingua Talk 移动应用中提供。您无法在本网站删除账户。",

    howToDeleteTitle: "如何删除您的账户",
    stepOpenAppTitle: "打开 Lingua Talk 移动应用",
    stepOpenAppDesc: "在您的手机或平板上启动应用",
    stepGoSettingsTitle: "进入设置",
    stepGoSettingsDesc: "导航到您的个人资料设置或账户设置",
    stepFindAccountTitle: "找到账户管理",
    stepFindAccountDesc: "查找“账户设置”或“隐私与安全”部分",
    stepSelectDeleteTitle: "选择“删除账户”",
    stepSelectDeleteDesc: "选择账户删除选项并按照确认步骤操作",

    whatDeletedTitle: "将被永久删除的内容",
    personalDataTitle: "个人数据",
    personalProfileInfo: "您的个人资料信息和设置",
    personalEmailCredentials: "电子邮箱地址和账户凭证",
    personalLangPrefsProgress: "语言偏好和学习进度",
    personalStatsAchievements: "个人统计和成就",

    conversationsContentTitle: "对话与内容",
    conversationsWithPartners: "与语言伙伴的所有对话",
    chatHistoryAI: "与 AI 角色的聊天记录",
    sharedMediaVoice: "已分享的媒体和语音消息",
    learningSessionRecordings: "学习会话录音",

    socialConnectionsTitle: "社交关系",
    partnerConnections: "您的语言伙伴关系",
    friendListsBlocked: "好友列表和已屏蔽用户",
    groupMemberships: "群组成员和社区",
    favoritesSavedContent: "收藏和已保存的内容",

    aiCharacterDataTitle: "AI 角色数据",
    customAIConfigs: "自定义 AI 角色配置",
    aiConversationHistory: "与 AI 导师的对话记录",
    personalizedRecommendations: "个性化学习推荐",
    aiGeneratedContent: "AI 生成的内容和回复",

    beforeDeleteTitle: "在删除您的账户之前",
    exportYourDataTitle: "导出您的数据",
    exportYourDataDesc: "在删除前，从应用设置下载您的对话记录和学习进度",
    considerBreakTitle: "考虑暂时停用",
    considerBreakDesc: "您可以暂时停用账户，而不是永久删除",
    contactSupportTitle: "联系支持",
    contactSupportDesc: "如果您遇到问题，我们的支持团队可能会帮您解决",

    needHelpTitle: "需要帮助？",
    needHelpIntro: "如果您在删除账户或过程中有问题：",
    supportResponseTime: "我们的支持团队通常在 24 小时内回复，可以协助您：",
    helpDeletionTroubleshooting: "账户删除问题排查",
    helpDataExport: "数据导出请求",
    helpAlternativeSolutions: "账户问题的替代解决方案",
    helpPrivacySecurity: "隐私和安全问题",

    securityNoteTitle: "安全说明",
    securityNoteDesc:
      "账户删除请求会被安全处理，通常在 30 天内完成，以确保从所有系统和备份中彻底删除数据。",

    safetyTitle: "Lingua Talk – 儿童安全与保护政策",
    safetyIntro:
      "在 Lingua Talk，用户，尤其是未成年人的安全与福祉是首要任务。作为一个连接用户与 AI 角色和真实语言伙伴的语言学习与社交应用，我们致力于为所有人保持安全和尊重的环境。",
    zeroToleranceTitle: "零容忍 CSAE",
    zeroToleranceIntro: "我们不允许任何与以下内容或行为相关的情况：",
    zeroToleranceItem1: "儿童性虐待或剥削",
    zeroToleranceItem2: "针对未成年人的不当或有害行为",
    zeroToleranceItem3: "任何形式的诱骗、涉及未成年人的裸露或非法交流",
    zeroToleranceAction: "所有此类内容将立即删除，并上报相关机构。",

    moderationTitle: "审核与监控",
    moderationItem1: "用户可以直接在应用中举报对话和个人资料。",
    moderationItem2: "被标记的内容由我们的审核团队审查。",
    moderationItem3: "我们会迅速采取措施（警告、停用、封禁或法律报告）。",

    userAgePolicyTitle: "用户年龄政策",
    userAgePolicyDesc:
      "Lingua Talk 仅适用于 13 岁及以上用户。违反此政策的账户可能会被暂停或移除。",

    reportAbuseTitle: "如何举报滥用行为",
    reportAbuseIntro: "用户可以举报：",
    reportAbuseItem1: "聊天中的消息或不当内容",
    reportAbuseItem2: "用户资料或可疑行为",
    reportAbuseUseButton:
      "在聊天或用户资料中使用“举报”按钮。举报将由我们的审核团队处理。",

    complianceTitle: "遵守儿童安全法律",
    compliesWith: "Lingua Talk 遵守：",
    complianceItem1: "Google Play 的儿童安全标准",
    complianceItem2: "所有运营地区适用的 CSAE 法律",
    complianceItem3: "行业最佳的安全与报告实践",
    complianceCooperate: "我们将根据要求，完全配合执法和相关机构。",

    contactTitle: "联系方式",
    safetyContactIntro:
      "如有儿童安全相关问题或报告严重问题，请联系我们的安全团队：",

    // Home V2
    v2MetaTitle: "LinguaTalk — Learn languages by talking to AI",
    v2MetaDescription: "LinguaTalk is a language learning app. Chat with AI tutors in Spanish, French, Japanese and 9 more languages. Free on iOS and Android.",
    v2StoreSubApple: "Download on the",
    v2StoreNameApple: "App Store",
    v2StoreSubGoogle: "Get it on",
    v2StoreNameGoogle: "Google Play",
    v2BazaarSub: "Download from",
    v2BazaarName: "Cafe Bazaar",
    v2MarqueeEnglish: "English",
    v2MarqueeSpanish: "Español",
    v2MarqueeFrench: "Français",
    v2MarqueeGerman: "Deutsch",
    v2MarqueeItalian: "Italiano",
    v2MarqueeTurkish: "Türkçe",
    v2WhatDesc: "Most apps throw the same content at everyone. LinguaTalk starts by understanding your level, your goals, and the language you want to learn — then builds a personalized roadmap. From there, you practice through real conversations with AI tutors and native speakers, both matched to exactly where you are right now.",
    v2Feature1Title: "AI Tutors",
    v2Feature1Desc: "Talk to intelligent AI characters that respond, correct, and guide you like a real teacher.",
    v2Feature2Title: "Real Conversations",
    v2Feature2Desc: "No flashcards. No multiple choice. Just you and your tutor — chatting in the language you want to learn.",
    v2Feature3Title: "Placement Test",
    v2Feature3Desc: "Start at exactly the right level. A short test puts you where you belong before your first lesson.",
    v2Feature4Title: "12+ Languages",
    v2Feature4Desc: "English, Spanish, French, German, Italian, Turkish and more — all in one app.",
    v2Step1Title: "Take the placement test",
    v2Step1Desc: "A 5-minute adaptive quiz finds your exact level.",
    v2Step2Title: "Choose your AI tutor",
    v2Step2Desc: "Pick a character that fits your style and target language.",
    v2Step3Title: "Start chatting every day",
    v2Step3Desc: "Short daily chats build real speaking confidence over time.",
    v2RingSub: "Your level",
    v2StatLessons: "Lessons done",
    v2StatStreak: "Day streak 🔥",
    v2StatWords: "Words learned",
    v2PopupDesc: "Take a free 5-minute placement test and get a personalized learning roadmap.",
    v2ChipSpanish: "🇪🇸 Spanish lesson",
    v2ChipAiTyping: "🤖 AI is typing…",
    v2ChipStreak: "🔥 7-day streak",
    v2ChatToday: "Today",
    v2ChatInputPlaceholder: "Type your reply…",
    v2TutorSub: "AI Tutor · Spanish",
    v2HeroLine1: "AI 为你设计",
    v2HeroLine2: "个人流利之路",
    v2Pill: "📱 语言学习应用 — iOS 和 Android 免费",
    v2Subtext: "LinguaTalk 根据你的水平制定学习路径，然后每天与 AI 导师和母语者练习，直到自信开口。",
    v2WhatTag: "什么是 LinguaTalk？",
    v2WhatTitle1: "不只是课程 —",
    v2WhatTitle2: "围绕你定制的路线",
    v2WhyLabel: "为什么有效",
    v2HowLabel: "如何使用",
    v2HowTitle1: "三步开始",
    v2HowTitle2: "自信开口",
    v2CtaTitle: "今天获取免费学习路线",
    v2CtaSub: "5 分钟设置 · 永久免费 · iOS 和 Android",
    v2PopupTitle: "测试你的水平",
    v2PopupBtn: "开始测试 — 免费",
    v2PopupFootnote: "无需注册",
  },
  ja: {
    appTitle: "LinguaTalk",
    appTagline:
      "最適な言語パートナーを見つけましょう。どんな言語でも自分のペースで練習しましょう。",
    appDescription:
      "LinguaTalkは、言語愛好家と世界中の練習パートナーをつなぎます。アラビア語、中国語、英語、フランス語、ドイツ語、イタリア語、日本語、韓国語、ペルシア語、ロシア語、トルコ語、あるいはその他の言語を学びたいと思っていても、私たちがサポートします。AIチューターとチャットして指導付き練習を行ったり、実際の人々とつながって、楽しく社交的な環境でスピーキング、ライティング、リーディングスキルを向上させましょう。",
    downloadIOS: "iOSにダウンロード",
    downloadAndroid: "Androidにダウンロード",
    directDownload: ".apkファイルをダウンロード",
    learnMore: "LinguaTalkについてもっと知る",
    howItWorks: "LinguaTalkの仕組み",
    step1Title: "プロフィールを作成",
    step1Description:
      "最適なマッチングを見つけるために、言語の好み、スキルレベル、興味を設定します。",
    step2Title: "パートナーを見つける",
    step2Description:
      "AIキャラクターを探索したり、言語学習の目標を共有する実際のユーザーを発見したりします。",
    step3Title: "練習と上達",
    step3Description:
      "チャット、音声通話、またはメッセージで目標言語スキルを定期的に練習します。",
    whyChoose: "なぜLinguaTalkを選ぶのか？",
    feature1Title: "AI言語パートナー",
    feature1Description:
      "言語スキル向上を支援するためにデザインされたインテリジェントなAIキャラクターといつでも練習できます。",
    feature2Title: "実際のパートナー",
    feature2Description:
      "ネイティブスピーカーや他の学習者と接続して、本物の言語交換を行います。",
    feature3Title: "詳細なプロフィール",
    feature3Description:
      "プロフィールを作成・カスタマイズして、言語スキルと興味を表示します。",
    feature4Title: "スマートマッチング",
    feature4Description:
      "スキルレベル、興味、学習目標に基づいて完璧な言語パートナーを見つけます。",
    aiScreenTitle: "AIキャラクターとチャット",
    aiScreenDescription:
      "安全で判断のない環境で練習できるよう設計されたAIキャラクターとチャットして、言語スキルを向上させましょう。初心者の方や人間のパートナーが利用できない場合に最適です。",
    exploreScreenTitle: "実際のパートナーを探索",
    exploreScreenDescription:
      "世界中の実際のユーザーを検索して接続します。言語、習熟度、興味によってフィルタリングし、完璧な言語交換パートナーを見つけましょう。",
    footerText: "© 2025 LinguaTalk. All rights reserved.",
    language: "言語",
    // Top Menu
    menuHome: "ホーム",
    menuPlacementTest: "プレースメントテスト",
    menuPrivacyPolicy: "プライバシーポリシー",
    menuSafetySecurity: "安全とセキュリティ",
    menuDeleteAccount: "アカウント削除",
    // Popup
    popupTitle: "言語レベルをテストしよう！",
    popupDescription:
      "わずか8分で習熟度を確認できます。A1からC2レベルの結果をすぐに取得。",
    popupButton: "今すぐテスト開始",
    popupFooter: "またはアプリをダウンロードして結果を保存",
    userPreviewTitle: "招待されました！",
    userPreviewMessage:
      "誰かがLingua Talkであなたと言語を練習したがっています。アプリをダウンロードしてつながりましょう — 無料で楽しい！",
    userPreviewTagline: "一緒に言語を練習",
    userPreviewOpenInApp: "アプリで開く",
    userPreviewHint: "無料ダウンロード · お試しに登録不要",
    // Placement Test
    testStarting: "テスト開始中...",
    getReady: "準備してください！",
    testPreview: "テストプレビュー",
    testYourLanguageProficiency: "あなたの言語能力をテストする",
    guest: "ゲスト",
    totalQuestions: "総問題数",
    totalTime: "合計時間",
    testParts: "テストセクション",
    question: "問題",
    questions: "問題",
    startTest: "テスト開始",
    loadingTest: "テスト読み込み中...",
    preparingTest: "レベル判定テストの準備中です。しばらくお待ちください。",
    unableToLoadTest:
      "テストを読み込めませんでした。アプリから再度お試しください。",
    greatJob: "素晴らしい！",
    yourEstimatedLevel: "推定レベルは",
    score: "スコア：",
    testEvaluated:
      "このレベル判定テストは、A1からC2までの複数のレベルで{language}の能力を評価しました。",
    greatWorkCompleted:
      "素晴らしい！{language}のレベル判定テストを完了しました。進捗を保存し、パーソナライズされた学習機能のロックを解除するには、ログインしてください。",
    tryAgain: "再試行",
    backToHome: "ホームに戻る",
    test: "テスト",
    level: "レベル：",
    type: "タイプ：",
    questionOf: "問題 {current}/{total}（{answered}問回答済み）",
    playAudio: "音声を再生",
    previous: "前へ",
    next: "次へ",
    submitTest: "テストを提出",
    testResultSaved: "テスト結果が正常に保存されました！",
    testResultFailed:
      "テスト結果の保存に失敗しました。もう一度お試しください。",
    saveResultsTrackProgress: "結果を保存して進捗を追跡しますか？",
    improveWithApp:
      "アプリのAIと実際のパートナーと一緒に言語スキルを向上させることもできます。",
    grammar: "文法",
    vocabulary: "語彙",
    listening: "リスニング",
    downloadForIOS: "iOS用をダウンロード",
    downloadForAndroid: "Android用をダウンロード",
    placementTest: "プレースメントテスト",
    min: "分",
    testForLanguage: "言語テスト：",
    testNotImplemented: "他の言語のテストをお探しですか？",
    availableLanguages: "現在、以下の言語でテストを受けることができます：",
    viewAllLanguages: "他の言語のテストをお探しですか？",
    comingSoon: "近日公開...",

    privacyTitle: "プライバシーポリシー",
    privacyIntro:
      "Lingua Talkでは、ユーザーのプライバシーを重視し、個人情報の保護に努めています。本ポリシーでは、言語学習およびソーシャルプラットフォームの利用時にどのように情報を収集、利用、保護するかを説明します。",
    informationWeCollect: "収集する情報",
    accountInformationTitle: "アカウント情報：",
    infoCollectEmailUsername: "メールアドレスとユーザー名",
    infoCollectProfileInfo: "プロフィール情報（名前、年齢、所在地）",
    infoCollectLanguagePrefs: "言語の好みと学習目標",
    infoCollectSettingsPrefs: "アカウント設定と好み",

    usageDataTitle: "使用データ：",
    usageDataConversationsAI: "AIキャラクターとの会話",
    usageDataMessagesPartners: "言語パートナーとのメッセージ",
    usageDataProgressStats: "学習進捗と統計",
    usageDataAppPatterns: "アプリ使用パターンと好み",

    howWeUseTitle: "情報の利用方法",
    provideServicesTitle: "サービス提供",
    provideServicesDesc: "言語学習機能とパートナーマッチングを有効化",
    improveExperienceTitle: "ユーザー体験の向上",
    improveExperienceDesc:
      "コンテンツとAIキャラクターとのインタラクションをパーソナライズ",
    safetyModerationTitle: "安全とモデレーション",
    safetyModerationDesc: "不適切なコンテンツの監視",
    communicationTitle: "コミュニケーション",
    communicationDesc: "重要なアップデートや通知を送信",
    analyticsTitle: "分析",
    analyticsDesc: "ユーザーのプラットフォーム利用状況を理解",

    dataProtectionTitle: "データ保護",
    encryptionTitle: "暗号化",
    encryptionDesc: "すべてのデータは転送中および保存時に暗号化されます",
    accessControlsTitle: "アクセス制御",
    accessControlsDesc: "必要に応じて従業員のアクセスを制限",
    regularAuditsTitle: "定期監査",
    regularAuditsDesc: "セキュリティレビューと脆弱性評価",
    secureInfrastructureTitle: "安全なインフラ",
    secureInfrastructureDesc: "業界標準のクラウドセキュリティ対策",
    dataMinimizationTitle: "データ最小化",
    dataMinimizationDesc: "必要な情報のみ収集します",

    dataSharingTitle: "データ共有と第三者",
    noSellData: "個人データを第三者に販売することはありません。",
    serviceProvidersTitle: "サービスプロバイダー",
    serviceProvidersDesc:
      "プラットフォーム運営を支援するクラウドホスティング、分析、セキュリティサービス",
    legalRequirementsTitle: "法的要件",
    legalRequirementsDesc:
      "法律、裁判所命令、またはユーザーの安全を守るために必要な場合",
    withConsentTitle: "同意による共有",
    withConsentDesc: "特定のパートナーとの情報共有に明示的に同意した場合のみ",

    yourRightsTitle: "プライバシー権",
    rightAccessTitle: "アクセス",
    rightAccessDesc: "個人データの閲覧",
    rightCorrectionTitle: "訂正",
    rightCorrectionDesc: "誤った情報の更新",
    rightDeletionTitle: "削除",
    rightDeletionDesc: "アカウントとデータの削除",
    rightPortabilityTitle: "データポータビリティ",
    rightPortabilityDesc: "データのエクスポート",

    contactUsTitle: "お問い合わせ",
    contactUsDesc: "プライバシー関連の質問や権利行使のために：",
    policyUpdatesTitle: "ポリシーの更新",
    policyUpdatesDesc:
      "このポリシーは随時更新される場合があります。重大な変更はメールまたはアプリ内通知でお知らせします。",
    lastUpdated: "最終更新日：2024年1月",

    deleteTitle: "アカウントを削除",
    deleteIntro:
      "ご利用ありがとうございました！Lingua Talkのアカウントを削除する場合は、以下の手順に従ってください。この操作は永久的で元に戻せません。",
    deleteImportantTitle: "⚠️ 重要：アカウント削除はモバイルアプリからのみ可能",
    deleteImportantDesc:
      "セキュリティ上の理由により、アカウント削除はモバイルアプリでのみ利用可能です。本ウェブサイトから削除することはできません。",

    howToDeleteTitle: "アカウントの削除方法",
    stepOpenAppTitle: "Lingua Talk モバイルアプリを開く",
    stepOpenAppDesc: "スマートフォンまたはタブレットでアプリを起動",
    stepGoSettingsTitle: "設定に移動",
    stepGoSettingsDesc: "プロフィール設定またはアカウント設定に進む",
    stepFindAccountTitle: "アカウント管理を探す",
    stepFindAccountDesc:
      "「アカウント設定」または「プライバシーとセキュリティ」を確認",
    stepSelectDeleteTitle: "「アカウント削除」を選択",
    stepSelectDeleteDesc: "アカウント削除オプションを選択し、確認手順に従う",

    whatDeletedTitle: "永久に削除される内容",
    personalDataTitle: "個人データ",
    personalProfileInfo: "プロフィール情報と設定",
    personalEmailCredentials: "メールアドレスとアカウント認証情報",
    personalLangPrefsProgress: "言語設定と学習進捗",
    personalStatsAchievements: "個人統計と実績",

    conversationsContentTitle: "会話とコンテンツ",
    conversationsWithPartners: "言語パートナーとのすべての会話",
    chatHistoryAI: "AIキャラクターとのチャット履歴",
    sharedMediaVoice: "共有メディアと音声メッセージ",
    learningSessionRecordings: "学習セッションの録音",

    socialConnectionsTitle: "ソーシャル接続",
    partnerConnections: "言語パートナーとの接続",
    friendListsBlocked: "友達リストとブロックされたユーザー",
    groupMemberships: "グループメンバーシップとコミュニティ",
    favoritesSavedContent: "お気に入りと保存されたコンテンツ",

    aiCharacterDataTitle: "AIキャラクターデータ",
    customAIConfigs: "カスタムAIキャラクター設定",
    aiConversationHistory: "AIチューターとの会話履歴",
    personalizedRecommendations: "パーソナライズされた学習推奨",
    aiGeneratedContent: "AI生成コンテンツと回答",

    beforeDeleteTitle: "アカウントを削除する前に",
    exportYourDataTitle: "データをエクスポート",
    exportYourDataDesc:
      "削除前にアプリ設定から会話履歴と学習進捗をダウンロード",
    considerBreakTitle: "一時的な休止を検討",
    considerBreakDesc:
      "永久削除の代わりにアカウントを一時的に非アクティブにできます",
    contactSupportTitle: "サポートに連絡",
    contactSupportDesc:
      "問題がある場合、サポートチームが解決を支援する可能性があります",

    needHelpTitle: "助けが必要ですか？",
    needHelpIntro: "アカウント削除や手順に関する質問がある場合：",
    supportResponseTime:
      "サポートチームは通常24時間以内に回答し、以下を支援します：",
    helpDeletionTroubleshooting: "アカウント削除のトラブルシューティング",
    helpDataExport: "データエクスポートリクエスト",
    helpAlternativeSolutions: "アカウント問題の代替解決策",
    helpPrivacySecurity: "プライバシーとセキュリティに関する質問",

    securityNoteTitle: "セキュリティ注意事項",
    securityNoteDesc:
      "アカウント削除リクエストは安全に処理され、通常30日以内に完了し、すべてのシステムとバックアップからデータが完全に削除されます。",

    safetyTitle: "Lingua Talk – 子どもの安全と保護ポリシー",
    safetyIntro:
      "Lingua Talkでは、特に未成年ユーザーの安全と福祉を最優先しています。AIキャラクターやリアルな言語パートナーとつながる学習・交流アプリとして、全てのユーザーに安全で尊重された環境を提供します。",
    zeroToleranceTitle: "CSAEへのゼロトレランス",
    zeroToleranceIntro: "以下に関連するコンテンツや行為は許可されません：",
    zeroToleranceItem1: "児童性的虐待または搾取",
    zeroToleranceItem2: "未成年者への不適切または有害な行為",
    zeroToleranceItem3: "未成年者を含む誘導、裸露、違法な通信",
    zeroToleranceAction:
      "これらのコンテンツは即座に削除され、関連機関に報告されます。",

    moderationTitle: "モデレーションと監視",
    moderationItem1:
      "ユーザーはアプリ内で直接会話やプロフィールを報告できます。",
    moderationItem2: "報告されたコンテンツはモデレーションチームが確認します。",
    moderationItem3: "違反には迅速に対応します（警告、停止、禁止、法的報告）。",

    userAgePolicyTitle: "ユーザー年齢ポリシー",
    userAgePolicyDesc:
      "Lingua Talkは16歳以上のユーザー向けです。このポリシーに違反するアカウントは停止または削除される場合があります。",

    reportAbuseTitle: "虐待の報告方法",
    reportAbuseIntro: "ユーザーは以下を報告できます：",
    reportAbuseItem1: "チャット内のメッセージや不適切なコンテンツ",
    reportAbuseItem2: "ユーザープロフィールや疑わしい行為",
    reportAbuseUseButton:
      "チャットやプロフィールの「報告」ボタンを使用してください。報告はモデレーションチームが確認します。",

    complianceTitle: "児童安全法の遵守",
    compliesWith: "Lingua Talkは以下に準拠しています：",
    complianceItem1: "Google Playの児童安全基準",
    complianceItem2: "すべての運営地域のCSAE関連法",
    complianceItem3: "プラットフォーム安全性と報告の業界ベストプラクティス",

    contactTitle: "連絡先",
    safetyContactIntro:
      "子どもの安全に関する質問や重大な問題の報告は、セーフティチームにお問い合わせください：",

    // Home V2
    v2MetaTitle: "LinguaTalk — Learn languages by talking to AI",
    v2MetaDescription: "LinguaTalk is a language learning app. Chat with AI tutors in Spanish, French, Japanese and 9 more languages. Free on iOS and Android.",
    v2StoreSubApple: "Download on the",
    v2StoreNameApple: "App Store",
    v2StoreSubGoogle: "Get it on",
    v2StoreNameGoogle: "Google Play",
    v2BazaarSub: "Download from",
    v2BazaarName: "Cafe Bazaar",
    v2MarqueeEnglish: "English",
    v2MarqueeSpanish: "Español",
    v2MarqueeFrench: "Français",
    v2MarqueeGerman: "Deutsch",
    v2MarqueeItalian: "Italiano",
    v2MarqueeTurkish: "Türkçe",
    v2WhatDesc: "Most apps throw the same content at everyone. LinguaTalk starts by understanding your level, your goals, and the language you want to learn — then builds a personalized roadmap. From there, you practice through real conversations with AI tutors and native speakers, both matched to exactly where you are right now.",
    v2Feature1Title: "AI Tutors",
    v2Feature1Desc: "Talk to intelligent AI characters that respond, correct, and guide you like a real teacher.",
    v2Feature2Title: "Real Conversations",
    v2Feature2Desc: "No flashcards. No multiple choice. Just you and your tutor — chatting in the language you want to learn.",
    v2Feature3Title: "Placement Test",
    v2Feature3Desc: "Start at exactly the right level. A short test puts you where you belong before your first lesson.",
    v2Feature4Title: "12+ Languages",
    v2Feature4Desc: "English, Spanish, French, German, Italian, Turkish and more — all in one app.",
    v2Step1Title: "Take the placement test",
    v2Step1Desc: "A 5-minute adaptive quiz finds your exact level.",
    v2Step2Title: "Choose your AI tutor",
    v2Step2Desc: "Pick a character that fits your style and target language.",
    v2Step3Title: "Start chatting every day",
    v2Step3Desc: "Short daily chats build real speaking confidence over time.",
    v2RingSub: "Your level",
    v2StatLessons: "Lessons done",
    v2StatStreak: "Day streak 🔥",
    v2StatWords: "Words learned",
    v2PopupDesc: "Take a free 5-minute placement test and get a personalized learning roadmap.",
    v2ChipSpanish: "🇪🇸 Spanish lesson",
    v2ChipAiTyping: "🤖 AI is typing…",
    v2ChipStreak: "🔥 7-day streak",
    v2ChatToday: "Today",
    v2ChatInputPlaceholder: "Type your reply…",
    v2TutorSub: "AI Tutor · Spanish",
    v2HeroLine1: "AIがあなたの",
    v2HeroLine2: "個別の流暢さロードマップを設計",
    v2Pill: "📱 語学学習アプリ — iOS & Android 無料",
    v2Subtext: "LinguaTalkはあなたのレベルに合わせた学習パスを作り、AIチューターとネイティブスピーカーと毎日練習して自信を持って話せます。",
    v2WhatTag: "LinguaTalkとは？",
    v2WhatTitle1: "レッスンだけじゃない —",
    v2WhatTitle2: "あなた専用のロードマップ",
    v2WhyLabel: "なぜ効くのか",
    v2HowLabel: "使い方",
    v2HowTitle1: "始める3ステップ",
    v2HowTitle2: "自信を持って話す",
    v2CtaTitle: "無料ロードマップを今すぐ",
    v2CtaSub: "5分で設定 · 永久無料 · iOS & Android",
    v2PopupTitle: "レベルを確認",
    v2PopupBtn: "テストを始める — 無料",
    v2PopupFootnote: "登録不要",
  },
  it: {
    appTitle: "LinguaTalk",
    appTagline:
      "Trova il partner linguistico perfetto. Esercitati in qualsiasi lingua al tuo ritmo.",
    appDescription:
      "LinguaTalk connette gli appassionati di lingue con partner di pratica da tutto il mondo. Che tu voglia imparare l'arabo, il cinese, l'inglese, il francese, il tedesco, l'italiano, il giapponese, il coreano, il persiano, il russo, il turco o qualsiasi altra lingua, noi ti copriamo. Chatta con tutor AI per esercizi guidati o connettiti con persone reali per migliorare le tue abilità di conversazione, scrittura e lettura in un ambiente divertente e sociale.",
    downloadIOS: "Scarica per iOS",
    downloadAndroid: "Scarica per Android",
    directDownload: "Scarica file .apk",
    learnMore: "Scopri di più su LinguaTalk",
    howItWorks: "Come funziona LinguaTalk",
    step1Title: "Crea il tuo profilo",
    step1Description:
      "Imposta le tue preferenze linguistiche, il livello di abilità e gli interessi per aiutare a trovare il match perfetto.",
    step2Title: "Trova partner",
    step2Description:
      "Esplora personaggi AI o scopri utenti reali che condividono i tuoi obiettivi di apprendimento linguistico.",
    step3Title: "Esercitati e migliora",
    step3Description:
      "Chatta, effettua chiamate vocali o invia messaggi per esercitare regolarmente le tue abilità linguistiche.",
    whyChoose: "Perché scegliere LinguaTalk?",
    feature1Title: "Partner linguistici AI",
    feature1Description:
      "Esercitati in qualsiasi momento con personaggi AI intelligenti progettati per aiutarti a migliorare le tue abilità linguistiche.",
    feature2Title: "Partner reali",
    feature2Description:
      "Connettiti con madrelingua e altri studenti per un autentico scambio linguistico.",
    feature3Title: "Profili dettagliati",
    feature3Description:
      "Crea e personalizza il tuo profilo per mostrare le tue abilità linguistiche e i tuoi interessi.",
    feature4Title: "Abbinamento intelligente",
    feature4Description:
      "Trova il partner linguistico perfetto in base al tuo livello di abilità, interessi e obiettivi di apprendimento.",
    aiScreenTitle: "Chatta con personaggi AI",
    aiScreenDescription:
      "Migliora le tue abilità linguistiche chattando con personaggi AI progettati per aiutarti a esercitarti in un ambiente sicuro e privo di giudizi. Perfetto per principianti o quando non sono disponibili partner umani.",
    exploreScreenTitle: "Esplora partner reali",
    exploreScreenDescription:
      "Cerca e connettiti con utenti reali da tutto il mondo. Filtra per lingua, livello di competenza e interessi per trovare il tuo partner di scambio linguistico perfetto.",
    footerText: "© 2025 LinguaTalk. Tutti i diritti riservati.",
    language: "Lingua",
    // Top Menu
    menuHome: "Home",
    menuPlacementTest: "Test di livello",
    menuPrivacyPolicy: "Informativa sulla privacy",
    menuSafetySecurity: "Sicurezza",
    menuDeleteAccount: "Elimina account",
    // Popup
    popupTitle: "Testa il tuo livello linguistico!",
    popupDescription:
      "Scopri la tua competenza in soli 8 minuti. Ottieni risultati immediati dal livello A1 al C2.",
    popupButton: "Inizia il test ora",
    popupFooter: "oppure scarica l'app per salvare i tuoi risultati",
    userPreviewTitle: "Sei stato invitato!",
    userPreviewMessage:
      "Qualcuno vuole praticare le lingue con te su Lingua Talk. Scarica l'app per connetterti — gratis e divertente!",
    userPreviewTagline: "Pratica le lingue insieme",
    userPreviewOpenInApp: "Apri nell'app",
    userPreviewHint: "Gratis · Nessun account per provare",
    // Placement Test
    testStarting: "Avvio del test...",
    getReady: "Preparati!",
    testPreview: "Anteprima del test",
    testYourLanguageProficiency:
      "Metti alla prova la tua competenza linguistica",
    guest: "Ospite",
    totalQuestions: "Domande totali",
    totalTime: "Tempo totale",
    testParts: "Sezioni del test",
    question: "domanda",
    questions: "domande",
    startTest: "Inizia il test",
    loadingTest: "Caricamento del test...",
    preparingTest: "Attendere mentre prepariamo il test di livello.",
    unableToLoadTest: "Impossibile caricare il test. Riprova dall'app.",
    greatJob: "Ottimo lavoro!",
    yourEstimatedLevel: "Il tuo livello stimato è",
    score: "Punteggio:",
    testEvaluated:
      "Questo test di livello ha valutato la tua competenza in {language} su più livelli da A1 a C2.",
    greatWorkCompleted:
      "Ottimo lavoro! Hai completato il test di livello in {language}. Accedi per salvare i tuoi progressi e sbloccare funzionalità di apprendimento personalizzate.",
    tryAgain: "Riprova",
    backToHome: "Torna alla home",
    test: "Test",
    level: "Livello:",
    type: "Tipo:",
    questionOf: "Domanda {current} di {total} ({answered} risposte)",
    playAudio: "Riproduci audio",
    previous: "Precedente",
    next: "Successivo",
    submitTest: "Invia il test",
    testResultSaved: "Il risultato del test è stato salvato con successo!",
    testResultFailed:
      "Salvataggio del risultato del test non riuscito. Riprova.",
    saveResultsTrackProgress:
      "Vuoi salvare i tuoi risultati e tracciare i tuoi progressi?",
    improveWithApp:
      "Puoi anche migliorare le tue competenze linguistiche con la nostra app AI e partner reali.",
    grammar: "Grammatica",
    vocabulary: "Vocabolario",
    listening: "Ascolto",
    downloadForIOS: "Scarica per iOS",
    downloadForAndroid: "Scarica per Android",
    placementTest: "Test di livello",
    min: "min",
    testForLanguage: "Test per la lingua:",
    testNotImplemented: "Stai cercando altri test linguistici?",
    availableLanguages: "Per ora, puoi sostenere il test in queste lingue:",
    viewAllLanguages: "Stai cercando un altro test linguistico?",
    comingSoon: "Prossimamente...",

    privacyTitle: "Informativa sulla privacy",
    privacyIntro:
      "Su Lingua Talk, valorizziamo la tua privacy e ci impegniamo a proteggere le tue informazioni personali. Questa politica spiega come raccogliamo, utilizziamo e proteggiamo i tuoi dati quando utilizzi la nostra piattaforma di apprendimento delle lingue e sociale.",
    informationWeCollect: "Informazioni raccolte",
    accountInformationTitle: "Informazioni sull'account:",
    infoCollectEmailUsername: "Indirizzo email e nome utente",
    infoCollectProfileInfo: "Informazioni del profilo (nome, età, posizione)",
    infoCollectLanguagePrefs:
      "Preferenze linguistiche e obiettivi di apprendimento",
    infoCollectSettingsPrefs: "Impostazioni e preferenze dell'account",

    usageDataTitle: "Dati di utilizzo:",
    usageDataConversationsAI: "Conversazioni con personaggi AI",
    usageDataMessagesPartners: "Messaggi con partner linguistici",
    usageDataProgressStats: "Progresso e statistiche di apprendimento",
    usageDataAppPatterns: "Modelli di utilizzo e preferenze dell'app",

    howWeUseTitle: "Come utilizziamo le tue informazioni",
    provideServicesTitle: "Fornire servizi",
    provideServicesDesc:
      "Abilitare funzionalità di apprendimento linguistico e matchmaking",
    improveExperienceTitle: "Migliorare l'esperienza utente",
    improveExperienceDesc:
      "Personalizzare contenuti e interazioni con personaggi AI",
    safetyModerationTitle: "Sicurezza e moderazione",
    safetyModerationDesc:
      "Monitorare le conversazioni per contenuti inappropriati",
    communicationTitle: "Comunicazioni",
    communicationDesc: "Inviare aggiornamenti e notifiche importanti",
    analyticsTitle: "Analisi",
    analyticsDesc:
      "Comprendere come gli utenti interagiscono con la piattaforma",

    dataProtectionTitle: "Protezione dei dati",
    encryptionTitle: "Crittografia",
    encryptionDesc: "Tutti i dati sono crittografati in transito e a riposo",
    accessControlsTitle: "Controlli di accesso",
    accessControlsDesc: "Accesso limitato ai dipendenti su base necessaria",
    regularAuditsTitle: "Audit regolari",
    regularAuditsDesc:
      "Revisioni di sicurezza e valutazioni delle vulnerabilità",
    secureInfrastructureTitle: "Infrastruttura sicura",
    secureInfrastructureDesc: "Misure di sicurezza cloud standard del settore",
    dataMinimizationTitle: "Minimizzazione dei dati",
    dataMinimizationDesc: "Raccogliamo solo le informazioni necessarie",

    dataSharingTitle: "Condivisione dei dati e terze parti",
    noSellData: "Non vendiamo i tuoi dati personali a terze parti.",
    serviceProvidersTitle: "Fornitori di servizi",
    serviceProvidersDesc:
      "Servizi di hosting cloud, analisi e sicurezza che ci aiutano a gestire la piattaforma",
    legalRequirementsTitle: "Requisiti legali",
    legalRequirementsDesc:
      "Quando richiesto dalla legge, da un ordine del tribunale o per proteggere la sicurezza dell'utente",
    withConsentTitle: "Con il tuo consenso",
    withConsentDesc:
      "Solo quando acconsenti esplicitamente a condividere informazioni con partner specifici",

    yourRightsTitle: "I tuoi diritti sulla privacy",
    rightAccessTitle: "Accesso",
    rightAccessDesc: "Visualizza i tuoi dati personali",
    rightCorrectionTitle: "Correzione",
    rightCorrectionDesc: "Aggiorna informazioni errate",
    rightDeletionTitle: "Cancellazione",
    rightDeletionDesc: "Elimina il tuo account e i dati",
    rightPortabilityTitle: "Portabilità",
    rightPortabilityDesc: "Esporta i tuoi dati",

    contactUsTitle: "Contattaci",
    contactUsDesc: "Per domande sulla privacy o per esercitare i tuoi diritti:",
    policyUpdatesTitle: "Aggiornamenti della politica",
    policyUpdatesDesc:
      "Potremmo aggiornare questa politica di tanto in tanto. Ti informeremo di modifiche significative via email o notifica in-app.",
    lastUpdated: "Ultimo aggiornamento: Gennaio 2024",

    deleteTitle: "Elimina il tuo account",
    deleteIntro:
      "Ci dispiace vederti andare! Se desideri eliminare il tuo account Lingua Talk, segui le istruzioni seguenti. Questa azione è permanente e non può essere annullata.",
    deleteImportantTitle:
      "⚠️ IMPORTANTE: L'eliminazione dell'account può avvenire solo tramite l'app mobile",
    deleteImportantDesc:
      "Per motivi di sicurezza, l'eliminazione dell'account è disponibile solo nell'app mobile Lingua Talk. Non puoi eliminare l'account da questo sito web.",

    howToDeleteTitle: "Come eliminare il tuo account",
    stepOpenAppTitle: "Apri l'app mobile Lingua Talk",
    stepOpenAppDesc: "Avvia l'app sul tuo telefono o tablet",
    stepGoSettingsTitle: "Vai alle impostazioni",
    stepGoSettingsDesc: "Naviga nelle impostazioni del profilo o dell'account",
    stepFindAccountTitle: "Trova la gestione account",
    stepFindAccountDesc:
      "Cerca la sezione 'Impostazioni account' o 'Privacy e sicurezza'",
    stepSelectDeleteTitle: "Seleziona 'Elimina account'",
    stepSelectDeleteDesc:
      "Scegli l'opzione di eliminazione e segui i passaggi di conferma",

    whatDeletedTitle: "Cosa sarà eliminato permanentemente",
    personalDataTitle: "Dati personali",
    personalProfileInfo: "Informazioni e impostazioni del tuo profilo",
    personalEmailCredentials: "Indirizzo email e credenziali dell'account",
    personalLangPrefsProgress:
      "Preferenze linguistiche e progressi di apprendimento",
    personalStatsAchievements: "Statistiche personali e risultati",

    conversationsContentTitle: "Conversazioni e contenuti",
    conversationsWithPartners:
      "Tutte le conversazioni con i partner linguistici",
    chatHistoryAI: "Cronologia chat con personaggi AI",
    sharedMediaVoice: "Media condivisi e messaggi vocali",
    learningSessionRecordings: "Registrazioni delle sessioni di apprendimento",

    socialConnectionsTitle: "Connessioni sociali",
    partnerConnections: "Connessioni con partner linguistici",
    friendListsBlocked: "Elenchi amici e utenti bloccati",
    groupMemberships: "Iscrizioni a gruppi e comunità",
    favoritesSavedContent: "Preferiti e contenuti salvati",

    aiCharacterDataTitle: "Dati dei personaggi AI",
    customAIConfigs: "Configurazioni personalizzate dei personaggi AI",
    aiConversationHistory: "Cronologia conversazioni con tutor AI",
    personalizedRecommendations:
      "Raccomandazioni di apprendimento personalizzate",
    aiGeneratedContent: "Contenuti e risposte generate dall'AI",

    beforeDeleteTitle: "Prima di eliminare il tuo account",
    exportYourDataTitle: "Esporta i tuoi dati",
    exportYourDataDesc:
      "Scarica la cronologia delle conversazioni e i progressi dalle impostazioni dell'app prima dell'eliminazione",
    considerBreakTitle: "Considera una pausa",
    considerBreakDesc:
      "Puoi disattivare temporaneamente l'account invece di eliminarlo permanentemente",
    contactSupportTitle: "Contatta il supporto",
    contactSupportDesc:
      "Se riscontri problemi, il nostro team di supporto potrebbe aiutarti a risolverli",

    needHelpTitle: "Hai bisogno di aiuto?",
    needHelpIntro:
      "Se hai difficoltà a eliminare il tuo account o hai domande sul processo:",
    supportResponseTime:
      "Il nostro team di supporto di solito risponde entro 24 ore e può aiutarti con:",
    helpDeletionTroubleshooting: "Risoluzione problemi di eliminazione account",
    helpDataExport: "Richieste di esportazione dati",
    helpAlternativeSolutions: "Soluzioni alternative per problemi di account",
    helpPrivacySecurity: "Domande su privacy e sicurezza",

    securityNoteTitle: "Nota sulla sicurezza",
    securityNoteDesc:
      "Le richieste di eliminazione dell'account vengono elaborate in modo sicuro e di solito completate entro 30 giorni per garantire la rimozione completa dei dati da tutti i sistemi e backup.",

    safetyTitle: "Lingua Talk – Politica di sicurezza e protezione dei minori",
    safetyIntro:
      "Su Lingua Talk, la sicurezza e il benessere degli utenti, in particolare dei minori, è una priorità. Come app di apprendimento linguistico e sociale che connette utenti con personaggi AI e partner linguistici reali, ci impegniamo a mantenere un ambiente sicuro e rispettoso per tutti.",
    zeroToleranceTitle: "Tolleranza zero per CSAE",
    zeroToleranceIntro: "Non permettiamo contenuti o comportamenti relativi a:",
    zeroToleranceItem1: "Abuso o sfruttamento sessuale di minori",
    zeroToleranceItem2: "Comportamento inappropriato o dannoso verso minori",
    zeroToleranceItem3:
      "Qualsiasi forma di grooming, nudità che coinvolge minori o comunicazione illegale",
    zeroToleranceAction:
      "Tali contenuti saranno rimossi immediatamente e segnalati alle autorità competenti.",

    moderationTitle: "Moderazione e monitoraggio",
    moderationItem1:
      "Gli utenti possono segnalare conversazioni e profili direttamente nell'app.",
    moderationItem2:
      "I contenuti segnalati vengono esaminati dal nostro team di moderazione.",
    moderationItem3:
      "Agiamo rapidamente contro le violazioni (avvisi, sospensioni, divieti o segnalazioni legali).",

    userAgePolicyTitle: "Politica sull'età degli utenti",
    userAgePolicyDesc:
      "Lingua Talk è destinato a utenti di età superiore ai 13 anni. Gli account che violano questa politica possono essere sospesi o rimossi.",

    reportAbuseTitle: "Come segnalare abusi",
    reportAbuseIntro: "Gli utenti possono segnalare:",
    reportAbuseItem1: "Messaggi o contenuti inappropriati nelle chat",
    reportAbuseItem2: "Profili utente o comportamenti sospetti",
    reportAbuseUseButton:
      "Usa il pulsante 'Segnala' nelle chat o nei profili utente. Le segnalazioni vengono esaminate dal team di moderazione.",

    complianceTitle: "Conformità alle leggi sulla sicurezza dei minori",
    compliesWith: "Lingua Talk è conforme a:",
    complianceItem1: "Standard di sicurezza dei minori di Google Play",
    complianceItem2: "Leggi CSAE applicabili in tutte le aree operative",
    complianceItem3:
      "Best practice del settore per la sicurezza e segnalazione sulla piattaforma",

    contactTitle: "Contatto",
    safetyContactIntro:
      "Per domande relative alla sicurezza dei minori o per segnalare un problema serio, contatta il nostro team di sicurezza:",

    // Home V2
    v2MetaTitle: "LinguaTalk — Learn languages by talking to AI",
    v2MetaDescription: "LinguaTalk is a language learning app. Chat with AI tutors in Spanish, French, Japanese and 9 more languages. Free on iOS and Android.",
    v2StoreSubApple: "Download on the",
    v2StoreNameApple: "App Store",
    v2StoreSubGoogle: "Get it on",
    v2StoreNameGoogle: "Google Play",
    v2BazaarSub: "Download from",
    v2BazaarName: "Cafe Bazaar",
    v2MarqueeEnglish: "English",
    v2MarqueeSpanish: "Español",
    v2MarqueeFrench: "Français",
    v2MarqueeGerman: "Deutsch",
    v2MarqueeItalian: "Italiano",
    v2MarqueeTurkish: "Türkçe",
    v2WhatDesc: "Most apps throw the same content at everyone. LinguaTalk starts by understanding your level, your goals, and the language you want to learn — then builds a personalized roadmap. From there, you practice through real conversations with AI tutors and native speakers, both matched to exactly where you are right now.",
    v2Feature1Title: "AI Tutors",
    v2Feature1Desc: "Talk to intelligent AI characters that respond, correct, and guide you like a real teacher.",
    v2Feature2Title: "Real Conversations",
    v2Feature2Desc: "No flashcards. No multiple choice. Just you and your tutor — chatting in the language you want to learn.",
    v2Feature3Title: "Placement Test",
    v2Feature3Desc: "Start at exactly the right level. A short test puts you where you belong before your first lesson.",
    v2Feature4Title: "12+ Languages",
    v2Feature4Desc: "English, Spanish, French, German, Italian, Turkish and more — all in one app.",
    v2Step1Title: "Take the placement test",
    v2Step1Desc: "A 5-minute adaptive quiz finds your exact level.",
    v2Step2Title: "Choose your AI tutor",
    v2Step2Desc: "Pick a character that fits your style and target language.",
    v2Step3Title: "Start chatting every day",
    v2Step3Desc: "Short daily chats build real speaking confidence over time.",
    v2RingSub: "Your level",
    v2StatLessons: "Lessons done",
    v2StatStreak: "Day streak 🔥",
    v2StatWords: "Words learned",
    v2PopupDesc: "Take a free 5-minute placement test and get a personalized learning roadmap.",
    v2ChipSpanish: "🇪🇸 Spanish lesson",
    v2ChipAiTyping: "🤖 AI is typing…",
    v2ChipStreak: "🔥 7-day streak",
    v2ChatToday: "Today",
    v2ChatInputPlaceholder: "Type your reply…",
    v2TutorSub: "AI Tutor · Spanish",
    v2HeroLine1: "L'IA progetta la tua",
    v2HeroLine2: "roadmap personale alla fluency",
    v2Pill: "📱 App per lingue — Gratis su iOS e Android",
    v2Subtext: "LinguaTalk crea un percorso su misura per il tuo livello — poi pratichi ogni giorno con tutor IA e madrelingua.",
    v2WhatTag: "Cos'è LinguaTalk?",
    v2WhatTitle1: "Non solo lezioni —",
    v2WhatTitle2: "un percorso costruito su di te",
    v2WhyLabel: "Perché funziona",
    v2HowLabel: "Come funziona",
    v2HowTitle1: "Tre passi per iniziare",
    v2HowTitle2: "parlare con sicurezza",
    v2CtaTitle: "Ottieni la roadmap gratuita oggi",
    v2CtaSub: "5 minuti di setup · Gratis per sempre · iOS e Android",
    v2PopupTitle: "Scopri il tuo livello",
    v2PopupBtn: "Inizia il test — Gratis",
    v2PopupFootnote: "Senza registrazione",
  },
  tr: {
    appTitle: "LinguaTalk",
    appTagline:
      "Mükemmel dil partnerini bul. Herhangi bir dili kendi hızında pratik yap.",
    appDescription:
      "LinguaTalk, dil meraklılarını dünyanın dört bir yanından pratik partnerlerle buluşturur. Arapça, Çince, İngilizce, Fransızca, Almanca, İtalyanca, Japonca, Korece, Farsça, Rusça, Türkçe veya başka herhangi bir dili öğrenmek istiyorsanız, size uygun çözümlerimiz var. Rehberli pratik için yapay zeka eğitmenleriyle sohbet edin veya eğlenceli, sosyal bir ortamda konuşma, yazma ve okuma becerilerinizi geliştirmek için gerçek insanlarla bağlantı kurun.",
    downloadIOS: "iOS için indir",
    downloadAndroid: "Android için indir",
    directDownload: ".apk dosyasını indir",
    learnMore: "LinguaTalk hakkında daha fazla bilgi edinin",
    howItWorks: "LinguaTalk Nasıl Çalışır",
    step1Title: "Profilinizi Oluşturun",
    step1Description:
      "Mükemmel eşleşmeyi bulmanıza yardımcı olmak için dil tercihlerinizi, beceri seviyenizi ve ilgi alanlarınızı ayarlayın.",
    step2Title: "Partner Bulun",
    step2Description:
      "Yapay zeka karakterlerini keşfedin veya dil öğrenme hedeflerinizi paylaşan gerçek kullanıcıları keşfedin.",
    step3Title: "Pratik Yapın ve Gelişin",
    step3Description:
      "Hedef dil becerilerinizi düzenli olarak pratik etmek için sohbet edin, sesli arama yapın veya mesaj gönderin.",
    whyChoose: "Neden LinguaTalk'ı Seçmelisiniz?",
    feature1Title: "Yapay Zeka Dil Partnerleri",
    feature1Description:
      "Dil becerilerinizi geliştirmenize yardımcı olmak için tasarlanmış akıllı yapay zeka karakterleriyle istediğiniz zaman pratik yapın.",
    feature2Title: "Gerçek Partnerler",
    feature2Description:
      "Otantik dil değişimi için anadil konuşmacıları ve diğer öğrenenlerle bağlantı kurun.",
    feature3Title: "Detaylı Profiller",
    feature3Description:
      "Dil becerilerinizi ve ilgi alanlarınızı sergilemek için profilinizi oluşturun ve özelleştirin.",
    feature4Title: "Akıllı Eşleştirme",
    feature4Description:
      "Beceri seviyenize, ilgi alanlarınıza ve öğrenme hedeflerinize göre mükemmel dil partnerinizi bulun.",
    aiScreenTitle: "Yapay Zeka Karakterleriyle Sohbet Edin",
    aiScreenDescription:
      "Güvenli, yargısız bir ortamda pratik yapmanıza yardımcı olmak için tasarlanmış yapay zeka karakterleriyle sohbet ederek dil becerilerinizi geliştirin. Yeni başlayanlar veya insan partnerlerin mevcut olmadığı zamanlar için mükemmel.",
    exploreScreenTitle: "Gerçek Partnerleri Keşfedin",
    exploreScreenDescription:
      "Dünyanın dört bir yanındaki gerçek kullanıcıları arayın ve bağlantı kurun. Mükemmel dil değişim partnerinizi bulmak için dil, yeterlilik seviyesi ve ilgi alanlarına göre filtreleyin.",
    footerText: "© 2025 LinguaTalk. Tüm hakları saklıdır.",
    language: "Dil",
    // Top Menu
    menuHome: "Ana Sayfa",
    menuPlacementTest: "Seviye Belirleme Testi",
    menuPrivacyPolicy: "Gizlilik Politikası",
    menuSafetySecurity: "Güvenlik",
    menuDeleteAccount: "Hesabı Sil",
    // Popup
    popupTitle: "Dil Seviyenizi Test Edin!",
    popupDescription:
      "Sadece 8 dakikada yeterliliğinizi keşfedin. A1'den C2'ye kadar anında sonuç alın.",
    popupButton: "Teste Şimdi Başla",
    popupFooter: "veya sonuçlarınızı kaydetmek için uygulamayı indirin",
    userPreviewTitle: "Davet edildin!",
    userPreviewMessage:
      "Biri seninle Lingua Talk'ta dil pratiği yapmak istiyor. Bağlanmak için uygulamayı indir — ücretsiz ve eğlenceli!",
    userPreviewTagline: "Birlikte dil pratiği yapın",
    userPreviewOpenInApp: "Uygulamada aç",
    userPreviewHint: "Ücretsiz · Denemek için hesap gerekmez",
    // Placement Test
    testStarting: "Test başlatılıyor...",
    getReady: "Hazır olun!",
    testPreview: "Test Önizlemesi",
    testYourLanguageProficiency: "Dil yeterliliğinizi test edin",
    guest: "Misafir",
    totalQuestions: "Toplam Soru",
    totalTime: "Toplam Süre",
    testParts: "Test Bölümleri",
    question: "soru",
    questions: "soru",
    startTest: "Teste Başla",
    loadingTest: "Test yükleniyor...",
    preparingTest: "Lütfen seviye belirleme testinizi hazırlarken bekleyin.",
    unableToLoadTest: "Test yüklenemedi. Lütfen uygulamadan tekrar deneyin.",
    greatJob: "Harika iş!",
    yourEstimatedLevel: "Tahmini seviyeniz",
    score: "Puan:",
    testEvaluated:
      "Bu seviye belirleme testi, {language} yeterliliğinizi A1'den C2'ye kadar birden fazla seviyede değerlendirdi.",
    greatWorkCompleted:
      "Harika iş! {language} seviye belirleme testini tamamladınız. İlerlemenizi kaydetmek ve kişiselleştirilmiş öğrenme özelliklerinin kilidini açmak için giriş yapın.",
    tryAgain: "Tekrar Dene",
    backToHome: "Ana Sayfaya Dön",
    test: "Test",
    level: "Seviye:",
    type: "Tür:",
    questionOf: "Soru {current}/{total} ({answered} cevaplandı)",
    playAudio: "Sesi Oynat",
    previous: "Önceki",
    next: "Sonraki",
    submitTest: "Testi Gönder",
    testResultSaved: "Test sonucunuz başarıyla kaydedildi!",
    testResultFailed: "Test sonucu kaydedilemedi. Lütfen tekrar deneyin.",
    saveResultsTrackProgress:
      "Sonuçlarınızı kaydetmek ve ilerlemenizi takip etmek ister misiniz?",
    improveWithApp:
      "Ayrıca uygulama AI'mız ve gerçek ortaklarımızla dil becerilerinizi geliştirebilirsiniz.",
    grammar: "Dilbilgisi",
    vocabulary: "Kelime Bilgisi",
    listening: "Dinleme",
    downloadForIOS: "iOS için İndir",
    downloadForAndroid: "Android için İndir",
    placementTest: "Seviye Belirleme Testi",
    min: "dk",
    testForLanguage: "Dil testi:",
    testNotImplemented: "Başka bir dil testi mi arıyorsunuz?",
    availableLanguages: "Şu anda bu dillerde test yapabilirsiniz:",
    viewAllLanguages: "Başka bir dil testi mi arıyorsunuz?",
    comingSoon: "Yakında...",

    privacyTitle: "Gizlilik Politikası",
    privacyIntro:
      "Lingua Talk'ta gizliliğinize değer veriyoruz ve kişisel bilgilerinizi korumaya kararlıyız. Bu politika, dil öğrenme ve sosyal platformumuzu kullanırken bilgilerinizi nasıl topladığımızı, kullandığımızı ve koruduğumuzu açıklar.",
    informationWeCollect: "Topladığımız Bilgiler",
    accountInformationTitle: "Hesap Bilgileri:",
    infoCollectEmailUsername: "E-posta adresi ve kullanıcı adı",
    infoCollectProfileInfo: "Profil bilgileri (isim, yaş, konum)",
    infoCollectLanguagePrefs: "Dil tercihleri ve öğrenme hedefleri",
    infoCollectSettingsPrefs: "Hesap ayarları ve tercihleri",

    usageDataTitle: "Kullanım Verileri:",
    usageDataConversationsAI: "AI karakterlerle yapılan konuşmalar",
    usageDataMessagesPartners: "Dil partnerleriyle mesajlar",
    usageDataProgressStats: "Öğrenme ilerlemesi ve istatistikler",
    usageDataAppPatterns: "Uygulama kullanım kalıpları ve tercihler",

    howWeUseTitle: "Bilgilerinizi Nasıl Kullanıyoruz",
    provideServicesTitle: "Hizmetlerimizi Sağlamak",
    provideServicesDesc:
      "Dil öğrenme özelliklerini ve partner eşleştirmeyi etkinleştir",
    improveExperienceTitle: "Kullanıcı Deneyimini İyileştirmek",
    improveExperienceDesc:
      "İçeriği ve AI karakter etkileşimlerini kişiselleştir",
    safetyModerationTitle: "Güvenlik ve Moderasyon",
    safetyModerationDesc: "Uygunsuz içerikleri izleyin",
    communicationTitle: "İletişim",
    communicationDesc: "Önemli güncellemeler ve bildirimler gönder",
    analyticsTitle: "Analitik",
    analyticsDesc:
      "Kullanıcıların platformla nasıl etkileşimde bulunduğunu anlamak",

    dataProtectionTitle: "Veri Koruma",
    encryptionTitle: "Şifreleme",
    encryptionDesc: "Tüm veriler iletim ve depolama sırasında şifrelenir",
    accessControlsTitle: "Erişim Kontrolleri",
    accessControlsDesc:
      "Çalışan erişimi yalnızca ihtiyaç duyulan durumlarla sınırlıdır",
    regularAuditsTitle: "Düzenli Denetimler",
    regularAuditsDesc:
      "Güvenlik incelemeleri ve güvenlik açıkları değerlendirmeleri",
    secureInfrastructureTitle: "Güvenli Altyapı",
    secureInfrastructureDesc: "Sektör standardı bulut güvenlik önlemleri",
    dataMinimizationTitle: "Veri Azaltma",
    dataMinimizationDesc: "Yalnızca gerekli bilgileri topluyoruz",

    dataSharingTitle: "Veri Paylaşımı ve Üçüncü Taraflar",
    noSellData: "Kişisel verilerinizi üçüncü taraflara satmıyoruz.",
    serviceProvidersTitle: "Hizmet Sağlayıcılar",
    serviceProvidersDesc:
      "Platformu çalıştırmamıza yardımcı olan bulut barındırma, analitik ve güvenlik hizmetleri",
    legalRequirementsTitle: "Yasal Gereklilikler",
    legalRequirementsDesc:
      "Yasa, mahkeme emri veya kullanıcı güvenliğini korumak gerektiğinde",
    withConsentTitle: "Rızanızla",
    withConsentDesc:
      "Sadece belirli partnerlerle bilgi paylaşmayı açıkça kabul ettiğinizde",

    yourRightsTitle: "Gizlilik Haklarınız",
    rightAccessTitle: "Erişim",
    rightAccessDesc: "Kişisel verilerinizi görüntüleyin",
    rightCorrectionTitle: "Düzeltme",
    rightCorrectionDesc: "Yanlış bilgileri güncelleyin",
    rightDeletionTitle: "Silme",
    rightDeletionDesc: "Hesabınızı ve verilerinizi silin",
    rightPortabilityTitle: "Taşınabilirlik",
    rightPortabilityDesc: "Verilerinizi dışa aktarın",

    contactUsTitle: "İletişim",
    contactUsDesc: "Gizlilikle ilgili sorular veya haklarınızı kullanmak için:",
    policyUpdatesTitle: "Politika Güncellemeleri",
    policyUpdatesDesc:
      "Bu politikayı zaman zaman güncelleyebiliriz. Önemli değişiklikleri e-posta veya uygulama içi bildirimle bildiririz.",
    lastUpdated: "Son güncelleme: Ocak 2024",

    deleteTitle: "Hesabınızı Silin",
    deleteIntro:
      "Gitmenize üzüldük! Lingua Talk hesabınızı silmek istiyorsanız, lütfen aşağıdaki talimatları izleyin. Bu işlem kalıcıdır ve geri alınamaz.",
    deleteImportantTitle:
      "⚠️ ÖNEMLİ: Hesap silme yalnızca mobil uygulama üzerinden yapılabilir",
    deleteImportantDesc:
      "Güvenlik nedenleriyle, hesap silme yalnızca Lingua Talk mobil uygulamasında mevcuttur. Bu web sitesinden hesabınızı silemezsiniz.",

    howToDeleteTitle: "Hesabınızı Silme Yöntemi",
    stepOpenAppTitle: "Lingua Talk Mobil Uygulamasını Açın",
    stepOpenAppDesc: "Telefonunuzda veya tabletinizde uygulamayı başlatın",
    stepGoSettingsTitle: "Ayarlar'a gidin",
    stepGoSettingsDesc: "Profil veya hesap ayarlarına gidin",
    stepFindAccountTitle: "Hesap Yönetimini Bulun",
    stepFindAccountDesc:
      "'Hesap Ayarları' veya 'Gizlilik ve Güvenlik' bölümünü bulun",
    stepSelectDeleteTitle: "'Hesabı Sil' seçeneğini seçin",
    stepSelectDeleteDesc:
      "Hesap silme seçeneğini seçin ve onay adımlarını takip edin",

    whatDeletedTitle: "Kalıcı Olarak Silinecekler",
    personalDataTitle: "Kişisel Veriler",
    personalProfileInfo: "Profil bilgileri ve ayarlar",
    personalEmailCredentials: "E-posta adresi ve hesap kimlik bilgileri",
    personalLangPrefsProgress: "Dil tercihleri ve öğrenme ilerlemesi",
    personalStatsAchievements: "Kişisel istatistikler ve başarılar",

    conversationsContentTitle: "Konuşmalar ve İçerik",
    conversationsWithPartners: "Dil partnerleriyle tüm konuşmalar",
    chatHistoryAI: "AI karakterleriyle sohbet geçmişi",
    sharedMediaVoice: "Paylaşılan medya ve sesli mesajlar",
    learningSessionRecordings: "Öğrenme oturumu kayıtları",

    socialConnectionsTitle: "Sosyal Bağlantılar",
    partnerConnections: "Dil partnerleriyle bağlantılar",
    friendListsBlocked: "Arkadaş listeleri ve engellenen kullanıcılar",
    groupMemberships: "Grup üyelikleri ve topluluklar",
    favoritesSavedContent: "Favoriler ve kaydedilen içerikler",

    aiCharacterDataTitle: "AI Karakter Verileri",
    customAIConfigs: "Özel AI karakter ayarları",
    aiConversationHistory: "AI eğitmenlerle konuşma geçmişi",
    personalizedRecommendations: "Kişiselleştirilmiş öğrenme önerileri",
    aiGeneratedContent: "AI tarafından oluşturulan içerik ve yanıtlar",

    beforeDeleteTitle: "Hesabınızı Silmeden Önce",
    exportYourDataTitle: "Verilerinizi Dışa Aktarın",
    exportYourDataDesc:
      "Silmeden önce uygulama ayarlarından sohbet geçmişinizi ve öğrenme ilerlemenizi indirin",
    considerBreakTitle: "Ara Vermeyi Düşünün",
    considerBreakDesc:
      "Kalıcı silme yerine hesabınızı geçici olarak devre dışı bırakabilirsiniz",
    contactSupportTitle: "Destek ile İletişime Geçin",
    contactSupportDesc:
      "Sorun yaşıyorsanız, destek ekibimiz çözüm bulmanıza yardımcı olabilir",

    needHelpTitle: "Yardıma mı ihtiyacınız var?",
    needHelpIntro:
      "Hesabınızı silmekte sorun yaşıyorsanız veya süreç hakkında sorularınız varsa:",
    supportResponseTime:
      "Destek ekibimiz genellikle 24 saat içinde yanıt verir ve şunlarla yardımcı olabilir:",
    helpDeletionTroubleshooting: "Hesap silme sorunlarını çözme",
    helpDataExport: "Veri dışa aktarma talepleri",
    helpAlternativeSolutions: "Hesap sorunları için alternatif çözümler",
    helpPrivacySecurity: "Gizlilik ve güvenlik soruları",

    securityNoteTitle: "Güvenlik Notu",
    securityNoteDesc:
      "Hesap silme talepleri güvenli bir şekilde işlenir ve tüm sistemlerden ve yedeklerden verilerin tamamen kaldırıldığından emin olmak için genellikle 30 gün içinde tamamlanır.",

    safetyTitle: "Lingua Talk – Çocuk Güvenliği ve Koruma Politikası",
    safetyIntro:
      "Lingua Talk’ta kullanıcıların, özellikle de çocukların güvenliği ve refahı en önemli önceliktir. Kullanıcıları AI karakterleri ve gerçek dil partnerleriyle buluşturan bir öğrenme ve sosyal uygulama olarak, tüm kullanıcılar için güvenli ve saygılı bir ortam sağlamaya kararlıyız.",
    zeroToleranceTitle: "CSAE için Sıfır Tolerans",
    zeroToleranceIntro:
      "Aşağıyla ilgili içerik veya davranışlara izin vermiyoruz:",
    zeroToleranceItem1: "Çocuklara yönelik cinsel istismar veya sömürü",
    zeroToleranceItem2: "Çocuklara yönelik uygunsuz veya zararlı davranış",
    zeroToleranceItem3:
      "Çocukları içeren grooming, çıplaklık veya yasadışı iletişim",
    zeroToleranceAction:
      "Bu tür içerikler hemen kaldırılır ve ilgili makamlara bildirilir.",

    moderationTitle: "Moderasyon ve İzleme",
    moderationItem1:
      "Kullanıcılar, uygulama içinde doğrudan konuşmaları ve profilleri bildirebilir.",
    moderationItem2:
      "Bildirim yapılan içerikler moderasyon ekibimiz tarafından incelenir.",
    moderationItem3:
      "İhlallere karşı hızlı önlem alırız (uyarılar, askıya almalar, yasaklamalar veya yasal raporlar).",

    userAgePolicyTitle: "Kullanıcı Yaşı Politikası",
    userAgePolicyDesc:
      "Lingua Talk, 13 yaş ve üzeri kullanıcılar için tasarlanmıştır. Bu politikayı ihlal eden hesaplar askıya alınabilir veya kaldırılabilir.",

    reportAbuseTitle: "İhlali Bildirme Yöntemi",
    reportAbuseIntro: "Kullanıcılar şunları bildirebilir:",
    reportAbuseItem1: "Sohbetlerdeki mesajlar veya uygunsuz içerikler",
    reportAbuseItem2: "Kullanıcı profilleri veya şüpheli davranışlar",
    reportAbuseUseButton:
      "Sohbetlerde veya kullanıcı profillerinde 'Bildir' düğmesini kullanın. Bildirimler moderasyon ekibi tarafından incelenir.",

    complianceTitle: "Çocuk Güvenliği Yasalarına Uyum",
    compliesWith: "Lingua Talk şunlara uygundur:",
    complianceItem1: "Google Play’in çocuk güvenliği standartları",
    complianceItem2: "Tüm işletim bölgelerinde geçerli CSAE yasaları",
    complianceItem3:
      "Platform güvenliği ve raporlama için sektörün en iyi uygulamaları",

    contactTitle: "İletişim",
    safetyContactIntro:
      "Çocuk güvenliği ile ilgili sorular veya ciddi bir endişeyi bildirmek için güvenlik ekibimizle iletişime geçin:",

    // Home V2
    v2MetaTitle: "LinguaTalk — Learn languages by talking to AI",
    v2MetaDescription: "LinguaTalk is a language learning app. Chat with AI tutors in Spanish, French, Japanese and 9 more languages. Free on iOS and Android.",
    v2StoreSubApple: "Download on the",
    v2StoreNameApple: "App Store",
    v2StoreSubGoogle: "Get it on",
    v2StoreNameGoogle: "Google Play",
    v2BazaarSub: "Download from",
    v2BazaarName: "Cafe Bazaar",
    v2MarqueeEnglish: "English",
    v2MarqueeSpanish: "Español",
    v2MarqueeFrench: "Français",
    v2MarqueeGerman: "Deutsch",
    v2MarqueeItalian: "Italiano",
    v2MarqueeTurkish: "Türkçe",
    v2WhatDesc: "Most apps throw the same content at everyone. LinguaTalk starts by understanding your level, your goals, and the language you want to learn — then builds a personalized roadmap. From there, you practice through real conversations with AI tutors and native speakers, both matched to exactly where you are right now.",
    v2Feature1Title: "AI Tutors",
    v2Feature1Desc: "Talk to intelligent AI characters that respond, correct, and guide you like a real teacher.",
    v2Feature2Title: "Real Conversations",
    v2Feature2Desc: "No flashcards. No multiple choice. Just you and your tutor — chatting in the language you want to learn.",
    v2Feature3Title: "Placement Test",
    v2Feature3Desc: "Start at exactly the right level. A short test puts you where you belong before your first lesson.",
    v2Feature4Title: "12+ Languages",
    v2Feature4Desc: "English, Spanish, French, German, Italian, Turkish and more — all in one app.",
    v2Step1Title: "Take the placement test",
    v2Step1Desc: "A 5-minute adaptive quiz finds your exact level.",
    v2Step2Title: "Choose your AI tutor",
    v2Step2Desc: "Pick a character that fits your style and target language.",
    v2Step3Title: "Start chatting every day",
    v2Step3Desc: "Short daily chats build real speaking confidence over time.",
    v2RingSub: "Your level",
    v2StatLessons: "Lessons done",
    v2StatStreak: "Day streak 🔥",
    v2StatWords: "Words learned",
    v2PopupDesc: "Take a free 5-minute placement test and get a personalized learning roadmap.",
    v2ChipSpanish: "🇪🇸 Spanish lesson",
    v2ChipAiTyping: "🤖 AI is typing…",
    v2ChipStreak: "🔥 7-day streak",
    v2ChatToday: "Today",
    v2ChatInputPlaceholder: "Type your reply…",
    v2TutorSub: "AI Tutor · Spanish",
    v2HeroLine1: "Yapay zeka sizin",
    v2HeroLine2: "kişisel akıcılık yol haritanızı tasarlar",
    v2Pill: "📱 Dil öğrenme uygulaması — iOS ve Android'de ücretsiz",
    v2Subtext: "LinguaTalk seviyenize uygun bir öğrenme yolu oluşturur — sonra AI eğitmenler ve gerçek kişilerle her gün pratik yapırsınız.",
    v2WhatTag: "LinguaTalk nedir?",
    v2WhatTitle1: "Sadece dersler değil —",
    v2WhatTitle2: "size özel bir yol haritası",
    v2WhyLabel: "Neden işe yarıyor",
    v2HowLabel: "Nasıl çalışır",
    v2HowTitle1: "Başlamak için üç adım",
    v2HowTitle2: "güvenle konuşmak",
    v2CtaTitle: "Ücretsiz yol haritanızı alın",
    v2CtaSub: "5 dakikada kurulum · Sonsuza kadar ücretsiz · iOS ve Android",
    v2PopupTitle: "Seviyenizi bulun",
    v2PopupBtn: "Teste başla — Ücretsiz",
    v2PopupFootnote: "Kayıt gerekmez",
  },
};

// Interface for the context
interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: keyof typeof translations.en) => string;
}

// Create the context
const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

// Provider component
interface LanguageProviderProps {
  children: ReactNode;
}

// Country code to language mapping
const countryToLanguage: Record<string, Language> = {
  // English speaking countries
  US: "en",
  UK: "en",
  CA: "en",
  AU: "en",
  NZ: "en",
  // Arabic speaking countries
  SA: "ar",
  AE: "ar",
  EG: "ar",
  IQ: "ar",
  JO: "ar",
  KW: "ar",
  LB: "ar",
  // Farsi speaking countries
  IR: "fa",
  AF: "fa",
  // Korean speaking countries
  KR: "ko",
  // German speaking countries
  DE: "de",
  AT: "de",
  CH_DE: "de",
  // French speaking countries
  FR: "fr",
  BE: "fr",
  CH_FR: "fr",
  CA_FR: "fr",
  // Russian speaking countries
  RU: "ru",
  BY: "ru",
  KZ: "ru",
  // Chinese speaking countries
  CN: "zh",
  HK: "zh",
  TW: "zh",
  SG: "zh",
  // Japanese speaking countries
  JP: "ja",
  // Italian speaking countries
  IT: "it",
  CH_IT: "it",
  // Turkish speaking countries
  TR: "tr",
  CY: "tr",
};

export const LanguageProvider: React.FC<LanguageProviderProps> = ({
  children,
}) => {
  // Use stored state for language persistence
  const [language, setLanguage] = useStoredState<Language>(
    "linguaTalk-language",
    "en"
  );
  const [isInitialized, setIsInitialized] = useState(false);

  // Detect user location and set initial language if no stored preference exists
  useEffect(() => {
    // Check if there's already a stored language preference
    const storedLang = localStorage.getItem("linguaTalk-language");

    // Check if there's a language query parameter
    const urlParams = new URLSearchParams(window.location.search);
    const langFromQuery = urlParams.get("l");

    // If there's a query parameter, use it and skip geolocation detection
    if (langFromQuery) {
      const availableLanguages: Language[] = [
        "en",
        "fa",
        "ar",
        "ko",
        "de",
        "fr",
        "ru",
        "zh",
        "ja",
        "it",
        "tr",
      ];

      if (availableLanguages.includes(langFromQuery as Language)) {
        setLanguage(langFromQuery as Language);
      }
      setIsInitialized(true);
      return;
    }

    // If no stored preference and no query parameter, detect based on location
    if (!storedLang || storedLang === '"en"') {
      const detectUserLanguage = async () => {
        try {
          // Free IP geolocation API
          const response = await fetch("https://ipapi.co/json/");
          const data = await response.json();

          // Get country code (e.g., 'US', 'FR', 'DE')
          const countryCode = data.country_code;

          // Map country to language or use browser language
          if (countryCode && countryToLanguage[countryCode]) {
            setLanguage(countryToLanguage[countryCode]);
          } else {
            // Fallback to browser language if available
            const browserLang = navigator.language.split("-")[0];
            const availableLanguages: Language[] = [
              "en",
              "fa",
              "ar",
              "ko",
              "de",
              "fr",
              "ru",
              "zh",
              "ja",
              "it",
              "tr",
            ];

            if (availableLanguages.includes(browserLang as Language)) {
              setLanguage(browserLang as Language);
            }
            // If no match, keep English as default (already set)
          }
        } catch (error) {
          console.error("Error detecting user language:", error);
          // Keep default language (English) if detection fails
        } finally {
          setIsInitialized(true);
        }
      };

      detectUserLanguage();
    } else {
      setIsInitialized(true);
    }
  }, [setLanguage]);

  // Update HTML lang attribute when language changes
  useEffect(() => {
    if (isInitialized) {
      document.documentElement.lang = language;
    }
  }, [language, isInitialized]);

  // Translation function
  const t = (key: keyof typeof translations.en): string => {
    return (translations[language] as any)[key] ?? translations.en[key] ?? key;
  };

  const value = {
    language,
    setLanguage,
    t,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook to use the language context
export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
